import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { PageRef } from 'providers/page-ref';
import { E2eTestHooksService } from 'services/e2e-test-hooks.service';
import { AppNavigationMenuService } from 'services/app-navigation-menu.service';
import { BugReportFabService } from 'services/bug-report-fab.service';
import { BootDiagnosticsReportersService } from 'services/boot-diagnostics-reporters.service';
import { SettingsService, programSettingKeys } from 'services/settings.service';
import { STICKY_REFERRAL_SETTING_KEYS } from 'services/referral-settings';
import { copySettingsFromDatabase, copySettingsToDatabase } from 'services/settings-sync-copy';
import { AuthService, UserAuth } from 'services/auth.service';
import { CouchLoginReason } from 'services/couch-login-storm';
import { shouldRecheckSubscriptionBeforeLockout, shouldRouteInAfterServerLogin } from 'services/warm-resume-lockout-policy';
import { LocationService } from 'services/location.service';
import { CoachManagingClient, UserDb } from 'services/user-db.service';
import { InitialSyncWaitFor, runOnInitialSyncSuccess, waitForInitialSyncAsync } from 'services/user-db.task-status';
import { ExerciseDbService } from 'services/exercise-db.service';
import { FoodDbService } from 'services/food-db.service';
import { AppEnvironment } from 'environments/environment';
import { browserLocalStorageOrNull, getDeviceId, getRootDomain, getSessionId, hasClientSmallScreen, isAppStandalone, isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { getLocalDatabaseEngine, hasLocalDatabaseEngineBeenRead, legacyIdbCleanupPermitted, peekLocalDatabaseEngineWithoutLatching, probeSpeaksForLocalStore, setLocalDatabaseEngineReporter, usesSqliteEngine } from 'services/local-database-engine';
import {
  LOCAL_DATABASE_ENGINE_KEY,
  SQLITE_COACH_COPY_NAMES_KEY,
  SQLITE_MIGRATION_ROLLED_BACK_KEY,
  SQLITE_MIGRATION_TARGETS_KEY,
  ROLLBACK_ALLOW_LIST_DOC_ID,
  ROLLBACK_PERCENT_DOC_ID,
  SQLITE_ROLLBACK_REQUESTED_KEY,
  shouldMigrateThisDeviceToSqlite,
  shouldRollBackThisDeviceToIndexedDb,
} from 'services/local-database-engine-decisions';
import {
  MigrationPhase,
  readCoachCopyNames,
  readMigrationTargetNames,
  readRequestedReArmGeneration,
  signOutMustDeleteSqliteFiles,
} from 'services/sqlite-migration-decisions';
import * as Sentry from '@sentry/angular';
import PouchDB from 'pouchdb-browser';
import { bucketForIdentifier } from '@cf-shared/payment-provider-bucketing';
import {
  bumpLocalStoreGeneration,
  currentLocalStoreName,
  localStoreNameFor,
  readLocalStoreGeneration,
  switchLocalStoreWithoutAsking,
} from 'services/local-store-generation';
import { pageStartedAfterPurchaseReload, UserDbInitWaitEnding, waitForUserDbInitAsync } from './after-purchase-reload';
import { ConfigKey } from 'services/config-keys';
import { runningTimeTimer, withLocalStoreIdleTimeout, withLocalStoreTimeout } from 'services/local-store-timeout';
import {
  MIGRATION_BATCH_TIMEOUT_MS,
  MIGRATION_SLICE_PAUSE_MS,
  SqliteMigrationDeps,
  SqliteMigrationDepsCore,
} from './sqlite-migration';
import {
  SqliteRollbackDeps,
  reverseCopyDirection,
} from './sqlite-migration-reverse';
import { SqliteLifecycle } from './sqlite-lifecycle';
import { IdbDeletionOutcome, IdbForDeletion, deleteIdbDatabasesAsync } from './idb-database-deletion';
import { getReadableAppVersion } from 'environments/version-getters';
import { AppVersion } from 'app/version';
import { AuthRole } from '@cf-shared/couch-auth';
import { DebugService } from 'services/debug.service';
import { NotificationService } from 'services/notification.service';
import { TestCasesDbService } from 'services/tests-cases-db.service';
import { OverlayStackService } from 'providers/overlay-stack.service';
import { DeferredPromise, roundToPrecision, sleep } from '@cf-shared/shared-util';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { PopoverController } from 'providers/popover-controller';
import { AppInstallService, InstallTargetKind } from 'services/app-install.service';
import { CsAppInstallSheet } from 'components/cs-app-install-sheet/cs-app-install-sheet';
import { DatabaseState, DatabaseSyncProgressEvent, DatabaseWrapper, DatabaseWrapperProvider, DocWatcher } from 'services/database-wrapper';
import { DateTime } from 'luxon';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { LoadingController } from 'providers/loading-controller';
import { FitnessPlan, WelcomePhase } from '@cf-shared/data-model/fitness-plan';
import { NavController } from 'providers/nav-controller';
import { PostLoginRouteClaim } from 'providers/post-login-route-coordination';
import { shouldReportNavNeverReady, shouldFireNavRestoreWatchdog, remainingNavRestoreWatchdogMs, NAV_RESTORE_WATCHDOG_MS, awaitNavRestoreOrRecovery, NavRestoreOutcome, navRestoreWatchdogResumeAction, NavRestoreWatchdogResume } from 'providers/nav-restore-coordination';
import { FatalErrorPageParams } from 'pages/fatal-error/fatal-error-interface';
import { TextDbService } from 'services/text-db.service';
import { ENGLISH_TEXT_SEGMENT } from 'services/text-locale';
import { LoggingService, isTransientFetchError, errorReportFields } from 'services/logging.service';
import { isExpectedChangelogReadFailure } from 'services/changelog-read-failure';
import { DatabaseRecoveryService } from 'services/database-recovery.service';
import { setReturnHostCookie } from 'services/discourse-return-host';
import { SsoService } from 'services/sso.service';
import { IHomePage } from 'pages/home/home-interface';
import { ConfigDbService } from 'services/config-db.service';
import { COACH_ONLY_PAGE_NAMES, getAllDeepLinks, getAllowedPageDuringWelcome, getPageName, getPageTitleByName } from 'app/app-page-helpers';
import { safeUrlForLog } from 'app/safe-url-for-log';
import { buildAppWideListeners } from 'app/app-wide-listeners';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { App as CapApp } from '@capacitor/app';
import { registerCapacitorSQLiteAdapter } from 'services/pouchdb-adapter-capacitor-sqlite';
import { setBootReadPriorityActive, BOOT_READ_PRIORITY_FAILSAFE_MS } from 'services/pouchdb-adapter-capacitor-sqlite/boot-read-priority';
import { BootWindowVisibility } from './boot-window-visibility';
import { TelemetryService } from 'services/telemetry.service';
import { CsFabComponent } from 'components/cs-fab/cs-fab';
import { clearAllCookies } from 'services/browser-utils';
import { ILoginPage } from 'pages/login/login-interface';
import { ITestingRootPage } from 'pages/testing/testing-root-interface';
import { ISubscribePage } from 'pages/subscribe/subscribe-interface';
import { CoachClaimService } from 'services/coach-claim.service';
import { CoachTierService } from 'services/coach-tier.service';
import { CoachManageDeepLinkService } from 'services/coach-manage-deep-link.service';
import { IIntroCarouselPage } from 'pages/intro-carousel-page/intro-carousel-interface';
import { IAwaitingCoachPage } from 'pages/awaiting-coach/awaiting-coach-interface';
import { IIntakeFormPage } from 'pages/intake-form/intake-form-interface';
import Bowser from 'bowser';
import { first } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { coachBannerEvent, declareEvent, PubSubService } from 'services/pubsub.service';
import { CsNavComponent } from 'components/cs-nav/cs-nav';
import { CommonDbService } from 'services/common-db.service';
import { StapleDbService } from 'services/staple-db.service';
import { ExternalFlowService } from 'services/external-flow.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { AppService } from 'services/app.service';
import { ReferralService } from 'services/referral.service';
import { UrlQueryService } from 'services/url-query-service';
import { CsSplitPaneComponent } from 'components/cs-split-pane/cs-split-pane';
import { IMyApp, ReloadAppOptions, setMyApp } from './app-interface';
import { adjustScheduleToDeclaredState, convertScheduleDaysToRules, deduplicateActiveWorkoutRules, doActiveWorkoutDaysMatchDeclared, generateScheduleWithDefaults, getLiveMealReferences, getWeeklyCheckupTimeHr, Schedule_ItemType, ScheduleRuleRepeat } from '@cf-shared/data-model/schedule';
import { FirebaseService } from 'services/firebase.service';
import { SendFeedbackType } from '@cf-shared/send-feedback-interface';
import { OnlineStateService, ConnectionState, connectionStateChangedEvent } from 'services/online-state.service';
import { isNeverSyncedOfflineBoot, isNeverSyncedServerUnreachableBoot } from './boot-offline-gate';
import { ACCOUNT_LOAD_AUTO_RELOAD_KEY, mayAutoReloadAccountLoadHold } from './account-load-hold';
import { confirmSignOutSafeAsync } from 'services/sign-out-guard-flow';
import { evaluateSplashWatchdog, SPLASH_STUCK_TICK_MS, SplashWatchdogTimes } from './splash-watchdog';
import { describeDatabaseShutdownForReport, runBoundedTeardownStepsAsync } from './signout-teardown';
import { ReloadTeardownDeps, runReloadTeardownAsync, sqliteDatabaseNamesForSignOut } from './reload-teardown';
import { cleanUpLegacyIndexedDbAsync } from './legacy-idb-cleanup';
import { cleanUpSupersededIndexedDbAsync } from './superseded-idb-cleanup';
import { bootTimings, BootPhase } from './boot-timings';
import { buildBootingTimeProperties } from './booting-time-properties';
import { ColdStartRecoveryAction, decideColdStartRecoveryActionAsync } from './cold-start-recovery';
import { localOpTracker } from 'services/local-op-tracker';
import { probeLocalStoreAsync, makeBrowserProbeDeps, scopeOfWedge, probeFoundAProblem } from 'services/local-store-probe';
import { readWedgedBootCount, recordWedgedBoot, clearWedgedBootCount } from './wedged-boot-count';
import { restartProcessForWedgedStoreAsync, clearProcessRestartCountAsync, processRestartStateAsync } from 'services/app-restart';
import { ProcessRestartState } from 'services/process-restart-state';
import { CouchSessionHint, getCouchSessionHint } from 'services/couch-session-hint';
import { acquireWebBootLock, clearWebBootMarker, evaluateWebBootMarker, markWebBootCleanExit, markWebBootHiddenDuringBoot, queryHeldWebBootPageIdsAsync, isStaleWebBootMarkerRead, readWebBootMarkerRaw, recordWebBootStage, releaseWebBootLockAfterReady } from './web-boot-marker';
import { waitForAnimationOrTimeout, computeSyncProgressFraction, computeProgressStepDurationMs, PROGRESS_BAR_MAX_WAIT_MS, PROGRESS_STEP_DEFAULT_MS } from './boot-progress-wait';
import { DebouncedCloudStateChangeEvent } from 'services/connection-state.service';
import { ConnectivityResumeService } from 'services/connectivity-resume.service';
import { CoachNotifyService } from 'services/coach-notify.service';
import { CoachPresenceStateService } from 'services/coach-presence-state.service';
import { CoachExerciseSyncService } from 'services/coach-exercise-sync.service';
import { ImageUploadOutboxService } from 'services/image-upload-outbox.service';
import { NativeImageCacheService } from 'services/native-image-cache.service';
import { FoodUnpublishOutboxService } from 'services/food-unpublish-outbox.service';
import { CoachBrandingStateService } from 'services/coach-branding-state.service';
import { UiLanguageReconcileService } from 'services/ui-language-reconcile.service';
import { DeepLinkSignInService } from 'services/deep-link-signin.service';
import { AccountHintService } from 'services/account-hint.service';
import { INSTALL_REFERRER_WAIT_LIMIT_MS, LAUNCH_URL_WAIT_LIMIT_MS } from 'services/deep-link-signin';
import { awaitBoundedLive } from 'services/couch-login-wait-bound';
import { isAbortError } from 'util/is-abort-error';
import { FetchFailureAction, FetchRejectionHandlers, handleFetchRejectionAsync } from './fetch-failure-classifier';
import { firebaseFetchFailures } from 'services/couch-login-firebase-ready';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';

export const PageEnteredEvent = declareEvent<{ screenName: string }>('page:enter');

// #4762 — bounds for the OTHER teardown, the one in logonChangedAsync that runs when the
// auth state goes to signed-out. Same reasoning and shape as the pre-reload teardown's own
// nested bounds in reload-teardown.ts: backstops, not budgets, and a healthy teardown clears
// all of them in well under a second.
//
// The telemetry flush gets the larger value because it is a different kind of wait —
// flushAsync() joins whichever background flusher is already running, so it inherits that
// job's state. 3 s matches what the reload path already allows the same call.
//
// They are sized so that even a teardown where EVERY step is wedged finishes before the
// splash watchdog's first threshold. That is arithmetic over the step list, and arithmetic
// in a comment rots the moment somebody adds a step, so a test in signout-teardown.test.ts
// recomputes it from these constants and the real list instead. If you add a step and that
// test goes red, the margin is genuinely gone: lower a bound or drop a step.
const SIGNOUT_TELEMETRY_FLUSH_TIMEOUT_MS = 3_000;
const SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS = 2_000;

// #6248: how long the account-load hold must last before it is reported. Long enough that a page
// navigating away while its start-up ran (the case both traced production users were in) is gone
// before the report would fire.
const ACCOUNT_LOAD_HOLD_REPORT_DELAY_MS = 10_000;

/** df-53c89a39b0e6: the claim on the post-login move that a login retry handed to this boot, and
 *  whether the warm path's background navigation start-up has taken over releasing it */
interface RetryRouteHandoff {
  claim: PostLoginRouteClaim | undefined;
  takenByNavInit: boolean;
}

/*
In order to show something as soon as possible, we have a static skeleton splash screen in index.html. Only after the booting page is ready and shown, we may hide that skeleton that is in index.html - otherwise, we get a short blink in-between.
However, we must also double-guard to make sure it is hidden before we proceed, in the rare case that the booting screen never gets a chance to display - if the app is loading too fast! So this is defined here to be called from multiple places.
*/
export function hideIndexHtmlSplash() {
  const splash = document.getElementById('splash');
  if (splash) {
    splash.remove();
  }
}

// boot stages reported by the splash stall telemetry (#3698) — one value per
// awaited boot step, so a stuck boot reports exactly where it is wedged.
// 'cold:*' = postLoginColdStartAsync steps, 'warm:*' = warm resume Phase A.
// string-valued enum: the values are the telemetry wire format.
enum BootStage {
  PreLogin = 'pre-login',
  ColdUserDbInit = 'cold:userDb.init',
  ColdSharedDbs = 'cold:sharedDbs',
  ColdDebugInit = 'cold:debug.init',
  ColdLoadPlan = 'cold:loadPlan',
  ColdScheduleFixes = 'cold:scheduleFixes',
  ColdCalendarWindowSync = 'cold:calendarWindowSync',
  ColdFinishing = 'cold:finishing',
  ColdLicenseDialog = 'cold:licenseDialog',
  ColdNavInit = 'cold:navInit',
  WarmLocalInit = 'warm:localInit',
  // #4981 phase 3: copying this device's local data from IndexedDB into the SQLite
  // engine. Placed before `Ready` in reading order, as the plan requires. Note these
  // values ARE the telemetry wire format and at least one consumer compares against
  // `Ready` as a raw string, so existing dashboards filtering on the old values will
  // not know about this one.
  SqliteMigration = 'sqlite-migration',
  // #4981 phase 4 (#5270): the SWITCH half of copying this device's local data back OUT
  // of SQLite and onto IndexedDB. A DISTINCT value from the migration above, not a reuse:
  // these values are the telemetry wire format, and the ramp has to be able to tell a
  // device rolling back from a device migrating — they mean opposite things about how the
  // rollout is going. Only the switch half appears here at all; the bulk copy runs in the
  // background, off the boot screen entirely.
  SqliteRollback = 'sqlite-rollback',
  Ready = 'ready',
}

// Whether the shared changelog check has to wait for the common database before
// it can read the published changelog document. The cold path has already
// awaited that database's initialization by the time it asks, so it does not
// wait. The warm-resume background phase asks before the sync has finished, so
// it must wait or the document is simply not in the local database yet.
enum CommonDbReadiness {
  AlreadyInitialized = 'already-initialized',
  MustWaitForSync = 'must-wait-for-sync',
}

@Component({
  selector: 'cs-app',
  templateUrl: 'app.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyApp implements IMyApp {
  protected LL = LL;
  @ViewChild(CsNavComponent) nav: CsNavComponent;
  @ViewChild('fab') fab: CsFabComponent;
  @ViewChild('progressBar') progressBar: ElementRef;
  @ViewChild('progressBarFill') progressBarFill: ElementRef;
  @ViewChild('splitPane') splitPane: CsSplitPaneComponent;

  rootPage = null;
  appLoading = true;
  navRestoring = false;
  // L8 (#3986): watchdog timer for the navRestoring window (which runs after appLoading=false, so
  // the splash watchdog no longer covers it). Fires NAV_RESTORE_WATCHDOG_MS after the restore
  // starts if it hasn't completed. Raw setTimeout — MyApp is the never-destroyed root.
  private navRestoreWatchdogHandle: ReturnType<typeof setTimeout> | null = null;
  // #4232: the watchdog measures FOREGROUND time only — backgrounded time proves nothing about
  // the restore being wedged. On visibilitychange→hidden the timer stops and the remaining
  // foreground budget is stored here; on visible it re-arms with that remainder.
  private navRestoreWatchdogRemainingMs = 0;
  // performance.now() when the current visible countdown stretch started (for the pause math)
  private navRestoreWatchdogVisibleSinceMs = 0;
  // the URL the wedged restore was heading to, reported by recoverStuckNavRestore
  private navRestoreWatchdogTargetUrl = '';
  private isNavRestoreVisibilityHooked = false;
  // df-f938864e16d1: true from armNavRestoreWatchdog until clearNavRestoreWatchdog. navRestoring alone
  // cannot say this: the cold boot shows the spinner before its pre-restore awaits and arms only when
  // the restore starts, so a spinner with no window open is normal there.
  private isNavRestoreWatchdogWindowOpen = false;
  // df-d80a99934799: resolved by recoverStuckNavRestore, so the cold boot's wait on a wedged
  // restore can end. Created by the cold boot right before it starts the restore, and dropped
  // whenever the watchdog is armed again, so a rescue can only end the wait on the restore that
  // was actually running when it fired.
  private navRestoreRecovery: DeferredPromise<void> | null = null;

  // Step 21-prep (#4027): the couch-session hint as it stood at boot START —
  // sampled in appBootSequenceAsync before this boot's own login overwrites
  // the stored marker. Classified into booting_time's `prior_session_hint`.
  private bootStartSessionHint: CouchSessionHint | null = null;

  // Step 22 (#4027): the PREVIOUS boot's post-mortem marker (web only) —
  // captured in appBootSequenceAsync before this boot's first bootStage
  // transition overwrites it. Evaluated + reported once this boot is Ready.
  private previousWebBootMarkerRaw: string | null = null;
  // #6181: which pages held a boot lock at the moment the marker above was
  // read (null = could not ask), and the release for this page's own lock,
  // held from boot start until Ready. See web-boot-marker.ts's header.
  private liveBootPageIdsAtCapture: Promise<Set<string> | null> = Promise.resolve(null);
  private releaseWebBootLock: (() => void) | null = null;
  // #6266: set when another tab's write, arriving after the capture, shows the
  // captured marker was a stale copy (isStaleWebBootMarkerRead). Sent with the
  // report; the listener that sets it lives from capture until the report
  private webBootMarkerStaleReadSeen = false;
  private webBootStaleReadListener: ((event: StorageEvent) => void) | null = null;
  // one-shot latches: report the previous boot at most once per boot window,
  // and report a failing marker WRITE at most once (the setter fires on every
  // stage transition — a broken localStorage must not spam Sentry)
  private webBootMarkerReported = false;
  private webBootMarkerWriteFailureReported = false;
  private webBootLockFailureReported = false;

  // S4 (#3986): resolves once the native SocialLogin plugin is initialized.
  // Kicked off concurrently with the Firebase auth init in
  // appBootSequenceAsync; auth.service.ts awaits it (via
  // whenSocialLoginReadyAsync) before the first SocialLogin.login call.
  // Pre-resolved so web / non-native paths never wait.
  socialLoginReady: Promise<void> = Promise.resolve();

  // S4 (#3986): the cold-start launch URL fetch, also kicked off before the
  // auth init and consumed at its original point in appBootSequenceAsync
  private launchUrlPromise: Promise<{ url: string } | undefined> = Promise.resolve(undefined);
  private isWarmResume = false;
  /** #6202: set once this page has told the native boot watchdog its boot finished. */
  private hasSentBootComplete = false;

  /** df-4ebecd016a94: how many cold starts this page has begun. `postLoginColdStartAsync` bumps
   *  it on entry and keeps its own number, so it can tell whether it is still the newest cold
   *  start — the newest one owns the boot screen. A cold start that has been superseded must
   *  never end the boot, because the newer one is going to. A counter rather than a flag: the
   *  question is "is a LATER one running", which a boolean cannot answer. */
  private coldStartsBegun = 0;

  loadingProgressType: 'spinner' | 'bar' | 'none' = 'spinner';
  syncRetryMessage: string = null;
  firstSyncMessage: string = null;

  // F24 (#3991): true once we have decided this boot must NOT route into onboarding, because the
  // user is signed in, offline, and on a device that has never synced. The splash stays up with an
  // honest message until connectivity returns.
  //
  // Public because the template renders the Retry button on this screen too — the held splash is
  // deliberately never "stuck" (no auto-reload, no hang report), so without this flag the button
  // would never appear and the user would have no way out.
  offlineFirstLoadBlocked = false;
  /** F24 (#3991): a Retry probe from the held splash is in flight. Disables the button and drives the
   *  "Checking…" copy, so the tap visibly does something during the up-to-10s probe. */
  offlineFirstLoadChecking = false;
  /** #6248: the cold start is held on the splash because start-up could not read some of the
   *  user's core documents. Public because the template renders the Retry button for it. See
   *  `gateUnreadAccountDocumentsBoot`. */
  accountLoadBlocked = false;
  /** #6248: what the Retry button does on that held splash — set by the gate. */
  private reloadHeldAccountLoad: (() => void) | null = null;
  /** The root component's equivalent of a page's `viewDestroyed`: guards markForCheck() after an
   *  await, per CLAUDE.md's NG0911 rule. The root outlives every page, but it IS torn down on the
   *  logout-shutdown path, and a probe started from the held splash can still be in flight then. */
  private appDestroyed = false;

  /** True once a sign-out has passed its data-safety guard and begun. It is deliberately NOT
   *  cleared when logoutAsync returns: that method returns as soon as auth.signOutAsync resolves,
   *  but the sign-out FLOW keeps going after it (auth-state event -> hide the loading screen ->
   *  show the login page -> LoginPage.ionViewCanEnter does a full page reload). Clearing it on
   *  return would therefore reopen exactly the window this flag exists to close — in the run that
   *  found this, the second sign-out began 130 ms after the first, at the moment the first one's
   *  auth.signOutAsync was resolving. So it stays set for the rest of the page's life,
   *  which is short by construction because a successful sign-out ends in that reload. The one
   *  residual: if the sign-out succeeds but the reload never happens, Sign Out cannot be pressed
   *  again in that page. That state is already the wedged state the reload watchdog covers, and
   *  the button is only reachable while signed in, so it costs nothing that was not already lost.
   *  Cleared only when the user cancels at the guard, or when the sign-out throws. */
  private signOutInFlight = false;

  // #3493 Phase B-2 + #3698: splash stuck detection. splashWatchdogTimes records
  // sync progress on every DatabaseSyncProgressEvent (existing heartbeat — fires on
  // every replication batch + stage transitions) and when the splash became
  // visible. The decision logic (stuck / auto-kick / emit telemetry) and the way
  // elapsed time is measured both live in the pure module splash-watchdog.ts so
  // they can be unit tested under node — scheduleSplashStuckCheck delegates to it.
  // The check runs on a recursive setTimeout chain (raw, not safeTimeout — MyApp
  // is the never-destroyed root shell), every SPLASH_STUCK_TICK_MS. The handle is
  // stored + cleared on lifecycle transitions so rapid markAppLoadingAsync
  // true→false→true cycles can't spawn parallel timer chains.

  // L13 (#3986): the consent/ToS gate is legitimately user-paced, so we do NOT bound it. But if
  // the dialog fails to render or flip, boot hangs at cold:licenseDialog with zero visibility.
  // Emit ONE ConsentGateSlow heartbeat this long after the gate opens so a regression is observable.
  private static readonly CONSENT_GATE_HEARTBEAT_MS = 60_000;
  // #6181: how long the previous-boot report waits for the browser to say
  // which boot locks are held. The answer normally takes milliseconds; past
  // this the report falls back to its pre-#6181 rule and WebBootLockFailed says so
  private static readonly WEB_BOOT_LOCK_QUERY_LIMIT_MS = 5_000;
  // longest boot will wait for the task calendar's sync window before giving up
  // and letting the calendar fill in after the loading screen instead.
  // Wall time, checked in #6096: it sends no report, and expiring early only means the
  // calendar fills in after the loading screen, which is the behaviour before #4249.
  private static readonly CALENDAR_SYNC_BOOT_WAIT_MS = 20_000;
  // #6093: measures running time on the monotonic clock, so a phone that froze the app in
  // the background does not wake to a "stuck" splash
  private readonly splashWatchdogTimes = new SplashWatchdogTimes(() => performance.now());
  // O8 (#3986): sessionStorage one-shot guard for the reload escalation. sessionStorage
  // survives location.reload() in the same tab but not a new session, so the reload can fire at
  // most once per session and can never loop. NOT cleared by armSplashWatchdog (per-session,
  // not per-splash-window) and NOT cleared by reloadAppAsync({resetToRoot}) (only the
  // clearLocalData path clears sessionStorage).
  private static readonly BOOT_RELOAD_RECOVERY_KEY = 'boot_reload_recovery_tried';
  splashStuck = false;
  // #4401: one storage probe per PAGE LOAD — deliberately not reset by armSplashWatchdog
  // with the other splash fields. Whether the browser's storage answers does not change
  // from one splash window to the next within a page, and repeating it would cost another
  // database open and fill the reports with the same answer
  private splashProbeReported = false;
  private splashHangEmitted = false;
  private splashStuckCheckHandle: ReturnType<typeof setTimeout> | null = null;
  // which awaited boot step is currently pending — included in the splash
  // stall telemetry so a stuck boot reports exactly where it is wedged (#3698).
  // Step 22 (#4027): the accessor pair below intercepts every transition and
  // records it into the web post-mortem boot marker; the backing-field
  // initializer deliberately bypasses the setter so construction cannot
  // overwrite the PREVIOUS boot's marker before appBootSequenceAsync captures it.
  private _bootStage: BootStage = BootStage.PreLogin;

  private get bootStage(): BootStage {
    return this._bootStage;
  }

  private set bootStage(stage: BootStage) {
    this._bootStage = stage;
    // Step 22 (#4027): web-only post-mortem marker — every stage transition is
    // written to localStorage so a boot that dies silently leaves evidence for
    // the next boot to report. Native is covered by FreezeWatchdog instead.
    if (!isCapacitorNative()) {
      this.guardedWebBootMarkerWrite(() => recordWebBootStage(stage, DateTime.utc().toISO()));
    }
    // if the boot just completed, evaluate + report the PREVIOUS boot's
    // marker (both the cold and warm paths funnel through this transition)
    if (stage === BootStage.Ready) {
      // #6181/#6266: this boot is done, so the lock that told other tabs it was
      // running goes after a grace period (why: releaseWebBootLockAfterReady).
      // raw setTimeout: MyApp is the never-destroyed root shell. cleared now,
      // so a re-run boot takes a fresh lock
      if (this.releaseWebBootLock) releaseWebBootLockAfterReady(this.releaseWebBootLock, setTimeout);
      this.releaseWebBootLock = null;
      this.reportPreviousWebBootIncompleteIfAny();
      // #4981 phase 4 (#5270): and count this boot towards the grace period before the old
      // IndexedDB copy is deleted. HERE, in the one place both boot paths funnel through,
      // and only on reaching Ready — a boot that wedged earlier says nothing about whether
      // the SQLite copy can be trusted, which is the whole thing being counted.
      this.sqliteLifecycle.countSqliteReadyBoot();
    }
  }

  // these are needed by the side menu
  userName: string = null;
  version: string = getReadableAppVersion();
  loadingTextsId = '@help:loading-hints.general';
  window = window; // needed by html for startInstallPrompt

  offlineModeAlert: Promise<void>;
  offlineModeExternalDismiss: DeferredPromise<void>;
  offlineModeShown = false; // used to prevent repeating the offline alert until back in online at least once
  public plan: FitnessPlan;


  hasClientSmallScreen = hasClientSmallScreen;

  @HostBinding('class.small-screen')
  get isSmallScreen() {
    return hasClientSmallScreen();
  }

  @HostBinding('class.is-ios')
  get isIOS() {
    return isPlatformIOS();
  }

  // Mirrors the cs-coach-banner render condition in app.html. Lets cs-header
  // know the banner sits above it and already owns the top safe-area inset, so
  // the header must not add that inset a second time.
  @HostBinding('class.coach-banner-visible')
  get isCoachBannerVisible() {
    return !!(this.coachManagingClient || this.coachIntakeBannerClientName);
  }

  // Coach white-labeling (#3707) lives in CoachBrandingStateService, injected
  // below as `coachBranding`. app.html reads the logo / brand name straight off
  // that service.
  @HostBinding('class.coach-branded')
  get isCoachBranded() {
    return this.coachBranding.brandingApplied;
  }

  public showChangelogPageNotification: boolean = false;
  public changelogRev: string | undefined = undefined;

  /** On IMyApp; the trip itself belongs to the bug-report button's service. */
  get screenshotTrip() {
    return this.bugReportFab.screenshotTrip;
  }

  // read lazily by BugReportFabService: the button sits inside an @if, so this comes and goes
  @ViewChild('bugFAB') bugFAB: ElementRef<HTMLElement>;

  fatalErrorParams: FatalErrorPageParams = undefined;
  coachManagingClient: CoachManagingClient | null = null;
  coachIntakeBannerClientName: string | null = null;
  showCoachOfflineOverlay = false;
  private docWatcher: DocWatcher;

  // read through UserDb, which reports a damaged or unreadable value once per page load (#5191)
  get impersonationUserAuth() {
    return this.userDb.getImpersonationUserAuth();
  }

  private subscribeToCoachBannerEvents() {
    // subscribe to coach banner events from intake form (coach filling intake on behalf of client)
    this.pubSub.subscribe(coachBannerEvent, (payload) => {
      this.coachIntakeBannerClientName = payload?.clientDisplayName ?? null;
      this.cd.markForCheck();
    });
  }

  getPageByWelcomePhase(): PageRef<any, any> {
    if (this.auth.getAuthRole() == AuthRole.Unknown && !this.coachClaim.hasPendingClaimToken()) {
      return ISubscribePage;
    } else if (!this.plan || this.plan.welcomePhase === WelcomePhase.Intro) {
      return IIntroCarouselPage;
    } else if (this.plan.welcomePhase === WelcomePhase.Intake) {
      return IIntakeFormPage;
    } else if (this.plan.welcomePhase === WelcomePhase.CoachedIntake) {
      // option B coach onboarding (#3389): the client claimed a blank
      // coach invite; route them into the intake form, which detects the
      // coached path and stops the wizard after Goals.
      return IIntakeFormPage;
    } else if (this.plan.welcomePhase === WelcomePhase.AwaitingCoachFinalize) {
      // option B coach onboarding (#3389): the client has submitted their
      // intake and is held on the holding page until the coach finalizes
      // the plan and welcomePhase flips to Done.
      return IAwaitingCoachPage;
    }
    return IHomePage;
  }

  getLearnMoreUrl(): string {
    const trackingParams = this.urlQuery.getTrackingDataAsUrlParams();
    let url = `https://mennohenselmans.com/mh-physique-app/?${trackingParams}&from_learn_more=true`;
    const hostPrefix = location.host.split('.')[0];
    if (['dev', 'local'].includes(hostPrefix)) {
      url += `&utm_campaign=${hostPrefix}`;
    }
    return url;
  }

  redirectToLearnMore(): void {
    location.href = this.getLearnMoreUrl();
  }

  async stopManagingClient() {
    this.coachPresenceState.teardown();
    this.userDb.setCoachManagingClient(null);
    await this.reloadAppAsync({ resetToRoot: true });
  }

  /** Tap handler for the floating "coach viewing" icon — shows an explanatory toast. */
  handleCoachViewingIconTap() {
    this.toastCtrl.showToast({
      uniqueId: 'coach-viewing-toast',
      message: LL.coach.presence.coachViewing(),
      type: ToastType.SuccessLight,
      duration: 4000,
      position: 'top',
      showCloseButton: true,
    });
  }

  async handleCoachIntakeBannerBack() {
    await this.navCtrl.popAsync();
  }

  // this is used when forcing the app to "go to the home page"
  async gotoDefaultPageAsync() {
    const pageRef = this.getPageByWelcomePhase();
    await this.navCtrl.setRootAsync(pageRef, {});
  }


  /** This must be called instead of location.reload(), so that it can handle eventual need to swap the service worker.
   *
   * Contract: this NEVER returns and NEVER rejects — it always reaches `location.reload()` and then
   * parks forever, because `reloadPending` is already true and nothing ever resets it. The teardown
   * and the guarantee that the reload happens both live in `reload-teardown.ts`; see the comment on
   * its try/finally, and design-docs/boot-sequence.md ("L8: a pending reload suppresses the
   * watchdog").
   */
  async reloadAppAsync(opts: ReloadAppOptions) {
    // #4942 milestone, promoted for the reason at APP STARTING: the app asking for
    // its own page to be replaced. Without it a boot log cannot tell a reload the
    // app chose from a page that died. `opts` is two booleans.
    console.info('Reloading app...', opts);
    // raised BEFORE the first await, deliberately. It is what suppresses the L8 navRestoring
    // watchdog, and nothing ever resets it — so from here on the invariant the watchdog relies on
    // is that a reload really is coming, which runReloadTeardownAsync guarantees in a finally.
    this.navCtrl.reloadPending = true;

    // #5449: stop the SQLite migration before anything is torn down.
    //
    // **The bulk copy matters more here than the follow, and it is the one that was
    // easy to miss.** The follow only exists once a copy has finished; the copy itself
    // runs for minutes, so the likeliest moment for a sign-out is while it is still
    // going. A replication still writing while the teardown deletes those files — and
    // wipes the record of their names — recreates a database holding the PREVIOUS
    // user's documents that nothing on the device can ever name or delete.
    //
    // Both calls are synchronous in effect and neither throws, so they cannot stop the
    // reload this method's contract promises. Nothing is awaited: cancelling is what
    // makes the writing stop, and waiting for handles to close would put a bounded
    // per-database wait in front of a page that is about to disappear anyway.
    this.sqliteLifecycle.cancelSqliteMigrationWork();
    this.sqliteLifecycle.stopFollowing();

    // the deps go in as a FUNCTION, not as a built object: building them reads browser globals that
    // can throw (`caches` is absent in a non-secure context, `localStorage` throws when storage is
    // blocked for the document), and a throw out here would skip the reload and wedge the app for
    // good. runReloadTeardownAsync calls it inside its own try/finally.
    await runReloadTeardownAsync(
      this.logging, () => this.buildReloadTeardownDeps(), opts, () => location.reload());

    // location.reload() doesn't stop JS execution — it schedules a reload while the current
    // call stack keeps running. Without this, callers continue past `await reloadAppAsync()`
    // and may clear databases or navigate to LoginPage before the reload actually happens,
    // destroying the Firebase Auth session in IndexedDB.
    await new Promise(() => {});
  }

  /** Hand the pre-reload teardown everything it reaches outside itself. Nothing here decides
   * anything — the decisions and their order live in `reload-teardown.ts`, which is where they can
   * be tested.
   *
   * The four browser globals below are GETTERS, and that is load-bearing rather than a style
   * choice. Reading one can throw — `caches` does not exist at all outside a secure context
   * (image-service.ts guards it with `'caches' in window` for the same reason), and
   * `window.localStorage` throws when the document has storage blocked. A plain property would
   * evaluate all four the moment this object is built, which is BEFORE the teardown starts, so a
   * device missing one would reload with nothing wiped at all: no telemetry flush, no storage or
   * cookie clear, no database deletions. A getter is evaluated where the teardown actually uses
   * it, which is where the old code touched it, so a throw costs only the steps after that point —
   * exactly as it did before this code moved. `reload-teardown.test.ts` pins that ordering with a
   * deps object whose `caches` getter throws.
   *
   * Do not "tidy" these back into plain properties. */
  private buildReloadTeardownDeps(): ReloadTeardownDeps {
    return {
      logging: this.logging,
      databaseRecovery: this.databaseRecovery,
      flushTelemetryAsync: () => this.telemetry.flushAsync(),
      shutdownFirebaseAsync: () => this.firebase.shutdownAsync(),
      // #5449: the recorded background-copy targets are handed over alongside the
      // provider's list. They are read from `localStorage` HERE rather than inside
      // `reload-teardown.ts`, which must stay free of browser globals so it can be driven
      // from a node test.
      //
      // **This read happens AFTER both localStorage wipes**, not before either of them:
      // the sign-out wipe runs first, and the teardown's own clear runs early while the
      // SQLite block asks for these names late. The names are still here only because both
      // wipes preserve the key on purpose. #5453 is what a device looks like when one of
      // the two forgets. See design-docs/android-local-database-engine.md, "Two notes must
      // survive BOTH localStorage wipes".
      localDatabaseNames: () => sqliteDatabaseNamesForSignOut(
        this.dbProvider.allDatabases.map(db => db.localDatabaseName),
        readMigrationTargetNames(localStorage.getItem(SQLITE_MIGRATION_TARGETS_KEY)),
        // df-a00755b05133: coach copies of client databases, closed by "Back to my account"
        // and so not in the provider's list. Read late for the same reason as the targets.
        readCoachCopyNames(localStorage.getItem(SQLITE_COACH_COPY_NAMES_KEY))),
      get localStorage() { return localStorage; },
      get sessionStorage() { return sessionStorage; },
      // cast because `databases()` is missing from the TypeScript DOM definitions — which is the
      // same reason the original code reached it through a bracket access.
      get indexedDb() { return indexedDB as unknown as IdbForDeletion; },
      get caches() { return caches; },
      clearCookies: clearAllCookies,
      clearWarmResumeFlag: () => AuthService.clearWarmResumeFlag(),
      replaceUrlWithRoot: () => history.replaceState(null, '', '/'),
      // A null loader is how the teardown is told there is nothing here to delete.
      //
      // It followed the ENGINE alone (#4981 phase 2): the moment an Android device
      // migrated, a platform check here would leave its SQLite files on disk through
      // sign-out, handing the next user the previous one's data.
      //
      // **#5449 adds the second fact, and it is the same leak one step earlier.** A
      // background copy keeps its target across boots on purpose, so a device still
      // reading and writing IndexedDB can now be carrying SQLite files full of the
      // signed-in user's workouts and queued photos. Asking only about the engine would
      // load no deleter at all and leave every one of them on the device. Everywhere
      // else — every browser, and every Android device the rollout has not reached —
      // both facts are false and the plugin is never loaded.
      //
      // df-4576933c7cf2 adds a third: a device phase 4 rolled back is on IndexedDB with no
      // targets, yet can still hold a coach's closed copy of a client database as a SQLite
      // file, which the rollback does not remove. Both notes survive both wipes, so reading
      // them here, before the teardown starts, gives the same answer as reading them late.
      loadSqliteDeleterAsync: signOutMustDeleteSqliteFiles(
        getLocalDatabaseEngine(),
        readMigrationTargetNames(localStorage.getItem(SQLITE_MIGRATION_TARGETS_KEY)),
        localStorage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY) !== null,
        readCoachCopyNames(localStorage.getItem(SQLITE_COACH_COPY_NAMES_KEY)))
        ? async () => {
          const { CsSQLite } = await import('services/cs-sqlite-plugin');
          return (sqliteName: string) => CsSQLite.deleteDatabase({ name: sqliteName });
        }
        : null,
      // #5692: through the service, which stops its own downloads first so none can put the
      // previous user's photo back after the folder is gone
      deleteNativeImageCacheAsync: () => this.nativeImageCache.stopAndDeleteAsync(),
    };
  }


  constructor(
    private toastCtrl: ToastController,
    private settings: SettingsService,
    private auth: AuthService,
    private locationService: LocationService,
    public userDb: UserDb,
    private exerciseDb: ExerciseDbService,
    private textDb: TextDbService,
    private commonDb: CommonDbService,
    private stapleDb: StapleDbService,
    private testCasesDb: TestCasesDbService,
    private foodDb: FoodDbService,
    private debug: DebugService,
    private notificationService: NotificationService,
    private overlayStack: OverlayStackService,
    private pubSub: PubSubService,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private appInstallSvc: AppInstallService,
    private dbProvider: DatabaseWrapperProvider,
    private navCtrl: NavController,
    private externalFlow: ExternalFlowService,
    private sso: SsoService,
    private logging: LoggingService,
    private databaseRecovery: DatabaseRecoveryService,
    public coachBranding: CoachBrandingStateService,
    private uiLanguageReconcile: UiLanguageReconcileService,
    private deepLinkSignIn: DeepLinkSignInService,
    private configDb: ConfigDbService,
    private telemetry: TelemetryService,
    private ssoSvc: SsoService,
    private appService: AppService,
    private firebase: FirebaseService,
    private onlineStateService: OnlineStateService,
    private connectivityResume: ConnectivityResumeService,
    private cd: ChangeDetectorRef,
    private loadingCtrl: LoadingController,
    private urlQuery: UrlQueryService,
    private referral: ReferralService,
    private coachClaim: CoachClaimService,
    private coachTierService: CoachTierService,
    private coachManageDeepLink: CoachManageDeepLinkService,
    private cloudFunctions: CloudFunctionsService,
    private coachNotify: CoachNotifyService,
    private e2eTestHooks: E2eTestHooksService,
    public navMenu: AppNavigationMenuService,
    public bugReportFab: BugReportFabService,
    private bootDiagnostics: BootDiagnosticsReportersService,
    public coachPresenceState: CoachPresenceStateService,
    private coachExerciseSync: CoachExerciseSyncService,
    private imageUploadOutbox: ImageUploadOutboxService,
    private nativeImageCache: NativeImageCacheService,
    private foodUnpublishOutbox: FoodUnpublishOutboxService,
    private accountHint: AccountHintService,
  ) {
    // build the services that do their whole job from their own constructor and are
    // never called into. See app-wide-listeners.ts for what belongs in that list and
    // why they are not constructor parameters here.
    buildAppWideListeners();

    // #6788: read the purchase-reload note now, on every page load, so it is removed even
    // when nothing reports this start. Left in sessionStorage, it would label a later reload.
    pageStartedAfterPurchaseReload();

    setMyApp(this);

    this.appService.setMyApp(this);
    this.logging.initialize(this, this.telemetry, this.dbProvider);
    // a Mixpanel SDK start-up failure was held back until LoggingService could report it
    // in full (df-3d12354a71ab)
    this.telemetry.reportMixpanelSdkInitFailure();

    // Give the engine question somewhere to report to (#4981 phase 2). It is asked
    // from free functions — getLocalAdapterOpts, shared by every local database — which have no
    // LoggingService in scope, so the reporter is handed over here, the same shape as
    // setMyApp(this) above. It goes AFTER logging.initialize because LoggingService
    // only has its telemetry client from that line on; anything the engine question
    // already raised during an earlier database open is buffered and flushed now.
    setLocalDatabaseEngineReporter((problemName, context) => this.logging.trackSilentProblem(problemName, context));

    // Give SettingsService.saveInBackground somewhere to report a real save failure
    // (df-0778ad3a63d7). Handed over for the same reason as the line above: that service is
    // built with no constructor arguments, so it cannot take LoggingService through DI.
    // Installed unconditionally and here, in the constructor — unlike the settings SYNC
    // callback, which is installed later in boot and deliberately skipped while a coach
    // manages a client. A background save can fail in either situation, so its reporter must
    // be in place for both.
    this.settings.setSaveFailureReporter((problemName, context) => this.logging.trackSilentProblem(problemName, context));
    this.navCtrl.setLogger(this.logging);

    // F8 (#3991): announce the offline / back-online transitions from here, at app level.
    this.connectivityResume.subscribeToCloudStateToasts();

    // record the native prod-bundle backend-routing decision (#4280) in
    // telemetry so the next Apple Review — or a #4273-style lockout report —
    // can be verified from data, not re-diagnosed. Reports only; the routing
    // decision itself belongs to getApiOriginAsync(). Does not block boot — it
    // waits for the route in the background and tags Sentry when it lands.
    this.bootDiagnostics.reportBackendRouteTelemetry();

    // the side menu and the bottom bar; app.html binds to the service directly
    this.navMenu.initialize({
      isFullAppUnlocked: () => this.isFullAppUnlocked(),
      isWelcomePhaseDone: () => this.isWelcomePhaseDone(),
      getDocWatcher: () => this.docWatcher,
      markForCheck: () => this.cd.markForCheck(),
    });
    this.subscribeToCoachBannerEvents();
    // the "report a bug" camera button; app.html binds to the service directly
    this.bugReportFab.initialize({
      getFabElement: () => this.bugFAB,
      getCaptureRoot: () => this.splitPane.getNativeElement(),
    });
    this.setDevFavicon();

    // Tier C white-labeling (#3707): theme the in-app splash from the
    // localStorage branding cache before the user DB loads, so a warm boot of a
    // branded client shows the coach logo + accent immediately. The authoritative
    // applyActiveBranding() runs later from the as-client doc. First-ever cold
    // boot has no cache (shows MH); sign-out wipes the cache.
    // The branding service re-renders the app shell through this callback, which
    // is what AppComponent used to do inline with its own markForCheck().
    this.coachBranding.onChanged = () => this.cd.markForCheck();
    // same bridge for coach presence: its watchers used to call this
    // component's markForCheck() inline.
    this.coachPresenceState.onChanged = () => this.cd.markForCheck();
    this.coachBranding.applySplashBrandingFromCache();

    // Install the E2E / DevTools window hooks. This call must stay HERE, in the
    // constructor: an Angular service is built on first injection, so hooks that
    // nothing injects early would never register at all, and the symptom would be
    // E2E specs failing with "not a function". The doc watcher is handed over as a
    // getter rather than a value because the boot sequence has not created it yet.
    this.e2eTestHooks.register({
      getDocWatcher: () => this.docWatcher,
      fakeNextDayAsync: () => this.fakeNextDayAsync(),
      markForCheck: () => this.cd.markForCheck(),
    });

    this.navCtrl.setPageFilter((page, params) => this.pageFilterAsync(page, params));

    if (this.sso.parseUrl()) {
      this.loadingTextsId = '@help:loading-hints.community';
    }

    // #5895: an onboarding email's link names the account it was sent to. Take it out of
    // the address bar now, before Sentry and Mixpanel record the address with anything.
    this.accountHint.captureFromBootUrl();
  }

  /**
   * A signed-in app has finished loading, so a dialog now has somewhere to appear. Lets
   * the account hint (#5895) check whether this is the account the link named. Called at
   * cold Ready, and on a warm resume once the navigation-restore overlay has cleared.
   */
  private notifySignedInAppReady() {
    // #6180: a push tap that launched the app waits for this moment before it reads the
    // user database and opens the reminder's entry
    this.notificationService.markSignedInAppReady();
    this.accountHint.onSignedInAppReady(() => {
      // the app's own signed-in user, not Firebase's: on a warm resume Firebase has not
      // restored the session yet at Ready, and its currentUser still reads null
      const appUser = this.auth.user;
      return {
        signedIn: !!appUser,
        signedInEmail: appUser?.email ?? null,
        otherLinkFlowPending: this.externalFlow.hasExternalFlowPending() || this.coachClaim.hasPendingClaimToken(),
        actingForSomeoneElse: !!this.userDb.getCoachManagingClient() || this.impersonationUserAuth != null,
      };
    });
  }

  /**
   * Tells the native boot watchdog that this boot finished (`bootComplete`, which deletes this
   * launch's boot log and resets the renderer-crash guard), and reports the previous launch's log
   * if that launch never finished. Called at cold Ready, and on a warm resume once its navigation
   * restore settles or is rescued (#6202). At most once per page, because a rescued warm restore
   * can still finish later, and a warm resume that falls back to a cold start reaches cold Ready.
   */
  private reportPreviousFreezeOnce() {
    if (this.hasSentBootComplete) return;
    this.hasSentBootComplete = true;
    void this.bootDiagnostics.reportPreviousFreezeIfAny();
  }

  async pageFilterAsync(page: string, params?: any): Promise<string> {
    // if unsure, redirect home (or to learn more if not logged in)
    if (page == null) {
      if (!this.auth.user) {
        // if there is a pending sign-in token or checkout result, let login/subscribe handle it
        if (this.externalFlow.hasExternalFlowPending()) {
          return this.externalFlow.getSignInToken() ? 'LoginPage' : 'SubscribePage';
        }
        // non-authenticated user with empty URL → show sign-in form
        // on Capacitor, skip the landing page (learn more doesn't apply)
        void this.navCtrl.setRootAsync(ILoginPage, { mode: isCapacitorNative() ? 'login' : 'landing' });
        return null;
      }
      return 'HomePage';
    }

    if (typeof window.indexedDB === 'undefined') {
      this.setFatalErrorParams({ type: 'lockdown_mode_error', error: 'IndexedDB is not available' });
      return 'FatalErrorPage';
    }

    // fatal error page is always allowed; if already in fatal error, block all other navigation
    if (page === 'FatalErrorPage' || this.fatalErrorParams) {
      return 'FatalErrorPage';
    }

    // if the user is not logged in
    if (!this.auth.user) {
      // Prevent accessing any pages other than the few that a person with no
      // account is allowed to see.
      //
      // ReferralInvitePage (#4627) is the newest of them, and it is the reason
      // this list is worth a comment. A referral invite is opened by a stranger
      // who has never used the app: the whole feature depends on that page
      // rendering for somebody who is not signed in. Registering a page in
      // app-page-list.ts does NOT achieve that — this array is the gate, and a
      // page missing from it simply shows the login screen instead, with nothing
      // reported anywhere. Do not confuse it with `allowAtWelcome`, which is
      // checked further down and governs a SIGNED-IN user whose plan has not
      // been finished yet.
      if (['LoginPage', 'SubscribePage', 'ReferralInvitePage'].indexOf(page) < 0) {
        // note that we _allow_ subscribe page, but only if directed to it

        // if we have a checkout error, go to subscribe to show it
        if (this.externalFlow.getLastCheckoutError()) {
          return 'SubscribePage';
          // in all other cases, show login
        } else {
          return 'LoginPage';
        }
      }

      // if the user is logged in
    } else {
      // if the user is in a web view, allow only a few pages, and force login to make them use the magic link to get out of web view
      if (this.auth.isWebView()) {
        // note that this logic doesn't do fall-through - this block is the only one that decides about webviews with logged in users
        if (['HelpPage', 'FatalErrorPage'].indexOf(page) >= 0) { // we probably don't need those, but just in case
          return page;
        } else {
          return 'LoginPage';
        }
      }

      // if a coach claim token is in the URL, redirect to the claim page
      // (alerts cannot show during boot, so the claim page handles the full flow)
      if (this.coachClaim.hasPendingClaimToken()) {
        return 'CoachClaimPage';
      }
      // with no claim pending the claim page has nothing to do and shows an empty
      // screen with the menu off. a reload after the server accepted a claim but
      // before the "connected" alert was dismissed boots here with no token and no
      // bridge flag (df-97e4565467bb), so send it home; the filter re-runs on
      // HomePage, so the welcome and paywall rules still apply
      if (page === 'CoachClaimPage') {
        return 'HomePage';
      }

      // if a coachManage deep link is in the URL, enter client-manage mode and reload
      if (this.coachManageDeepLink.hasPendingCoachManage()) {
        void this.coachManageDeepLink.processCoachManageAsync();
        return null; // app will reload
      }

      // if a client's own notification link is in the URL (#6433), remember the page it names.
      // it is opened on top of the home page once the app is ready, by
      // CoachManageDeepLinkService (#6573)
      this.coachManageDeepLink.adoptClientPageFromUrl();

      // while the coach is inside a client's account, shut their own coaching
      // screens — see COACH_ONLY_PAGE_NAMES for why. This arm must stay BELOW
      // the two above: an invite token still has to reach the claim page, and a
      // coachManage link still has to be able to start a session.
      if (COACH_ONLY_PAGE_NAMES.includes(page) && this.userDb.getCoachManagingClient()) {
        return 'HomePage';
      }

      // prevent accessing login page if already logged in
      if (page === 'LoginPage') {
        return 'HomePage';
      }
      const canCancelSubscription = this.auth.getCanCancelSubscription();
      // a warm restart shows the restored page before its server login, so the record
      // getCanCancelSubscription reads may not be loaded yet. then let the unsubscribe
      // page open: it decides for itself once the record arrives (df-e26cc1c4b021)
      const subscriptionRecordUnknown = !this.auth.couch;
      if ((page === 'UnsubscribePage' && !canCancelSubscription && !subscriptionRecordUnknown) ||
        (page === 'SubscribePage' && canCancelSubscription)) {
        return 'HomePage';
      }

      // the test pages are allowed for all paid users
      if (page.startsWith('Test') && this.auth.getAuthRole() >= AuthRole.Paid) {
        return page;
      }

      if (!this.isFullAppUnlocked()) { // users whose subscription has expired, are limited to welcome pages only
        const welcomePageClass = this.getPageByWelcomePhase();
        // #4139: a coach who is managing (impersonating) a client must be able to
        // reach the intake-review form even while the client's plan is still in a
        // welcome phase (AwaitingCoachFinalize / CoachedIntake). Without this the
        // intake-review deep-link push of IntakeFormPage is redirected here to the
        // client's awaiting-coach holding page, and the coach can never review the
        // intake or build the plan. Only a coach impersonation ever sets
        // getCoachManagingClient(), so this never loosens the welcome lock for a
        // real client.
        const isCoachReachingIntakeForm = !!this.userDb.getCoachManagingClient() && page === 'IntakeFormPage';
        // a couple of aux pages are allowed during welcome
        if (!isCoachReachingIntakeForm && !getAllowedPageDuringWelcome().some(p => p == page) && !page.startsWith('Admin')) {
          return getPageName(welcomePageClass);
        }
      }

      // defense-in-depth: if app is fully unlocked, welcome pages should only be
      // reachable with explicit params (e.g. intake-form with mode=change for
      // editing program settings). Bare navigation (stale URL, pre-reload nav)
      // redirects to home.
      if (this.isFullAppUnlocked()) {
        if (page === 'IntroCarouselPage') {
          return 'HomePage';
        }
        if (page === 'IntakeFormPage' && params?.mode !== 'change' && !params?.coachClientSlotId) {
          return 'HomePage';
        }
        // #4193: a Done plan must never land on the coach-onboarding holding page
        // (e.g. a stale URL / boot restore into page-awaiting-coach after finalize).
        // Defense-in-depth: the primary fix routes the coached finalize to Home
        // directly (intake-form performFinishIntakeAsync), but this backstops any
        // other path that navigates to the awaiting-coach page once unlocked.
        if (page === 'AwaitingCoachPage') {
          return 'HomePage';
        }
      }
    }

    return page;
  }

  public setFatalErrorParams(params: FatalErrorPageParams) {
    this.fatalErrorParams = { ...params };
    console.error('FatalError: ', JSON.stringify(params));
    // this boot is over and will never read the parameters a cold-start link
    // handed it, so let the person's one natural recovery — tapping the link
    // again — reload the app instead of being ignored as a repeat delivery
    this.deepLinkSignIn.forgetAdoptedBootParams();
  }

  public markForCheck() {
    // #4283: guard against a call landing after logout has torn the root down —
    // this is reachable from auth.service.ts's couch-login retry loop, which can
    // still be in flight during a sign-out race (CLAUDE.md's NG0911 markForCheck rule)
    if (this.appDestroyed) return;
    this.cd.markForCheck();
  }

  public showRetryProgressBar(totalDurationMs: number) {
    this.loadingProgressType = 'bar';
    this.cd.markForCheck();
    // animate from 0% to 100% over the total retry duration
    // use setTimeout to let Angular render the bar element first
    setTimeout(() => {
      if (this.progressBarFill) {
        this.progressAnimationStart = 0;
        this.progressAnimationEnd = 1;
        const el = this.progressBarFill.nativeElement as HTMLElement;
        el.style.setProperty('opacity', '1');
        this.progressAnimation = el.animate(
          [{ width: '0%' }, { width: '100%' }],
          { duration: totalDurationMs, fill: 'forwards', easing: 'linear' },
        );
      }
    }, 0);
  }

  private isWelcomePhaseDone(): boolean {
    return this.plan && this.plan.welcomePhase === WelcomePhase.Done;
  }

  // if user has completed onboarding and has an active subscription
  isFullAppUnlocked(): boolean {
    return this.isWelcomePhaseDone() && this.auth.getAuthRole() >= AuthRole.Paid;
  }

  // boot sequence must start here so that the Nav is ready
  ngAfterContentInit() {
    void this.appBootSequenceAsync();
  }

  onNewPage(screenName: string) {
    this.updatePageTitle(screenName);
    this.telemetry.trackScreenEnter(screenName);
    this.pubSub.publish(PageEnteredEvent, { screenName });
  }

  ngOnDestroy() {
    this.appDestroyed = true;
    this.coachPresenceState.teardown();
    // Both of these services live for the whole application, so they outlive
    // this component. Drop their re-render callbacks, or they keep holding a
    // reference to a destroyed view and a later call would mark a dead view for
    // check. Before these concerns were lifted out of this component the state
    // died with the component and the question could not arise.
    this.coachPresenceState.onChanged = null;
    this.coachBranding.onChanged = null;
  }

  /*
  TODO: The main reason we WERE setting the titles WAS for Google Analytics!
  For that, they had to be constant (no variable things like dates etc in the title).
  Now that this is no longer needed, we can set more informative, like put Yesterday instead of "Past"
  */
  updatePageTitle(pageTitle: string) {
    // get the title from the table
    let title = getPageTitleByName(this.navCtrl.getPageBySelector(pageTitle));
    // if not defined (shouldn't happen!) construct one from the screen name
    if (!title) {
      // this page is missing from both the translated-title map and the deep-link list --
      // report it so a future page addition that forgets to register its title gets caught,
      // instead of silently shipping the raw-URL fallback below forever
      this.logging.trackSilentProblem('page-title-missing', { pageTitle });
      const screenNameParts = pageTitle.split('-');
      if (screenNameParts[0] === 'page') {
        screenNameParts.shift();
      }
      // capitalize first letter of each word; a part can be empty (e.g. a double hyphen in
      // the selector), so guard the first-character read
      const constructedTitle = screenNameParts.map(p => p ? p[0].toUpperCase() + p.slice(1) : p).join(' ');
      // dev-only last-resort label built from the raw URL segment when the page
      // is missing from both the translated-title map and the deep-link list
      title = untranslatedString(constructedTitle);
    }
    console.log(`App: Entering page ${pageTitle} (${title})`);

    this.setPageTitle(title);
  }

  setPageTitle(title: TranslatedString) {
    document.title = title;
    console.log(`App: Setting title to ${title}`);

    /* This is a cludge to avoid the bug where sometimes (1 out of 10 or so?), the title doesn't get updated in the tab header.
    Note that the actual value is changed, and inspecting the page contents the html>header>title value is correct.
    So the problem is just in tab rendering. The bug apparently happens both in Chrome and Edge, so it's probably somewhere in Chromium. */
    void (async () => {
      await (sleep(500));
      const title = document.title;
      document.title = '';
      document.title = title;
    })();
  }


  // F24 (#3991): a signed-in user booting on a device that has never synced, while offline, used
  // to land in empty onboarding — exactly as if they were a brand-new user. That is a lie, and a
  // frightening one: it looks like their account and their whole history is gone. All their data
  // is on the server; this device simply has not downloaded it yet.
  //
  // So we do not route at all. We hold the splash with an honest message and wait for a
  // connection. Coming back online re-boots the app cleanly, which then takes the normal initial
  // sync path.
  //
  // The user must also be able to get out of this screen by hand. The device can rejoin a network
  // without the WebView ever firing an `online` event — that unreliability is the reason
  // OnlineStateService exists at all — so waiting only for the event above can strand someone who
  // is, in fact, back online. `offlineFirstLoadBlocked` therefore also renders the Retry button on
  // the held splash (see app.html), and onSplashRetry() forces a fresh probe; a probe that succeeds
  // publishes the Online state this subscription is waiting for.
  //
  // Returns true when the boot was gated, so the caller stops (the splash is never hidden).
  private gateNeverSyncedOfflineBoot(): boolean {
    const database = this.userDb?.database;

    const neverSyncedOffline = isNeverSyncedOfflineBoot({
      initialSyncCompleted: database?.initialSyncCompletedState,
      hasSyncedOnThisDeviceMarker: !!database?.hasSyncedOnThisDeviceMarker,
      connectionOnline: this.onlineStateService.connectionState === ConnectionState.Online,
    });
    if (!neverSyncedOffline) return false;

    // Second condition: only when this device really HAS no data for this account.
    //
    // The test is `_rev`, and that is the whole point. `getSingleton` hands back a DEFAULT plan
    // object when the document does not exist locally, so `this.plan` is never null and its
    // welcomePhase is Intro — meaning "no plan at all" and "a real plan, mid-onboarding" look
    // identical by value. Only a document that has actually been written to PouchDB carries a
    // `_rev`. So:
    //   - no `_rev`  -> the plan document does not exist on this device. Nothing was ever downloaded
    //                   or created here, which is the situation this gate is for.
    //   - has `_rev` -> this device has a real plan for this account, however early in onboarding.
    //                   The user signed up HERE. There is nothing on the server we are keeping from
    //                   them, so holding them on the splash would strand them for no reason — and
    //                   they could carry on onboarding offline before this change, so it would also
    //                   be a regression.
    //
    // Testing welcomePhase instead was the original bug: it caught the second case as if it were the
    // first, and locked a signed-up-but-not-yet-onboarded user on the splash forever, telling them to
    // connect so we could load an account the server does not have. `_rev` separates them properly,
    // and unlike the localStorage markers it is also correct for installs that UPGRADE into this
    // build (they have no settled marker, because no earlier build ever wrote one).
    const localDataIsEmpty = !this.plan?._rev;
    if (!localDataIsEmpty) return false;

    console.log('App: signed-in user booting offline on a device that has never synced — holding the splash instead of showing onboarding');
    this.offlineFirstLoadBlocked = true;
    this.firstSyncMessage = LL.app.offlineFirstLoadMessage();
    // the splash prefers a sync-retry message over the first-sync one, so a retry line left over
    // from a sync that started while we still had a connection would hide the honest offline
    // message we just set. clear it.
    this.syncRetryMessage = null;
    this.cd.markForCheck();

    const subscription = this.pubSub.subscribe(connectionStateChangedEvent, (state: ConnectionState) => {
      if (state !== ConnectionState.Online) return;
      // one shot: unsubscribe before reloading so a second Online event cannot start a second reload
      subscription.unsubscribe();
      console.log('App: back online after an offline first load — reloading to run the initial sync');
      void this.reloadAppAsync({ resetToRoot: false });
    });

    return true;
  }

  // #6248: start-up could not read some of the user's core documents (settings, plan, schedule…)
  // from the server OR from this device, so the app holds only empty defaults for them. The
  // owner's rule (2026-10-07): a session never starts on those. Showing the app would show an
  // account that looks brand new, and the first save of a default (the settings migration just
  // below this gate in the cold start saves one straight away) would overwrite the real document
  // on the server. So the loading screen stays up, the same way the F24 gate above holds it.
  //
  // It comes back by reloading the app, which re-runs the start-up read:
  //  - when the connection comes back (the same Online event F24 waits for),
  //  - when the background initial sync succeeds: the documents are then on this device, and
  //    the reloaded start-up reads them from here,
  //  - or when the person taps Retry.
  //
  // The problem is reported only if the hold is still up after ACCOUNT_LOAD_HOLD_REPORT_DELAY_MS.
  // Both production users traced for #6248 were on a page that was navigating away while its
  // start-up ran; that page's timer dies with it, so it does not report a hold nobody saw.
  //
  // Returns true when the boot was held, so the caller stops (the splash is never hidden).
  private gateUnreadAccountDocumentsBoot(): boolean {
    const unreadIds = this.userDb.unreadBootSingletonIds;
    if (unreadIds.length === 0) return false;

    console.warn('App: start-up could not read the account documents — holding the loading screen:', unreadIds);
    this.accountLoadBlocked = true;
    const isOffline = this.onlineStateService.connectionState !== ConnectionState.Online;
    // the offline wording when the device knows it is offline, because then that IS the reason
    this.firstSyncMessage = isOffline ? LL.app.offlineFirstLoadMessage() : LL.app.accountLoadFailedMessage();
    // the splash prefers a sync-retry message over this one; clear it so the honest one shows
    this.syncRetryMessage = null;
    this.cd.markForCheck();

    // one shot: whichever trigger fires first reloads, and the other is let go
    let reloading = false;
    let onlineSubscription: { unsubscribe(): void } | null = null;
    let stopWaitingForSync: (() => void) | null = null;
    const reloadOnce = (why: string) => {
      if (reloading || this.appDestroyed) return;
      reloading = true;
      onlineSubscription?.unsubscribe();
      stopWaitingForSync?.();
      console.log(`App: ${why} after a held account load — reloading to read the documents again`);
      void this.reloadAppAsync({ resetToRoot: false });
    };
    // the automatic triggers are rate-limited so a cause that does not go away cannot become a
    // reload loop (see account-load-hold.ts); Retry below always reloads
    const autoReload = (why: string) => {
      if (reloading) return;
      const nowMs = Date.now();
      if (!mayAutoReloadAccountLoadHold(this.readLastAccountLoadAutoReloadMs(), nowMs)) {
        console.log(`App: ${why} during a held account load, but it reloaded on its own moments ago — waiting for Retry`);
        return;
      }
      this.writeLastAccountLoadAutoReloadMs(nowMs);
      reloadOnce(why);
    };
    onlineSubscription = this.pubSub.subscribe(connectionStateChangedEvent, (state: ConnectionState) => {
      if (state === ConnectionState.Online) autoReload('back online');
    });
    stopWaitingForSync = runOnInitialSyncSuccess(
      listener => this.userDb.database.onInitialSyncSettled(listener),
      () => autoReload('the initial sync finished'),
    );
    this.reloadHeldAccountLoad = () => reloadOnce('Retry tapped');

    // raw setTimeout, not safeTimeout: MyApp is the never-destroyed root shell
    setTimeout(() => {
      if (reloading || this.appDestroyed || !this.accountLoadBlocked) return;
      this.logging.trackSilentProblem('Db: boot held, account documents could not be read', {
        doc_ids: unreadIds,
        count: unreadIds.length,
        connection_state: this.onlineStateService.connectionState,
        initial_sync_completed: this.userDb.database?.initialSyncCompletedState,
        has_synced_on_this_device: !!this.userDb.database?.hasSyncedOnThisDeviceMarker,
      });
    }, ACCOUNT_LOAD_HOLD_REPORT_DELAY_MS);

    return true;
  }

  /** #6248: when the account-load hold last reloaded on its own in this tab, or null. */
  private readLastAccountLoadAutoReloadMs(): number | null {
    try {
      const raw = sessionStorage.getItem(ACCOUNT_LOAD_AUTO_RELOAD_KEY);
      return raw == null ? null : Number(raw);
    } catch (err) {
      // sessionStorage refused by the browser (storage blocked). Without the record the reload is
      // allowed, so the loop guard cannot work in this tab; reported so that is visible
      this.logging.trackSilentProblem('AccountLoadHoldReloadRecordUnreadable', errorReportFields(err));
      return null;
    }
  }

  /** #6248: record that the account-load hold reloaded on its own, so the next start-up's hold
   *  waits for Retry instead of reloading again straight away. */
  private writeLastAccountLoadAutoReloadMs(nowMs: number): void {
    try {
      sessionStorage.setItem(ACCOUNT_LOAD_AUTO_RELOAD_KEY, String(nowMs));
    } catch (err) {
      // the loop guard cannot work in this tab, so a cause that does not go away would reload
      // again on every start-up. Reported so that is visible
      this.logging.trackSilentProblem('AccountLoadHoldReloadRecordUnwritable', errorReportFields(err));
    }
  }

  // #4617: the same never-synced device as the gate above, but the network is UP and our backend is
  // the thing that did not answer. The gate declines to hold in that case, so the person is routed
  // into new-user onboarding — which, to someone who already has an account, looks exactly as if
  // their whole history were gone.
  //
  // We do NOT change any routing or anything the user sees. Widening the gate to hold here too is a
  // real trade rather than an obvious improvement (see isNeverSyncedServerUnreachableBoot's comment
  // for why it would strand a brand-new signup). #4617 decided on 2026-10-02 not to widen, because
  // this report recorded 0 production events. Nothing else the app records separates a never-synced
  // device from any other, so this report stays: it fires once per cold boot, so a future occurrence
  // is still seen.
  //
  // Tier `problem`, not `info`: nothing has been repaired at this point. The deferred login retry is
  // armed but has not succeeded, and the next thing the user sees is the misleading screen.
  //
  // One side effect the word "measurement" would otherwise hide: every problem-tier report marks the
  // session unhealthy (noteSilentProblem), which suppresses the store-review prompt for the rest of
  // it. That is intended here — this person has just been shown a screen that lies to them — but it
  // does mean this is not literally a no-op. See design-docs/telemetry.md.
  private reportNeverSyncedServerUnreachableBoot(): void {
    const database = this.userDb?.database;

    const isNeverSyncedServerUnreachable = isNeverSyncedServerUnreachableBoot({
      initialSyncCompleted: database?.initialSyncCompletedState,
      hasSyncedOnThisDeviceMarker: !!database?.hasSyncedOnThisDeviceMarker,
      connectionOnline: this.onlineStateService.connectionState === ConnectionState.Online,
      // same _rev test the gate uses, and for the same reason: a defaulted plan object exists even
      // when nothing was ever downloaded, so only a written document proves local data
      localPlanIsEmpty: !this.plan?._rev,
      couchLoginRetryNeeded: this.auth.couchLoginRetryNeeded,
    });
    if (!isNeverSyncedServerUnreachable) return;

    this.logging.trackSilentProblem('NeverSyncedBootServerUnreachable', {
      couch_login_retry_needed: this.auth.couchLoginRetryNeeded,
      initial_sync_completed: database?.initialSyncCompletedState,
      // which kind of outage this was — the name alone, so no free-text server message travels
      last_couch_login_error: this.auth.getLastCouchLoginErrorReport().name,
      // the field that makes the count answerable at all. Everything above is true BOTH for someone
      // whose account is months old and cannot reach their data, and for someone who created an
      // account minutes ago while our backend happened to be down — and only the first of those is
      // what #4617 is about. A tiny age says the second; a large one says the first.
      account_age_hours: this.auth.getAccountAgeHours(),
      app_native: isCapacitorNative(),
    });
  }

  /**
   * If the operating system started this app from a link carrying boot
   * parameters, hand them to the boot that is running right now.
   *
   * This is why a cold-start magic link no longer costs a whole second boot, and
   * it MUST stay ahead of the Firebase auth init — that init reads `signInToken`
   * out of the address bar, and it also takes the warm-resume fast path for
   * somebody already signed in on this device. A link NOT carrying boot
   * parameters is left untouched here for the ordinary launch-link handling
   * further down the boot, which runs after the auth init because its page
   * navigation needs one. Both rules, and what breaks without them, are in
   * design-docs/purchase-deep-linking.md, "A cold-start link is read in place".
   *
   * Nothing here is allowed to stop the boot.
   */
  private async adoptLaunchUrlBootParamsAsync(): Promise<void> {
    // the two outcomes are told apart by a flag rather than by the value, because
    // "the phone did not answer in time" and "the app was not started from a link"
    // both arrive as no launch URL and mean completely different things
    const answer = await awaitBoundedLive(this.launchUrlPromise, LAUNCH_URL_WAIT_LIMIT_MS);

    if (answer.timedOut) {
      // the plugin only reads a string the native side already stored, so taking
      // longer than the limit is abnormal. Report it rather than letting it look
      // identical to "the app was not started from a link": the person who tapped
      // a magic link gets a login page instead, and nothing else would say why.
      this.logging.trackSilentProblem('LaunchUrlWaitHitItsLimit', {
        limit_ms: LAUNCH_URL_WAIT_LIMIT_MS,
      });
      return;
    }
    if (!answer.value?.url) return;

    try {
      // no address is logged here: the ordinary launch-link block further down the
      // boot logs it once, redacted, and this runs for the same link
      await this.deepLinkSignIn.adoptLaunchUrlBootParamsAsync(new URL(answer.value.url));
    } catch (err) {
      // an unparseable launch URL, or an address bar that refused the write. Boot
      // carries on without the parameters, which shows the person a login page
      // instead of signing them in — so this must not be swallowed silently.
      //
      // The error's own message is deliberately NOT reported. Both failures this
      // catches can quote the address they were given, and on this path that
      // address is the launch link's query — which is where the sign-in token
      // lives. WebKit's invalid-URL message does exactly that, and describeError
      // passes a message through untouched. The name alone says which failure it
      // was without carrying a credential into Sentry.
      this.logging.trackSilentProblem('AdoptLaunchUrlBootParamsFailed', {
        error_name: (err as any)?.name ?? 'unknown',
      });
    }
  }

  /**
   * The install-referrer answer the boot stopped waiting for (df-febb6fcfb9e9).
   * The check never runs again once started, so a late claim token is still
   * exchanged, but only while nobody has signed in by other means; the exchange
   * checks that again right before it signs in. A late failure is reported the
   * same way an on-time one is.
   */
  private async handleLateInstallReferrerAsync(referrerPromise: Promise<string | undefined>): Promise<void> {
    try {
      const lateToken = await referrerPromise;
      if (!lateToken || this.firebase.fbAuth.currentUser) return;
      console.log('App: Install referrer claim token found (late)');
      await this.deepLinkSignIn.exchangeLateInstallReferrerClaimTokenAsync(lateToken);
    } catch (err) {
      this.logging.trackSilentProblem('InstallReferrerCheckFailed', errorReportFields(err));
    }
  }

  // S4 (#3986): initialize the native SocialLogin plugin (Google provider,
  // plus the Apple provider on iOS). Started from appBootSequenceAsync
  // concurrently with the Firebase auth init — it used to run serially after
  // it on the splash-blocking path. Never rejects: a failure is surfaced as
  // a silent problem and boot continues (callers await the returned promise
  // only to ORDER against the init, not to observe errors).
  private async initializeSocialLoginAsync(): Promise<void> {
    const isProd = AppEnvironment.firebaseConfig.projectId === 'cybernetic-fitness-app';
    const webClientId = isProd
      ? '896009282039-q9848jg75b99fncrgeukjp54vn18k8gv.apps.googleusercontent.com'
      : '570795989423-q0pj7l36tgesecahanc0a1phr93m26dq.apps.googleusercontent.com';
    // iOSClientId rotates with iOS Firebase app re-registration; see
    // design-docs/mobile-app-accounts.md "Code Files with App ID References".
    const iOSClientId = isProd
      ? '896009282039-puson9i82hgc634n60usarnr257phvuh.apps.googleusercontent.com'
      : '570795989423-krnsrhgb47rmrn5mjvom6r3ctlf7ncqf.apps.googleusercontent.com';
    try {
      // Apple Sign-In is iOS-only here. The plugin's Android Apple
      // implementation requires `apple.android.redirectUrl` and throws
      // `"apple.android.redirectUrl is null or empty"` from
      // `SocialLogin.initialize` when an `apple` block is present
      // without it. That throw is fatal: initialize bails before the
      // Google provider is registered, and every subsequent
      // `SocialLogin.login({ provider: 'google' })` fails with
      // `"Cannot find provider 'google'. Provider was not initialized."`
      // (the regression behind #3678 on Android). Apple Sign-In on
      // Android is not a planned platform here — see the
      // androidbrowserhelper resolution block in
      // `client/android-overlay/app/build.gradle` — so the apple block
      // is omitted on non-iOS.
      //
      // On iOS: `useProperTokenExchange: true` populates the
      // `authorizationCode` field on the Apple login response so the
      // SiwA capture path in `auth.service.ts` can exchange it
      // server-side for a refresh_token (then used at deletion time for
      // /auth/revoke). Apple's documented OAuth flow; legacy mode
      // (default) stuffs the code into `accessToken.token` and leaves
      // `authorizationCode` undefined.
      const initConfig: Parameters<typeof SocialLogin.initialize>[0] = {
        google: { webClientId, iOSClientId },
      };
      if (isPlatformIOS()) {
        initConfig.apple = { useProperTokenExchange: true };
      }
      await SocialLogin.initialize(initConfig);
    } catch (err: any) {
      // Surface to Sentry — a half-initialized SocialLogin shows up later
      // as opaque "Sign in with Google failed" with no auth/* code (see
      // #3678). Boot continues either way.
      console.error('SocialLogin: init failed, continuing boot', err);
      this.logging.trackSilentProblem('SocialLogin.initFailed', {
        code: err?.code,
        message: err?.message ?? String(err),
      });
    }
  }

  // S4 (#3986): lets auth.service.ts wait for the SocialLogin plugin init
  // before the first SocialLogin.login call. Resolves immediately on web /
  // non-native (the field is pre-resolved and only replaced on native).
  whenSocialLoginReadyAsync(): Promise<void> {
    return this.socialLoginReady;
  }

  async appBootSequenceAsync() {
    // O1 (#3986): fresh per-phase timing window for this boot. #4615: the same
    // call opens the window `booting_time` measures, so the total and the phases
    // are read off one monotonic clock — and so a sign-in can move that window.
    bootTimings.reset();
    // #4401: fresh outstanding-local-operation window too (see its own doc)
    localOpTracker.reset();
    // Step 21-prep (#4027): sample the persisted couch-session hint NOW,
    // before this boot's own login overwrites it — booting_time classifies
    // this sample into `prior_session_hint` at emit time
    this.bootStartSessionHint = getCouchSessionHint();

    // Step 22 (#4027, web only): capture the PREVIOUS boot's post-mortem
    // marker before this boot's first stage transition overwrites it, then
    // install the two annotation listeners: pagehide stamps "leaving on
    // purpose" (so tab closes / navigations / our own reloads never report),
    // and visibilitychange→hidden before Ready stamps "backgrounded mid-boot"
    // (downgraded like the native abandoned-background classification, #3797).
    if (!isCapacitorNative()) {
      try {
        this.previousWebBootMarkerRaw = readWebBootMarkerRaw();
      } catch (markerErr: any) {
        // expected: SecurityError when the browser blocks storage entirely —
        // boot must proceed; the marker feature simply stays inert. Deliberately
        // console-only: the same broken storage trips the FIRST marker write
        // moments later, which reports the once-latched WebBootMarkerWriteFailed
        // silent problem — a second report here would be a duplicate.
        this.previousWebBootMarkerRaw = null;
        console.warn('App: web boot marker unreadable:', markerErr?.message);
      }
      // #6181: right after the read, so the lock answer describes the same moment
      this.startWebBootLockTracking();
      this.watchForStaleWebBootMarkerRead(DateTime.utc().toISO());
      // clear the stored marker NOW: recordWebBootStage inherits the sticky
      // hiddenDuringBoot flag from whatever is stored, and without this
      // clear the new boot's first transition would inherit the PREVIOUS
      // boot's flag — contaminating the whole window and downgrading a
      // genuine foreground wedge (see clearWebBootMarker's comment).
      // Guarded SEPARATELY from the capture above: if only the removal
      // throws, the successfully-captured marker must still be reported —
      // folding both into one try would discard it.
      this.guardedWebBootMarkerWrite(() => clearWebBootMarker());
      // pagehide is a WINDOW event and does not bubble — registering it on
      // document would silently never fire (the exact false-WebBootIncomplete
      // bug this stamp exists to prevent); a wiring tripwire pins `window.`
      window.addEventListener('pagehide', () => {
        this.guardedWebBootMarkerWrite(() => markWebBootCleanExit());
      });
      document.addEventListener('visibilitychange', () => {
        // if the app is backgrounded before boot completes, annotate the marker.
        // ACCEPTED GAP: a hide landing between the clear above and the first
        // stage transition finds no marker to annotate (the helper no-ops), so
        // that sliver of backgrounded boots reports as incomplete instead of
        // the downgrade — inherent to the marker-must-exist-first design.
        if (document.visibilityState === 'hidden' && this.bootStage !== BootStage.Ready) {
          this.guardedWebBootMarkerWrite(() => markWebBootHiddenDuringBoot());
        }
      });
    }

    // #4942: `info`, not `log` — a production build drops `log` before the console
    // the native boot-log capture hooks, so this milestone would appear in no
    // production boot log at all. Which milestones are promoted, and why the rest
    // of the boot path deliberately is not: design-docs/boot-sequence.md, "A
    // production boot log only carries info, warn and error". The other promoted
    // milestones in this file carry the same short note.
    console.info(`------ APP STARTING ------ (${new Date().toISOString()})`);
    registerCapacitorSQLiteAdapter();
    // S3 (#3986 Step 19): open the SQLite boot READ-priority window — while
    // it is open the adapter's sync-write chunk loop yields to pending reads,
    // so boot-critical reads don't queue behind initial-sync write batches on
    // the one native bridge. Closed on EVERY boot-exit path: bootStage=Ready
    // (cold and warm), the cold boot-error catch, and the logged-out boot
    // completion (login page shown). A boot that never reaches any of those
    // is bounded by the gate's own failsafe, and a FIRED failsafe is abnormal
    // (wedged boot, sync throttled the whole window) so it surfaces via the
    // injected reporter. Harmless no-op on platforms that don't use the
    // SQLite adapter.
    //
    // #4616: the report carries three more fields so a reader can tell a boot the
    // user backgrounded from one that genuinely wedged — the two look identical
    // without them. NOTHING is suppressed; a backgrounded boot is still reported.
    // Why each field is there: design-docs/boot-sequence.md, "#4616".
    //
    // #5204: `wall_elapsed_ms` and `timer_native` say whether this client's timers
    // can be trusted at all — some desktop-Chrome clients fire every setTimeout
    // after about 1.8 seconds whatever delay it was given. The wall clock is read
    // as a SECOND, separately named measurement beside the monotonic one, never in
    // place of it: only the pair can disagree, and the disagreement is the answer.
    // Written up in design-docs/boot-sequence.md, "#4616".
    const bootWindowArmedAtMs = performance.now();
    const bootWindowArmedAtWallMs = Date.now();
    const bootWindowVisibility = new BootWindowVisibility(bootWindowArmedAtMs, document.hidden);
    // its own subscription, deliberately not the web-boot-marker listener further up:
    // that one lives inside an `isCapacitorNative()` guard, and native boots hit this
    // failsafe too. MyApp is the never-destroyed root, so it never needs removing.
    document.addEventListener('visibilitychange', () => {
      bootWindowVisibility.onVisibilityChanged(document.hidden, performance.now());
    });
    setBootReadPriorityActive(true, () => {
      const firedAtMs = performance.now();
      this.logging.trackSilentProblem('BootReadPriorityFailsafeFired', {
        timeout_ms: BOOT_READ_PRIORITY_FAILSAFE_MS,
        boot_stage: this.bootStage,
        monotonic_elapsed_ms: Math.round(firedAtMs - bootWindowArmedAtMs),
        hidden_at_fire: document.hidden,
        hidden_ms: Math.round(bootWindowVisibility.hiddenMsSoFar(firedAtMs)),
        wall_elapsed_ms: Math.round(Date.now() - bootWindowArmedAtWallMs),
        // the browser's own setTimeout stringifies to "[native code]"; a JavaScript
        // replacement does not. Nothing in this app wraps it (@sentry/angular's
        // defaults omit BrowserApiErrors, and zone.js is not loaded), so false means
        // a page-level script replaced it, and true puts the cap below the page. A
        // Sentry wrapper would not show up here at all - see app-sentry.ts.
        timer_native: /\[native code\]/.test(String(setTimeout)),
      });
    });
    console.log('Booting page setting...');
    hideIndexHtmlSplash(); // after we are sure the booting div is visible, hide the original placeholder

    // if warm resume flag set: show spinner instead of progress bar during Phase A
    // (loading screen stays visible until Phase A completes and local data is loaded)
    if (AuthService.getWarmResumeUid()) {
      console.log('App: Warm resume detected — using spinner for fast Phase A');
      this.loadingProgressType = 'spinner';
    }

    console.log('Booting page shown.');
    this.setPageTitle(LL.app.starting());
    this.installHttpErrorHandler();
    await this.preSettingsAsync();
    await this.settings.loadSettingsAsync();
    void this.locationService.initializeAsync(); // location service is started in background, but must be after the settings!
    console.log('loaded role: ', this.settings.cachedRole);

    // S4 (#3986): start the two native plugin handshakes NOW so they overlap
    // the Firebase auth init below, instead of running serially after it.
    // SocialLogin readiness is awaited at the first social sign-in tap
    // (whenSocialLoginReadyAsync, consumed by auth.service.ts), so its promise is
    // not awaited here at all. initializeSocialLoginAsync never rejects (it
    // surfaces failures itself); the launch-url catch here surfaces its own.
    //
    // The launch URL is REQUESTED FIRST, ahead of the social-login handshake,
    // because Capacitor runs plugin calls on one serial queue on both phone
    // platforms — asking second would put this read behind whatever the
    // social-login setup does natively, and the boot waits for this one.
    if (isCapacitorNative()) {
      this.launchUrlPromise = CapApp.getLaunchUrl().catch((err: any) => {
        // getLaunchUrl failing is unexpected (it only reads the cold-start
        // intent) — surface it so a broken cold-start deep-link path stays
        // visible, and let boot continue without a launch URL
        this.logging.trackSilentProblem('GetLaunchUrlFailed', {
          code: err?.code,
          message: err?.message ?? String(err),
        });
        return undefined;
      });
      this.socialLoginReady = this.initializeSocialLoginAsync();
      await this.adoptLaunchUrlBootParamsAsync();
    }

    bootTimings.start(BootPhase.Auth);
    await this.auth.initAsync();
    bootTimings.end(BootPhase.Auth);

    // #3987 A5: arm the foreground sync-health tick chain once per boot. The
    // call is self-guarding, so a re-entrant boot path cannot spawn parallel
    // chains; the tick itself no-ops until the app is visible + online + signed in.
    this.connectivityResume.startSyncHealthTick();

    // native deep link listener + cold-start launch URL for Capacitor
    if (isCapacitorNative()) {
      void CapApp.addListener('appUrlOpen', (event) => {
        // host and path only: since #4688 an incoming deep link is routinely the
        // community sign-in return, whose query IS a forum login for this person
        // (#4525), and every console line here becomes a Sentry breadcrumb.
        // #4942 milestone, promoted for the reason at APP STARTING. safeUrlForLog
        // strips everything but host and path, so no token can reach the boot log.
        console.info('App: Deep link received:', safeUrlForLog(event.url));
        void this.deepLinkSignIn.handleDeepLinkAsync(new URL(event.url));
      });

      // handle deep link from cold start (app was not running when Universal Link was tapped).
      // the fetch was kicked off before the auth init above (S4), so it has usually
      // answered by now.
      //
      // df-febb6fcfb9e9: the boot does NOT await it here. On some Android boots the
      // plugin never answers, and an await here sat in front of the userAuth
      // subscription below, the only thing that routes a signed-in user — they stayed
      // on the loading screen for good. The link is handed over from a .then instead.
      // An answer already in hand runs that callback at the boot's next await, which
      // is still before the subscription, so a timely link is handed over before the
      // user is routed, as it always was. A late answer is handled when it arrives,
      // the way the appUrlOpen listener above handles a link at runtime.
      // #4994: this goes through handleLaunchUrlOnceAsync, which acts on the launch
      // link at most once per web-view session. Handling it on every boot reloaded
      // the app about once a second, for ever. The listener above deliberately stays
      // on handleDeepLinkAsync. See design-docs/purchase-deep-linking.md, "The launch link is acted on ONCE per web-view session".
      //
      // A link carrying boot parameters was already dealt with before the auth
      // init, by adoptLaunchUrlBootParamsAsync — the mark it wrote makes the call
      // below a no-op. What still arrives here is every OTHER kind of launch link
      // (a page to open, a community sign-in, a path for the browser), which needs
      // the auth init to have finished first.
      void this.launchUrlPromise.then(launchUrl => {
        if (!launchUrl?.url) return;
        try {
          // host and path only, for the same reason as the listener above (#4525).
          // #4942 milestone, promoted for the reason at APP STARTING. This is the line
          // that names the #4994 reboot loop: a launch link acted on once per page, for
          // ever. Redacted by safeUrlForLog, as the listener above is.
          console.info('App: Launch URL received:', safeUrlForLog(launchUrl.url));
          void this.deepLinkSignIn.handleLaunchUrlOnceAsync(new URL(launchUrl.url));
        } catch (err) {
          // an unparseable launch URL. The error's own message is NOT reported: it can
          // quote the address, whose query may carry a sign-in token (see
          // AdoptLaunchUrlBootParamsFailed above)
          this.logging.trackSilentProblem('LaunchUrlHandoffFailed', {
            error_name: (err as any)?.name ?? 'unknown',
          });
        }
      });

      // start native network path monitor for diagnostic logging (Sentry breadcrumbs)
      void this.bootDiagnostics.startNetworkLoggerAsync();

      // set up local notification deep link listener
      this.notificationService.setupDeepLinkListener(this.navCtrl, this.telemetry);

      // set up server push notification tap listener
      const coachManageFromPush = (clientUid: string, page?: string) =>
        this.coachManageDeepLink.processCoachManageAsync({ clientUid, page });
      this.notificationService.setupPushListener(this.navCtrl, this.userDb, this.telemetry, coachManageFromPush);
    }

    // Process sign-in token during boot to prevent deadlock with suppressNullAuth.
    // Without this, suppressNullAuth blocks the null auth emission that would show
    // the login page, but the token is only processed in the login page — deadlock.
    const signInToken = this.externalFlow.getSignInToken();
    const claimToken = this.externalFlow.getClaimToken();
    if (signInToken) {
      await this.deepLinkSignIn.processSignInTokenAsync(signInToken, claimToken);
      this.externalFlow.consumeSignInToken();
    } else if (claimToken && !this.firebase.fbAuth.currentUser) {
      // if only claimToken (no signInToken) and not signed in — exchange it
      await this.deepLinkSignIn.exchangeClaimTokenAndSignInAsync(claimToken, 'link-claim-only');
      this.externalFlow.consumeSignInToken();
    }

    // on first native Android launch, check for install referrer claim token
    if (isCapacitorNative() && !this.firebase.fbAuth.currentUser && !localStorage.getItem('installReferrerChecked')) {
      localStorage.setItem('installReferrerChecked', '1');
      try {
        const { getInstallReferrerClaimTokenAsync } = await import('services/install-referrer.service');
        // df-febb6fcfb9e9: bounded, like the launch link above. A referrer service that
        // never answers must not keep the person off the login page. The flag above is
        // already set, so this check never runs again: a late answer is still acted on,
        // but only while nobody has signed in by other means in the meantime.
        const referrerPromise = getInstallReferrerClaimTokenAsync();
        const answer = await awaitBoundedLive(referrerPromise, INSTALL_REFERRER_WAIT_LIMIT_MS);
        if (answer.timedOut) {
          this.logging.trackSilentProblem('InstallReferrerWaitHitItsLimit', {
            limit_ms: INSTALL_REFERRER_WAIT_LIMIT_MS,
          });
          void this.handleLateInstallReferrerAsync(referrerPromise);
        } else if (answer.value) {
          console.log('App: Install referrer claim token found');
          await this.deepLinkSignIn.exchangeClaimTokenAndSignInAsync(answer.value, 'install-referrer');
        }
      } catch (err) {
        // the lazy import failing, the referrer plugin failing (getInstallReferrerClaimTokenAsync
        // lets its error through to here on purpose), or the exchange throwing something it does
        // not handle itself. The check runs once per install, so this cannot repeat
        this.logging.trackSilentProblem('InstallReferrerCheckFailed', errorReportFields(err));
      }
    }

    this.bootDiagnostics.reportSwRegistrationError();

    await this.preLoginAsync();

    // start monitoring login state
    this.auth.userAuth.subscribe({
      next: user => this.logonChangedAsync(user),
      error: error => this.logonError(error)
    });

    // handle deferred account switch prompt (UI is now ready)
    const pendingSwitch = this.externalFlow.consumePendingAccountSwitch();
    if (pendingSwitch) {
      void this.deepLinkSignIn.handlePendingAccountSwitchAsync(pendingSwitch);
    }

    this.checkDarkMode();

    // Set manifest for everything other than iOS non standalone
    if ((isAppStandalone() || !isPlatformIOS()) || location.hostname.startsWith('local')) {
      const manifestLink = document.getElementById('app-manifest') as HTMLLinkElement;
      if (manifestLink) {
        manifestLink.setAttribute('href', "manifest.json");
      }
    }
  }

  // Step 22 (#4027): runs one web-boot-marker write, absorbing localStorage
  // failures. The write path fires on every stage transition, so a broken
  // localStorage (Safari private mode, full quota) reports ONE
  // WebBootMarkerWriteFailed silent problem and then stays quiet — the boot
  // itself must never break over its own post-mortem bookkeeping.
  private guardedWebBootMarkerWrite(write: () => void): void {
    try {
      write();
    } catch (err: any) {
      if (this.webBootMarkerWriteFailureReported) return;
      this.webBootMarkerWriteFailureReported = true;
      this.logging.trackSilentProblem('WebBootMarkerWriteFailed', {
        name: err?.name,
        message: err?.message,
      });
    }
  }

  // #6181: asks which pages are still booting at the moment the previous
  // marker was read — by Ready the tab that wrote it may have finished and
  // dropped its lock, and its mid-boot marker would then look like a dead
  // boot. Then takes this page's own lock, so another tab starting up meanwhile
  // can see that this boot is alive. A refused query or lock is reported once,
  // and the report then falls back to its pre-#6181 classification.
  // The answer is also given a time limit: the previous-boot report waits on
  // it, so a query that never settled would lose that report silently.
  private startWebBootLockTracking(): void {
    let limitTimer: ReturnType<typeof setTimeout> | undefined;
    const answered = queryHeldWebBootPageIdsAsync().catch((err: any) => {
      this.reportWebBootLockFailure('query', err);
      return null;
    }).finally(() => clearTimeout(limitTimer));
    // raw setTimeout, not safeTimeout: MyApp is the never-destroyed root shell
    const timedOut = new Promise<null>(resolve => {
      limitTimer = setTimeout(() => {
        this.reportWebBootLockFailure('query-timeout', null);
        resolve(null);
      }, MyApp.WEB_BOOT_LOCK_QUERY_LIMIT_MS);
    });
    this.liveBootPageIdsAtCapture = Promise.race([answered, timedOut]);
    // if this page already holds its lock (a re-run boot), keep it
    if (this.releaseWebBootLock) return;
    try {
      this.releaseWebBootLock = acquireWebBootLock(err => this.reportWebBootLockFailure('request', err));
    } catch (lockErr: any) {
      this.reportWebBootLockFailure('request', lockErr);
    }
  }

  // #6266: listens, from the capture until the report, for another tab's write
  // proving the captured marker was a stale copy. Evidence for the report only
  private watchForStaleWebBootMarkerRead(readAtIso: string): void {
    if (!this.previousWebBootMarkerRaw) return;
    const capturedRaw = this.previousWebBootMarkerRaw;
    this.webBootStaleReadListener = (event: StorageEvent) => {
      if (isStaleWebBootMarkerRead(capturedRaw, readAtIso, event)) this.webBootMarkerStaleReadSeen = true;
    };
    window.addEventListener('storage', this.webBootStaleReadListener);
  }

  // #6181: the browser refused the boot lock or the question about who holds
  // one. Reported once per page, like a failing marker write: the boot goes
  // on, and the previous-boot report falls back to its pre-#6181 rule, which
  // may report another tab's running boot as dead.
  private reportWebBootLockFailure(operation: 'request' | 'query' | 'query-timeout', err: any): void {
    if (this.webBootLockFailureReported) return;
    this.webBootLockFailureReported = true;
    this.logging.trackSilentProblem('WebBootLockFailed', {
      operation,
      name: err?.name,
      message: err?.message,
    });
  }

  // Step 22 (#4027): the web counterpart of the native freeze report, which
  // lives in boot-diagnostics-reporters.service.ts and is called from
  // markAppLoadingAsync below. Once this boot reaches Ready, classify
  // the PREVIOUS boot's marker (captured at boot start) and report a
  // foreground mid-boot death as WebBootIncomplete, or a backgrounded one as
  // the low-signal WebBootAbandonedBackground.
  // Runs at most once per boot window (Ready can be assigned on both the cold
  // and warm paths, and warm-failure fallbacks can traverse both).
  private reportPreviousWebBootIncompleteIfAny(): void {
    if (isCapacitorNative()) return;
    if (this.webBootMarkerReported) return;
    this.webBootMarkerReported = true;
    // the lock answer is a promise taken at capture time (#6181), so the
    // classification runs once it settles. a failure here must not vanish
    this.reportPreviousWebBootMarkerAsync().catch((err: any) => {
      this.logging.trackSilentProblem('WebBootMarkerReportFailed', { name: err?.name, message: err?.message });
    });
  }

  // classifies the previous boot's marker against the pages that were still
  // booting when it was read, and reports it (see the method above)
  private async reportPreviousWebBootMarkerAsync(): Promise<void> {
    const liveBootPageIds = await this.liveBootPageIdsAtCapture;
    if (this.webBootStaleReadListener) window.removeEventListener('storage', this.webBootStaleReadListener);
    this.webBootStaleReadListener = null;
    const decision = evaluateWebBootMarker(this.previousWebBootMarkerRaw, BootStage.Ready, liveBootPageIds);
    if (decision.category === 'other-tab-booting') {
      // #6181: another tab's page was alive when this one read the marker —
      // still booting, or finished within the lock's grace period (#6266).
      // nobody died. counted so the overlap rate stays visible
      this.logging.trackSilentInfo('WebBootMarkerOtherTabBooting', {
        last_stage: decision.lastStage,
        last_ts: decision.lastTs,
        stale_read_seen: this.webBootMarkerStaleReadSeen,
      });
    } else if (decision.report) {
      this.logging.trackSilentProblem('WebBootIncomplete', {
        last_stage: decision.lastStage,
        last_ts: decision.lastTs,
        category: decision.category,
        // #6266: true when the marker read was proven a stale copy
        stale_read_seen: this.webBootMarkerStaleReadSeen,
        // Step 23: present when the previous boot died in bootstrapModule
        // itself — the rejection text recorded by recordBootstrapFailure
        bootstrap_error: decision.bootstrapError,
      });
    } else if (decision.downgrade) {
      this.logging.trackSilentProblem('WebBootAbandonedBackground', {
        last_stage: decision.lastStage,
        last_ts: decision.lastTs,
        category: decision.category,
      });
    }
  }

  private firstInit = true;

  async markAppLoadingAsync(loading: boolean) {
    if (!loading) {
      // the spinner goes up now; the L8 watchdog is armed only when the restore itself starts, below
      this.navRestoring = true;
      this.loadingProgressType = 'spinner';
    }
    this.appLoading = loading;
    this.cd.markForCheck();

    // #3493 Phase B-2: drive the stuck-detection lifecycle alongside the splash.
    // Always cancel any in-flight tick first so rapid true→false→true cycles
    // can't accumulate parallel timer chains.
    if (this.splashStuckCheckHandle !== null) {
      clearTimeout(this.splashStuckCheckHandle);
      this.splashStuckCheckHandle = null;
    }
    if (loading) {
      this.armSplashWatchdog();
    } else {
      this.splashStuck = false;
      // #4401: forget how many previous boots had to be reloaded, and how many times the
      // app restarted its own process. Both counts mean "since the app last worked", and
      // left to accumulate across weeks of healthy use they would eventually refuse a
      // rescue to somebody whose problem is brand new.
      //
      // The `fatalErrorParams` check is what makes "this boot worked" true rather than
      // merely "the splash came down". `LoggingService.fatalError` dismisses the splash in
      // order to SHOW an error page, and it sets those params first — so without this
      // check, arriving at the storage-failure screen would itself clear the very budgets
      // that sent the user there, and pressing Retry would start the whole reload cycle
      // over. That applies to every fatal-error type, not just this ticket's.
      if (!this.fatalErrorParams) {
        clearWedgedBootCount(browserLocalStorageOrNull());
        void clearProcessRestartCountAsync();
      }
    }
    if (!loading) {

      if (location.pathname === '/community') {
        // df-f938864e16d1: the community detour is optional and runs before the watchdog is armed, so
        // if it throws, report it and carry on to the restore — rethrowing here would skip the restore
        // and leave the spinner up with nothing left to take it down
        try {
          await this.openCommunityPage();
        } catch (err) {
          this.logging.trackAbnormalException(err);
        }
      }

      console.log('App: Hiding loading screen.');
      bootTimings.start(BootPhase.NavInit);
      // This is needed otherwise it is possible that next page will not be attached to nav.
      // (it never throws — after its 10 s budget it reports NavNeverReady and returns undefined)
      await this.waitNavReadyAsync();

      // suppress page transition animations during navigation stack restoration
      const navEl = this.nav?.getElementRef()?.nativeElement as HTMLElement;
      navEl?.classList.add('restoring');

      // L8 (#3986), df-f938864e16d1: arm the watchdog only now, as the restore starts. The awaits above
      // are user-paced (the community intro alert) or bounded on their own, and a watchdog armed before
      // them could spend its whole budget there — reporting a restore that had not started as stuck,
      // and leaving the restore that followed with no watchdog at all.
      this.armNavRestoreWatchdog();
      // df-d80a99934799: the rescue signal, created after the arm (arming drops any earlier one), so a
      // rescue can only end the wait on the restore that is actually running.
      const navRestoreRecovery = DeferredPromise.defer<void>();
      this.navRestoreRecovery = navRestoreRecovery;
      let navRestoreOutcome: NavRestoreOutcome;
      try {
        // initialize the entire navigation system in one place. df-d80a99934799: raced against the
        // watchdog's rescue — a wedged restore never settles, and a plain await here left the rest
        // of the boot (booting_time, the Ready stage, the paid-user background tasks) unrun.
        navRestoreOutcome = await awaitNavRestoreOrRecovery(
          this.navCtrl.initializeNavigationSystem(this.overlayStack),
          navRestoreRecovery,
          err => this.logging.trackSilentProblem('NavRestoreFailedAfterRecovery', errorReportFields(err)),
        );
      } finally {
        // after a rescue, recoverStuckNavRestore already did the first three of these — each is
        // safe to repeat, and doing them again keeps a completed restore and a rescued one on one path
        navEl?.classList.remove('restoring');
        this.navRestoring = false;
        this.clearNavRestoreWatchdog(); // L8 (#3986): restore completed or rescued
        this.cd.markForCheck();
      }
      const isNavRestoreRecovered = navRestoreOutcome === NavRestoreOutcome.Recovered;
      bootTimings.end(BootPhase.NavInit);

      const bootingTime = bootTimings.elapsedSinceWindowStartMs();
      // #4615: which of the several things this event can describe it IS. One app
      // launch emits one of these for the login page and another for the boot that
      // follows a sign-in, and they used to be indistinguishable.
      const bootKind = bootTimings.nextEmissionKind({
        signedIn: this.auth.user != null,
        warmResume: this.isWarmResume,
      });
      // #4942 milestone, promoted for the reason at APP STARTING: says the boot
      // FINISHED, which is what separates a slow boot from one that never completes.
      console.info(`App: Ready in ${(bootingTime / 1000).toFixed(2)} s (${bootKind}${isNavRestoreRecovered ? ', nav restore recovered' : ''})`);
      this.telemetry.trackEvent('booting_time', buildBootingTimeProperties({
        bootingTimeMs: bootingTime,
        bootKind,
        warmResume: this.isWarmResume,
        // identity comes from auth.user (NOT a `user` param like the warm emit):
        // markAppLoadingAsync has no user in scope and also completes logged-out
        // boots, where a leftover hint must classify as expired
        uid: this.auth.user?.uid,
        bootStartSessionHint: this.bootStartSessionHint,
        settingsForTelemetry: this.settings.getSettingsForTelemetry(),
        pageUrl: window.location.href,
        navRestoreRecovered: isNavRestoreRecovered,
      }));
      bootTimings.noteEmission(bootKind);
      this.auth.whenAppReady.resolve(true);

      // tell the native watchdog this boot finished, and report the previous one if it never did
      this.reportPreviousFreezeOnce();

      // report if this non-English build shipped any untranslated UI strings
      this.bootDiagnostics.reportI18nUntranslatedStringsIfAny();

      // if a snapshot was just restored, show confirmation
      const restoredSnapshot = sessionStorage.getItem('snapshot_restored');
      if (restoredSnapshot) {
        sessionStorage.removeItem('snapshot_restored');
        void this.alertCtrl.showAlertAsync({
          title: LL.dbSnapshots.restoreConfirmTitle(),
          message: LL.dbSnapshots.restoreComplete({ name: restoredSnapshot }),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
        });
      }

      // if a JSON restore was just performed, show confirmation
      const jsonRestoredCount = sessionStorage.getItem('json_restored');
      if (jsonRestoredCount) {
        sessionStorage.removeItem('json_restored');
        void this.alertCtrl.showAlertAsync({
          title: LL.dbRestoreJson.restoreCompleteTitle(),
          message: LL.dbRestoreJson.restoreCompleteMessage({ count: Number(jsonRestoredCount) }),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
        });
      }

      if (this.firstInit) {
        this.firstInit = false;
      }
    } else {
      console.log('App: Showing loading screen.');
      this.setPageTitle(LL.app.loading());
      console.log(`App: Unready`);
    }
  }

  // arm the splash watchdog for a new splash window: reset the per-window
  // fields (timers, one-shot gates, boot stage) and start the recursive
  // check timer (#3493 + #3698)
  private armSplashWatchdog() {
    this.splashWatchdogTimes.arm();
    this.splashStuck = false;
    this.splashHangEmitted = false;
    this.bootStage = BootStage.PreLogin;
    this.scheduleSplashStuckCheck();
  }

  // defensive arm for boot paths that show the splash WITHOUT going through
  // markAppLoadingAsync(true): the persisted-session cold boot (appLoading
  // comes from the field initializer) and the warm-resume failure fallback
  // (#3698). no-op when the splash is hidden or the watchdog already runs.
  private armSplashWatchdogIfNeeded() {
    if (!this.appLoading || this.splashStuckCheckHandle !== null) {
      return;
    }
    this.armSplashWatchdog();
  }

  // #3493 Phase B-2 + #3698: recursive tick. The decision logic lives in the
  // pure module splash-watchdog.ts (unit-tested under node): stuck when the
  // couch login already gave up, when no DatabaseSyncProgressEvent arrived
  // for 25s, OR (#3698) when the splash has been visible for over 3 minutes
  // in total — that last case catches boots that wedge while background
  // replication keeps emitting progress events. While stuck and online, the
  // watchdog also auto-kicks the sync engine (rate-limited) through the same
  // ensureSyncsActive path that app resume uses. Handle is stored in
  // splashStuckCheckHandle so markAppLoadingAsync can cancel it on lifecycle
  // transitions instead of relying on an active-flag guard.
  private scheduleSplashStuckCheck() {
    this.splashStuckCheckHandle = setTimeout(() => {
      this.splashStuckCheckHandle = null;
      const elapsed = this.splashWatchdogTimes.elapsed();
      const { noProgressMs, splashUpMs } = elapsed;
      const decision = evaluateSplashWatchdog({
        appLoading: this.appLoading,
        couchLoginRetryNeeded: this.auth.couchLoginRetryNeeded,
        connectionOnline: this.onlineStateService.connectionState === ConnectionState.Online,
        noProgressMs,
        splashUpMs,
        msSinceLastAutoKick: elapsed.msSinceLastAutoKick,
        hangEmitted: this.splashHangEmitted,
        // O8 (#3986): reload-escalation inputs. msSinceFirstAutoKick measures how long the
        // sync-kick lever has been failing; the sessionStorage guard makes the reload one-shot.
        msSinceFirstAutoKick: elapsed.msSinceFirstAutoKick,
        reloadAlreadyTried: sessionStorage.getItem(MyApp.BOOT_RELOAD_RECOVERY_KEY) != null,
        // #3803: while the boot is parked at the consent / ToS dialog, the
        // wait is on the user, not the system — don't let the watchdog flag
        // it as a stuck boot. Key off the bootStage, NOT the raw
        // licenseDialogSubject: the subject flips true at login (before the
        // user-DB initial sync at ColdUserDbInit), so gating on it would also
        // suppress a genuine initial-sync stall for fresh sign-ins. The
        // ColdLicenseDialog stage is set only around the dialog-dismissal
        // await (sync already complete), which is exactly what the reported
        // stuck events show.
        awaitingUserConsent: this.bootStage === BootStage.ColdLicenseDialog,
        // F24 (#3991): this boot is deliberately held on the offline first-load splash, so it is
        // blocked rather than stuck — do not report a hang that is not happening.
        // #6248 holds the splash the same deliberate way, so it is not a hang either
        heldOfflineFirstLoad: this.offlineFirstLoadBlocked || this.accountLoadBlocked,
        // #5449 removed a `copyingLocalStore` input that used to sit here. It switched
        // the watchdog off for the whole SQLite-migration boot stage, because the copy
        // ran on this screen and took about five minutes. The copy now runs in the
        // background while the app is usable, so that stage is a delta of tens of
        // documents and the watchdog watches it like any other.
        // #4401: how many boots have already ended in a watchdog reload on this device.
        // Kept in localStorage, which is a different storage system from the one that
        // jams, so it survives the reload the sessionStorage guard cannot see past.
        wedgedBootCount: readWedgedBootCount(browserLocalStorageOrNull()),
      });

      if (decision.stuck !== this.splashStuck) {
        this.splashStuck = decision.stuck;
        this.cd.markForCheck();
      }

      // one-shot per splash window. splashHangEmitted gates re-emission while
      // the threshold stays crossed; it's reset whenever a new splash window
      // is armed (armSplashWatchdog — via markAppLoadingAsync(true) or the
      // defensive armSplashWatchdogIfNeeded paths).
      // (no extra appLoading check — evaluateSplashWatchdog returns all-false
      // when the splash is not visible, and nothing awaits in between)
      if (decision.emitHang) {
        this.splashHangEmitted = true;
        // cold_start derived from the durable WarmResumeStorage signal
        // rather than this.isWarmResume — the latter is set inside
        // postLoginAsync which runs after markAppLoadingAsync(true), so a
        // hang detected before that assignment would misreport cold_start.
        const hadWarmResume = AuthService.getWarmResumeUid() != null;
        const hangContext = {
          no_progress_for_ms: Math.round(noProgressMs),
          splash_up_ms: Math.round(splashUpMs),
          boot_stage: this.bootStage,
          // #4981 phase 4 (#5270): WHICH ENGINE this device's local data is in. Every
          // stuck-boot report the whole project family was built on named a local database
          // operation that never answered — and until now none of them said which store
          // that operation was issued against. Without it a ramp cannot tell "the SQLite
          // devices are wedging" from "devices are wedging, and some of them happen to be
          // on SQLite", which is the one question the rollout has to be able to answer.
          //
          // **The NON-latching read, because this watchdog fires at any point in the boot
          // — including at `PreLogin`, before the migration and rollback hooks have run.**
          // An earlier version used the latching accessor and claimed the latch was already
          // taken by here; that is false, and it would have taken the latch itself on
          // precisely the wedged-boot devices this project exists for, disabling their
          // migration for ever. See `peekLocalDatabaseEngineWithoutLatching`.
          local_database_engine: peekLocalDatabaseEngineWithoutLatching(),
          cold_start: !hadWarmResume,
          has_couch_session: this.auth.couch != null,
          had_warm_resume: hadWarmResume,
          couch_login_retry_needed: this.auth.couchLoginRetryNeeded,
          // O1 (#3986): durations of every boot phase that COMPLETED before the
          // hang — together with boot_stage (the phase we are stuck in) this
          // tells where a wedged boot spent its time. See design-docs/telemetry.md
          ...bootTimings.snapshot(),
          // #4401: the step still OPEN right now (snapshot() above lists only
          // the ones that FINISHED), plus which LOCAL database operation was
          // issued and never came back. See design-docs/boot-sequence.md,
          // "Naming the step that is wedged (#4401)".
          boot_step: bootTimings.currentPhase() ?? null,
          ...localOpTracker.snapshot(),
        };
        this.telemetry.trackEvent('splash_hang_detected', hangContext);
        // #3698: also surface to Sentry with per-database sync state — a
        // stuck boot previously produced no Sentry signal at all, which made
        // these stalls undiagnosable in prod
        this.logging.trackSilentProblem('SplashStuckOnBoot', {
          ...hangContext,
          databases: this.dbProvider.allDatabases.map(db => db.getSyncDiagnostics()),
          // #6093: the time since the splash came up that the phone kept the app frozen.
          // The two durations above leave it out, so this is where a reader sees a freeze
          // happened. Sentry only: the Mixpanel event's properties are unchanged.
          not_running_ms: Math.round(elapsed.notRunningMs),
        });
        // #4401: ask the browser's storage whether it answers AT ALL, and report that
        // separately. It cannot ride along in the payload above because it takes time to
        // find out, and this tick must stay synchronous. The answer says whether only the
        // app's own database is jammed — in which case a store opened under a different
        // name would work — or the whole origin's storage is, in which case nothing the
        // page can do will help. Nothing before this could tell those two apart.
        void this.reportLocalStoreProbeAsync();
      }

      // #3698: self-recovery — restart dead/stranded syncs while the user is
      // stuck on the splash. ensureSyncActive refuses to interfere with a
      // genuinely-running initial sync, and replication restarts resume from
      // the last checkpoint, so the kick is safe and cheap.
      if (decision.autoKick) {
        // O8 (#3986): this also remembers when the FIRST kick fired, so the watchdog can measure
        // how long the sync-kick lever has been failing before escalating to a reload.
        this.splashWatchdogTimes.recordAutoKick();
        console.log(`App: splash watchdog kicking sync engine (bootStage=${this.bootStage}, noProgressMs=${Math.round(noProgressMs)}, splashUpMs=${Math.round(splashUpMs)})`);
        this.dbProvider.ensureSyncsActive();
      }

      // O8 (#3986): the sync-kick lever has failed for the escalation window — escalate to ONE
      // guarded full reload. The sessionStorage guard (set here, survives location.reload but
      // not a new session) makes this un-loopable. This is terminal on success: reloadAppAsync
      // never returns, so we do not reschedule the check here. If the reload itself FAILS,
      // performBootReloadRecovery re-arms the check loop from its catch, so the sync-kick lever
      // keeps running instead of leaving the splash with no recovery lever at all.
      // #4401: reloading has been tried and has not helped. Another reload is exactly what
      // the reporter described as the app "flashing white rapidly", so stop and tell the
      // user what is actually wrong. This is terminal for the watchdog: the fatal-error
      // page replaces the splash, so there is nothing left to watch.
      if (decision.surfaceStorageFailure) {
        void this.surfaceLocalStorageFailure(noProgressMs, splashUpMs);
        return;
      }

      if (decision.autoReload) {
        void this.performBootReloadRecovery(noProgressMs, splashUpMs);
        return;
      }

      if (this.appLoading) {
        this.scheduleSplashStuckCheck();
      }
    }, SPLASH_STUCK_TICK_MS);
  }

  // L4 (#3986): when the splash is stuck because the device is offline, the Retry tap is a
  // no-op (a login retry can't reach the server), so the stuck copy tells the user to
  // reconnect first. Read live so the message flips as connectivity changes.
  get isSplashLikelyOffline(): boolean {
    return !this.onlineStateService.isLikelyOnline();
  }

  // #3493 Phase B-2 + #3698: user-initiated retry from the splash. Retries
  // the couch login (de-duped against the focus/online auto-retry via
  // couchLoginRetryInFlight; no-op if not pending) AND restarts the sync
  // engine — a stalled initial replication is the more common reason the
  // splash is stuck, and a login-only retry could not recover it.
  onSplashRetry() {
    // #6248: on the held account-load splash, Retry reloads the app, which reads the documents
    // again. Checked before F24 below: when both holds could apply, this gate ran first.
    if (this.accountLoadBlocked) {
      this.reloadHeldAccountLoad?.();
      return;
    }
    // F24 (#3991): on the held offline-first-load splash there is nothing to retry a login
    // against — we are holding precisely BECAUSE we are offline. What the user needs is a fresh
    // connectivity probe: the device may have rejoined a network without the WebView firing an
    // `online` event. A probe that succeeds publishes the Online state, and the subscription in
    // gateNeverSyncedOfflineBoot() reloads the app into a normal initial sync.
    //
    // verifyOnlineStatus, NOT isLikelyOnlineOrRecheckAsync: that helper trusts any probe newer than
    // RECENT_PROBE_TRUST_MS (30s) and answers "offline" from it WITHOUT probing. That is right for an
    // automatic refusal — it stops a genuinely offline device hanging for the probe timeout on every
    // tap — but it is exactly wrong for a button whose entire meaning is "check again NOW". The boot
    // probe that produced the state we are holding on is itself less than 30s old, so routing Retry
    // through the trust window made the button a silent no-op during precisely the window in which a
    // user reads "connect to the internet", turns wifi on, and taps it. A user-initiated check must
    // always actually check.
    if (this.offlineFirstLoadBlocked) {
      // the catch is not decoration. verifyOnlineStatus can reject (a subscriber throwing during the
      // publish, per its own finally-block comment), and this is fire-and-forget from a click handler
      // — so without it the rejection is unowned, nothing reaches Sentry, and the user is left staring
      // at "Checking your connection..." forever. That is exactly the never-resolving spinner this
      // method exists to prevent, so it must not be able to arrive through the error path.
      void this.checkConnectionFromHeldSplashAsync().catch(err => {
        this.logging.trackAbnormalException(err);
        if (this.appDestroyed) return;
        this.offlineFirstLoadChecking = false;
        this.firstSyncMessage = LL.app.offlineFirstLoadStillOffline();
        this.cd.markForCheck();
      });
      return;
    }

    // pass manual=true so a Retry that still fails while online reports
    // CouchLoginStuckOffline immediately (Sentry triage v1.25.0 Group 3).
    // L4 (#3986): manual=true is also the `force` flag — retryCouchLoginAsync now proceeds
    // even when couchLoginRetryNeeded was never armed (pre-deferral hang) and falls through
    // to a fresh login when the user hasn't been emitted yet (pre-login hang).
    void this.auth.retryCouchLoginAsync(true);
    this.dbProvider.ensureSyncsActive();
  }

  /** F24 (#3991): the Retry button on the held offline-first-load splash.
   *
   *  The probe can take up to REACHABILITY_PROBE_TIMEOUT_MS (10s) on a network that swallows requests
   *  rather than refusing them. Without any feedback the button LOOKS broken for that whole stretch,
   *  and then — if we are still offline — nothing on screen changes at all, so the user cannot tell
   *  their tap did anything. On a change whose entire purpose is to stop the app lying about
   *  connectivity, a button that silently does nothing is the same lie in a smaller font. So the
   *  button shows a checking state while the probe runs, and says so plainly when we are still offline.
   *
   *  A success needs no message: the probe publishes Online, and the subscription in
   *  gateNeverSyncedOfflineBoot() reloads the app out from under this screen. */
  private async checkConnectionFromHeldSplashAsync(): Promise<void> {
    // ignore a second tap while the first probe is still in flight
    if (this.offlineFirstLoadChecking) return;

    this.offlineFirstLoadChecking = true;
    this.firstSyncMessage = LL.app.offlineFirstLoadChecking();
    this.cd.markForCheck();

    try {
      const pingSaidOnline =
        await this.onlineStateService.verifyOnlineStatus(ConnectionState.Online, 'suspected') === ConnectionState.Online;
      if (this.appDestroyed) return;

      // Two different questions, and they can genuinely disagree — so ask both.
      //
      // `pingSaidOnline` normally comes from the reachability ping alone. The PUBLISHED state
      // additionally requires the CouchDB session refresh that setConnectionStateAsync performs on
      // an Offline->Online flip; if that login fails, it demotes the state back to Offline. So a
      // user whose wifi is genuinely working, but whose session could not be renewed, gets
      // pingSaidOnline = true and a published state of Offline.
      //
      // The one case where it is NOT the ping alone: if another connectivity check started after
      // this one and answered first, verifyOnlineStatus drops this check's own answer and hands
      // back the state that newer check published (df-dfa0b79fc17e). Both values below are then
      // the same field and cannot disagree, so we report on the newer check's answer rather than
      // on our own ping. That is the right outcome — the newer check is the better authority —
      // but it means the two questions collapse into one in that window.
      //
      // Only the PUBLISHED state drives the reload this screen is waiting for. But only the PING
      // knows whether the user has a network. Reporting either one alone gets a case wrong:
      //   - trust the ping     -> "Checking your connection..." stays up forever with no reload
      //                           coming. A spinner that never resolves.
      //   - trust the state    -> we tell a user sitting on working wifi "Still offline", which is
      //                           simply untrue, and is precisely the class of lie this change exists
      //                           to remove.
      const publishedOnline = this.onlineStateService.connectionState === ConnectionState.Online;

      if (publishedOnline) {
        // the reload is already on its way (the gate's subscription saw the published Online state);
        // leave the checking message up rather than flashing another one on the way out
        return;
      }

      // The network is back but we could not sign in to the server. Do not claim they are offline —
      // they are not. Say what is actually true. (The auth failure itself already raises its own
      // user-visible error and reaches Sentry from attemptCouchLoginAsync, so this is only about not
      // lying on the splash; Retry stays enabled so they can try again.)
      this.firstSyncMessage = pingSaidOnline
        ? LL.app.offlineFirstLoadServerUnreachable()
        : LL.app.offlineFirstLoadStillOffline();
    } finally {
      if (!this.appDestroyed) {
        this.offlineFirstLoadChecking = false;
        this.cd.markForCheck();
      }
    }
  }

  /**
   * Stop reloading and tell the user their device's storage is not answering (#4401).
   *
   * Reached only after the watchdog has already reloaded as often as it is going to and
   * the boot is stuck again. Everything before this point tries to fix the problem; this
   * is the rung that admits it cannot, which is still far better than the blank screen
   * flashing over and over that a customer sat through for 73 minutes.
   *
   * The probe result decides what the page is allowed to offer: a store that is jammed on
   * its own can be started over under a fresh name, while a whole storage layer that has
   * gone silent cannot be rescued from inside the page at all.
   */
  private async surfaceLocalStorageFailure(noProgressMs: number, splashUpMs: number): Promise<void> {
    const probe = await probeLocalStoreAsync(makeBrowserProbeDeps());
    // the probe opens an IndexedDB database, which is the app's own store only on a
    // device whose engine IS IndexedDB — see probeSpeaksForLocalStore
    const speaksForTheStore = probeSpeaksForLocalStore();
    const scope = scopeOfWedge(probe, speaksForTheStore);

    // On Android, one rung is still untried, and it is the only one that can work there:
    // the WebView keeps its storage machinery inside OUR process, so reloading the page —
    // everything the watchdog has done so far — never resets the jammed part. Restarting
    // the process does, and it costs the user nothing. It happens at most once, counted
    // natively, and nothing after this line runs when it fires.
    // Reported at problem tier, and only when a restart is really about to happen — which
    // is a question the native side has to answer, because it owns the one-restart budget.
    // Asking first is what keeps the report honest: nothing of ours runs after the kill,
    // so it cannot be written afterwards, and writing it unconditionally would claim a
    // restart on exactly the devices where the budget is spent and none is coming.
    // The condition it describes is a wedged store that nothing softer has fixed, which is
    // the kind of thing somebody should eventually look at.
    // `Available` is the only value that means a restart is going to be attempted, so every
    // way of not knowing keeps the report unwritten. That is the right trade HERE and only
    // here: not reporting costs one restart that happens unrecorded, while reporting on a
    // guess claims a restart that never took place.
    // The same answer is also what the give-up report below records (#4980), which is why it
    // is read once, into a value, rather than asked for twice.
    const restartState = await processRestartStateAsync();
    if (restartState === ProcessRestartState.Available) {
      this.logging.trackSilentProblem('LocalStoreProcessRestart', { wedge_scope: scope, ...probe });
    }
    // The attempt itself is NOT gated on that answer, and the difference matters: the state
    // above is not `Available` for every way of not knowing, so gating the restart on it
    // would let an unanswerable question skip the one rung that can actually fix an
    // Android device. The native side owns the budget and refuses safely on its own, and
    // this call never throws — so ask it, always, and let it decide.
    if (await restartProcessForWedgedStoreAsync()) return;

    await this.logging.fatalError({
      type: 'local_storage_wedged',
      error: {
        no_progress_for_ms: Math.round(noProgressMs),
        splash_up_ms: Math.round(splashUpMs),
        boot_stage: this.bootStage,
        wedge_scope: scope,
        // #4980: which rungs had already been spent by the time the app gave up. `spent`
        // here means a fresh process was already tried for this and the boot wedged anyway
        // — the condition #4980's storage wipe would hang off, and until this field there
        // was no way to count it rather than infer it.
        process_restart: restartState,
        // #6788: whether this store had anything to lose, and whether the start followed the
        // purchase reload — what tells the two iPhone buyers' group apart from the others
        ...this.userStoreFactsForReport(),
        ...probe,
      },
      // starting the store over only helps when the storage layer itself still works —
      // offering it otherwise would cost the user their unsynced data for nothing
      canResetLocalStore: scope === 'this-database',
    });
  }

  /**
   * Start this device's local store over under a fresh name, and reload (#4401).
   *
   * Nothing is deleted: a delete waits for every open connection to close and can queue
   * for ever against the very store that is not answering, which is the failure this is
   * trying to escape. A name nothing has ever touched cannot queue behind anything.
   *
   * Everything the server already holds is downloaded again. What is lost is anything
   * this device had not managed to sync yet — which is why this is only ever reached by
   * the user pressing a button that says so.
   */
  async resetLocalStoreAndReloadAsync(): Promise<void> {
    // the COMPLETE list, not `activeDatabases` (#4506) — see userDatabaseForReset below for
    // why. Written out here because a test pins this method's own lookup.
    const userDb = this.dbProvider.allDatabases.find(db => db.internalDatabaseIdentifier === 'user');
    const baseName = userDb?.localDatabaseBaseNameForReset;
    if (!baseName) {
      // there is no user database to reset — nothing to do but reload, which is what the
      // Retry button does anyway. Report it: the button should not have been offered.
      this.logging.trackSilentProblem('LocalStoreResetHadNothingToReset', {});
      await this.reloadAppAsync({ resetToRoot: true });
      return;
    }
    const newName = this.startLocalStoreOver(baseName);
    this.logging.trackSilentInfo('LocalStoreReset', { db: 'user', new_store: newName });
    await this.reloadAppAsync({ resetToRoot: true });
  }

  /**
   * The user database, from the COMPLETE list rather than `activeDatabases` (#4506): a
   * shutdown that HANGS never leaves `activeDatabases`, and a hung store is exactly the case
   * a reset is for — filtering would turn the remedy into a reload into the same wedged
   * store. See design-docs/pass-through-db.md, "Which databases a sweep may act on".
   */
  private userDatabaseForReset(): DatabaseWrapper | undefined {
    return this.dbProvider.allDatabases.find(db => db.internalDatabaseIdentifier === 'user');
  }

  /**
   * The reset button's steps (#4401): move to the next generation and clear the wedged-boot
   * count. Returns the name the app will open from now on, which is the old name when
   * storage refused the move.
   */
  private startLocalStoreOver(baseName: string): string {
    const storage = browserLocalStorageOrNull();
    const newName = bumpLocalStoreGeneration(storage, baseName);
    // the count is about boots that had to be reloaded; a different remedy has just been
    // chosen, so give it a fresh start rather than an immediate error screen
    clearWedgedBootCount(storage);
    return newName;
  }

  /**
   * Switch the user's silent local store to a fresh name WITHOUT asking (#6788).
   *
   * Called by the database recovery only for a store the app may abandon: the user's own,
   * never finished a download here, never confirmed a write, never switched automatically
   * before (`mayAbandonSilentLocalStore`). Records the once-only flag FIRST, and switches
   * only if that stuck, so a storage that refuses it can never turn this into a reload loop.
   *
   * Returns the new name, or undefined when the switch was not made. The caller reloads.
   */
  switchSilentLocalStoreWithoutAsking(): string | undefined {
    const userDb = this.userDatabaseForReset();
    const baseName = userDb?.localDatabaseBaseNameForReset;
    const oldName = userDb?.localDatabaseName;
    if (!baseName || !oldName) return undefined;
    const storage = browserLocalStorageOrNull();
    const newName = switchLocalStoreWithoutAsking(storage, baseName, oldName);
    if (newName === undefined) return undefined;
    // same as the button: a different remedy has just been chosen, so the wedged-boot count
    // starts again rather than sending the next start straight to the error screen
    clearWedgedBootCount(storage);
    // info tier: a repair has run. The silent store itself is already reported as
    // LocalStoreNotAnswering, which is the problem somebody may need to look at.
    this.logging.trackSilentInfo('LocalStoreSwitchedAutomatically', {
      db: 'user',
      new_store: newName,
      after_purchase_reload: pageStartedAfterPurchaseReload(),
    });
    return newName;
  }

  /** The #6788 report fields for the user's store. A storage that throws leaves the two store
   *  facts out rather than guessing them. */
  private userStoreFactsForReport(): Record<string, boolean> {
    const fields: Record<string, boolean> = { after_purchase_reload: pageStartedAfterPurchaseReload() };
    try {
      const facts = this.userDatabaseForReset()?.silentLocalStoreFacts;
      if (facts) {
        fields.has_synced_on_this_device = facts.hasSyncedOnThisDevice;
        fields.store_ever_written = facts.everWritten;
      }
    } catch {
      // not reported on its own: this runs while building a report that is already going out
    }
    return fields;
  }

  /**
   * Wait, at most `maxMs`, for the user database's set-up to finish (#6788). The
   * purchase-first checkout calls this between signing the buyer in and reloading, so the
   * reload does not cut off the brand-new store's first open. See `waitForUserDbInitAsync`.
   */
  whenUserDbInitSettledAsync(maxMs: number): Promise<UserDbInitWaitEnding> {
    return waitForUserDbInitAsync(maxMs, {
      inFlight: () => this.userDbInitInFlight,
      sleepAsync: (ms) => new Promise<void>(resolve => setTimeout(resolve, ms)),
      nowMs: () => DateTime.utc().toMillis(),
    });
  }

  /** The user database's set-up while it runs, settled either way; null before the first
   *  cold start begins it (#6788). */
  private userDbInitInFlight: Promise<void> | null = null;

  /**
   * Ask the browser's storage whether it answers at all, and report what it said (#4401).
   *
   * Fire-and-forget, once per page load: it is a diagnostic, so it must never delay
   * or affect the boot it is measuring. It never throws — `probeLocalStoreAsync` returns
   * every outcome as a value, including "this browser does not have that".
   */
  private async reportLocalStoreProbeAsync(): Promise<void> {
    if (this.splashProbeReported) return;
    this.splashProbeReported = true;
    const result = await probeLocalStoreAsync(makeBrowserProbeDeps());
    const context = {
      ...result,
      // the plain-words reading of the numbers above, so a report can be understood
      // without knowing what each field means
      wedge_scope: scopeOfWedge(result, probeSpeaksForLocalStore()),
      boot_stage: this.bootStage,
    };
    // if the storage layer itself went silent or failed, that is still unresolved: report a
    // problem. otherwise the probe found storage working, which is only an observation —
    // the stuck start is already reported as SplashStuckOnBoot (df-b83a2b04a9c2)
    if (probeFoundAProblem(result)) {
      this.logging.trackSilentProblem('LocalStoreProbe', context);
    } else {
      this.logging.trackSilentInfo('LocalStoreProbe', context);
    }
  }

  // O8 (#3986): last-resort boot recovery — when the splash has been stuck long enough that the
  // sync-kick lever has clearly failed, reload the app once. Guarded by a sessionStorage flag so
  // it can never loop (the flag survives location.reload in the same tab but not a new session).
  // Emits boot_reload_recovery telemetry (Mixpanel + Sentry) so we can see how often this fires
  // and whether it actually recovers boots in prod.
  private async performBootReloadRecovery(noProgressMs: number, splashUpMs: number): Promise<void> {
    // set the one-shot guard BEFORE reloading so the post-reload boot sees it and does not
    // escalate again.
    sessionStorage.setItem(MyApp.BOOT_RELOAD_RECOVERY_KEY, DateTime.utc().toISO());
    // #4401: and count it somewhere that survives the reload, so a device that keeps
    // wedging is eventually told rather than reloaded again
    const wedgedBootCount = recordWedgedBoot(browserLocalStorageOrNull());
    const hadWarmResume = AuthService.getWarmResumeUid() != null;
    const context = {
      no_progress_for_ms: Math.round(noProgressMs),
      splash_up_ms: Math.round(splashUpMs),
      boot_stage: this.bootStage,
      cold_start: !hadWarmResume,
      has_couch_session: this.auth.couch != null,
      wedged_boot_count: wedgedBootCount,
    };
    console.warn('App: splash watchdog escalating to a one-shot reload recovery', context);
    this.telemetry.trackEvent('boot_reload_recovery', context);
    this.logging.trackSilentProblem('BootReloadRecovery', context);
    try {
      // resetToRoot forces a cold start (clears warm-resume) but does NOT clear sessionStorage,
      // so the guard set above persists across the reload.
      await this.reloadAppAsync({ resetToRoot: true });
    } catch (err) {
      // Last-resort only. reloadAppAsync now guarantees location.reload() in a finally (its
      // teardown is bounded, so neither a throw nor a hang can skip it) and then parks forever, so
      // it normally neither resolves nor rejects — this catch is reachable only if the reporting
      // inside it throws. A pre-reload teardown failure surfaces as ReloadPrepareFailed there, not
      // here. Kept as a backstop: if we somehow DO land here, no reload is coming, so report the
      // distinct failure and re-arm the stuck-check loop (the autoReload branch stopped it). The
      // one-shot guard above stays set, so this can never become a reload loop.
      this.logging.trackSilentProblem('BootReloadFailed', {
        ...context,
        ...errorReportFields(err),
      });
      if (this.appLoading) {
        this.scheduleSplashStuckCheck();
      }
    }
  }

  async logoutAsync() {
    // a second sign-out started while one is already running strands the app on its loading
    // screen until the splash watchdog reloads the page 85 seconds later, so it is refused.
    // Why it strands rather than merely repeating work is not obvious — see
    // design-docs/boot-sequence.md, "A second sign-out wedges the splash".
    if (this.signOutInFlight) {
      console.log('App: sign-out already in flight, ignoring repeat request.');
      return;
    }
    this.signOutInFlight = true;
    try {
      // F1 (#3988): run the data-safety guard BEFORE any destructive step (before
      // markAppLoadingAsync, so a Cancel leaves the UI untouched). The guard warns
      // when signing out offline could discard unsynced local writes, and drains a
      // pending push first when the server is reachable.
      const canProceed = await confirmSignOutSafeAsync({
        onlineState: this.onlineStateService,
        userDbWrapper: this.userDb?.database ?? null,
        alertCtrl: this.alertCtrl,
        logging: this.logging,
        imageUploadOutbox: this.imageUploadOutbox,
      });
      if (!canProceed) {
        // the user cancelled at the guard, so nothing destructive ran and this is not a
        // sign-out at all — release the flag or Sign Out would be dead for the rest of the
        // page's life. (The guard runs above markAppLoadingAsync for the same reason.)
        this.signOutInFlight = false;
        return;
      }
      await this.markAppLoadingAsync(true);
      // coach white-labeling (#3707): drop any coach brand theme + splash cache
      // before signing out, so the next account on this device starts on pure MH.
      this.coachBranding.resetForSignOut();
      // this will signal login state change, which will show the login page,
      // which will in turn do a full reload (see `LoginPage.ionViewCanEnter()`)
      // eslint-disable-next-line no-restricted-syntax -- guarded call site approved by the F1 guard flow (confirmSignOutSafeAsync above)
      await this.auth.signOutAsync();
    } catch (err) {
      // the sign-out itself failed, so no reload is coming and the user is still signed in —
      // let them try again rather than leaving the button permanently dead.
      this.signOutInFlight = false;
      throw err;
    }
  }

  async loginCompleteAsync() {
    console.log('LoginPage: Logged in.');
    /* Note that the app component will detect the login state transition and go to the default page itself. This just shows the loading page in the mean time. */
    await this.markAppLoadingAsync(true);
  }

  async showDatabaseRecoveryLoadingAsync() {
    const loading = this.loadingCtrl.create({
      content: LL.app.databaseRecoveryLoading()
    });
    await loading.present();
    return loading;
  }

  async logonChangedAsync(user: UserAuth) {

    // update the menu display
    this.userName = (user && user.name) || '<unnamed>';

    // handle any pending checkout redirect
    try {
      const result = await this.externalFlow.assureCheckoutResultHandledAsync();
      if (result?.action === 'reload') {
        // this handles the weird case when the user is give a temporary license from PPG's 'in-review' state
        // that state is only resolved after parsing the URL result, and we need a logged in user to apply that state
        // but then need to reload because the user suddenly becomes subscribed
        await this.reloadAppAsync({ resetToRoot: true });
      } else if (result?.action === 'informMagicLink') {
        await this.navCtrl.setRootAsync(ILoginPage, {
          mode: 'subscription-success',
          androidInstallUrl: result.androidInstallUrl,
          iosInstallUrl: result.iosInstallUrl
        });
      }
    } catch (error) {
      // AbortError comes from our own reloadAppAsync({ resetToRoot: true }) above
      // tearing down the in-flight userSubscription fetch. That is the intended
      // post-Review reload sequence — not a bug — so it must not page Sentry.
      if (isAbortError(error)) {
        console.log('App: checkout-handling fetch aborted (expected during reload)');
        return;
      }
      // defensive: never let checkout handling crash the login flow
      console.error('App: Error during checkout result handling:', error);
      this.logging.trackSilentProblem('CheckoutResultHandlingError', { error: (error as { message?: string })?.message || String(error) });
    }

    // if user is logged in
    if (user) {
      console.log(`App: Logged in: ${user.name} <${user.email}>`);
      void this.postLoginAsync(user);
      this.telemetry.trackAuthenticatedSession(user);

      // if user is not logged in, reroute to login page
    } else {
      console.log('App: Not logged in.');

      // S3 (#3986 Step 19): a logged-out boot is a NORMAL boot completion —
      // the login/welcome page is about to show. Close the READ-priority
      // window here too, or a user idling on the login screen for over
      // 3 minutes would trip the failsafe and emit a spurious "wedged boot"
      // report (on every platform, not just iOS).
      setBootReadPriorityActive(false);

      // Only shut down databases if this is an actual logout (user was previously logged in)
      // Don't shut down on initial auth checks or temporary auth state changes
      if (this.auth.getWasEverLoggedIn()) {
        console.log('App: User was previously logged in, shutting down databases.');
        // stop presence heartbeats before databases close
        this.coachPresenceState.teardown();

        // #4762: every step below is BOUNDED, because markAppLoadingAsync(false) comes after
        // them and is the only thing that takes the loading screen back down — so one step
        // that never settles freezes the app until the watchdog reloads the page. Giving up
        // on a step is cheap: the login page reloads and wipes local data moments later, so a
        // handle we failed to close is destroyed regardless. See signout-teardown.ts for the
        // contract, design-docs/boot-sequence.md for the incident, and the tripwires in
        // signout-teardown.test.ts that fail if a bare `await` is added back to this block.
        await runBoundedTeardownStepsAsync([
          // flush pending telemetry deletes before databases close, to avoid stale events in IDB
          { name: 'telemetryFlush', timeoutMs: SIGNOUT_TELEMETRY_FLUSH_TIMEOUT_MS, run: () => this.telemetry.flushAsync() },
          { name: 'coachExerciseSync', timeoutMs: SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS, run: () => this.coachExerciseSync.stopAsync() },
          { name: 'exerciseDb', timeoutMs: SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS, run: () => this.exerciseDb.shutdownAsync() },
          { name: 'textDb', timeoutMs: SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS, run: () => this.textDb.shutdownAsync() },
          {
            name: 'foodDb', timeoutMs: SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS, run: () => this.foodDb.shutdownAsync(),
            describeOnGiveUp: () => describeDatabaseShutdownForReport(this.foodDb.database?.describeShutdown()),
          },
          {
            name: 'userDb', timeoutMs: SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS, run: () => this.userDb.shutdownAsync(),
            describeOnGiveUp: () => describeDatabaseShutdownForReport(this.userDb.describeShutdown()),
          },
        ], this.logging);

        // NOT one of the steps above, deliberately: a step is something we are willing to
        // skip, and this is the one call we are not.
        await this.markAppLoadingAsync(false);

        // commonDb closes AFTER the loading screen comes down — that ordering predates
        // #4762 and is kept. It is bounded all the same, so a wedged close cannot leave an
        // await hanging around for the life of the page.
        await runBoundedTeardownStepsAsync([
          {
            name: 'commonDb', timeoutMs: SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS, run: () => this.commonDb.shutdownAsync(),
            describeOnGiveUp: () => describeDatabaseShutdownForReport(this.commonDb.database?.describeShutdown()),
          },
        ], this.logging);
        // Drop the staple snapshot read out of commonDb, so the next account cannot be answered
        // with this one's staples (food staples plan 4c, #5625). This call is the ONLY thing that
        // notices: the snapshot lives in the store, not on the wrapper, so nothing downstream can
        // tell the accounts apart — and a read already in flight is holding the old wrapper
        // whatever the next account gets. It is synchronous and cannot fail, so
        // it needs no bounded teardown step of its own, and it goes after the shutdown above so a
        // read still in flight cannot repopulate the store afterwards.
        this.stapleDb.reset();
      } else {
        console.log('App: Initial auth check returned null, not shutting down databases.');
        await this.markAppLoadingAsync(false);
      }
      // if app booted successfully without login, clear any stale IDB recovery flag
      // (completeDatabaseRecovery is also called at the end of postLoginAsync for logged-in users)
      this.databaseRecovery.completeDatabaseRecovery();
    }
  }

  logonError(error: any) {
    console.error('App: Logon error:', describeErrorForLog(error));
    // if already navigating to fatal error page, don't dismiss loading (it triggers progress animation)
    if (!this.fatalErrorParams) {
      void this.markAppLoadingAsync(false);
    }
  }

  // things to do before even the settings are loaded
  async preSettingsAsync() {
    // if (this.platform.is('core')) {
    //   this.config.set('tabsPlacement', 'top');
    // }
    // this.config.set('tabsHideOnSubPages', true);
  }

  progressPercent: number = 0;
  allDatabasesSyncProgressState: any[] = [];
  /** epoch ms of the previous progress-bar step, used to pace the next one */
  private lastProgressUpdateMs: number | null = null;

  async updateLoadingProgress() {
    // Each database reports "work done / work to do" on every pull batch, so the
    // bar can track the download continuously instead of jumping to full at the
    // end. For the user database that total is a real document count once its
    // background pre-count lands (see countInitialSyncDocsAsync); until then it
    // is PouchDB's rougher `pending` estimate. Logic lives in
    // boot-progress-wait.ts so it is unit-testable (same pattern as the
    // animation wait above).
    // #4809: the wrapper the CURRENT start-up is downloading into — deliberately not
    // `this.userDb.database`, which since #4809 is only handed out once the documents are
    // loaded, i.e. AFTER the download this bar is tracking. Asking the wrapper what it is
    // (`isUserDb()`, true from the moment the database is opened) restores what the identity
    // comparison used to mean. Newest wins, and the `.reverse()` is still load-bearing even
    // though a superseded wrapper is now shut down at the hand-over: supersession happens at
    // the hand-over, so for the whole of a second start-up's LOAD both wrappers are live and
    // both answer `isUserDb()`, and the oldest would otherwise be found first. Without this
    // the bar falls through to the aggregate below during the whole load, which is the
    // measured failure the comment in boot-progress-wait.ts describes.
    const startingUpUserDb = [...this.dbProvider.activeDatabases].reverse()
      .find(database => database.isUserDb());
    const fraction = computeSyncProgressFraction(this.dbProvider.activeDatabases.map(database => ({
      syncTasksCount: database.getSyncTasksCount(),
      syncTasksCompleted: database.getSyncTasksCompleted(),
      hasReported: this.allDatabasesSyncProgressState.some(d => d.database === database),
      isUserDb: database === startingUpUserDb,
    })));
    // nothing is reporting work yet — leave the bar where it is
    if (fraction == null) {
      return;
    }
    const progressPercent = roundToPrecision(fraction, 0.01);
    // the bar only ever moves forward: each sync phase restarts its own task
    // counts, so a raw reading can dip when the next phase begins
    if (progressPercent <= this.progressPercent) {
      return;
    }
    this.progressPercent = progressPercent;

    // stretch this step across the gap the previous one took, so the bar keeps
    // moving between batches instead of jumping and then sitting still. linear,
    // not eased: this is an interpolation between two real measurements, and an
    // ease-out would spend most of the step already parked at the target
    const nowMs = DateTime.utc().toMillis();
    const msSinceLastUpdate = this.lastProgressUpdateMs == null ? null : nowMs - this.lastProgressUpdateMs;
    this.lastProgressUpdateMs = nowMs;

    return this.animateProgressBar(this.progressPercent, {
      durationMs: computeProgressStepDurationMs(msSinceLastUpdate),
      easing: 'linear',
    });
  }

  progressAnimation: Animation;
  progressAnimationStart: number;
  progressAnimationEnd: number;

  getCurrentProgressBarValue() {
    let currentProgressBarValue = 0;
    if (this.progressAnimation) {
      const timing = this.progressAnimation.effect.getComputedTiming();
      const progress = timing.progress;
      const from = this.progressAnimationStart;
      const to = this.progressAnimationEnd;
      currentProgressBarValue = from + (to - from) * progress;
    }

    return currentProgressBarValue;
  }

  // `options` lets a sync-progress step pace itself against the observed batch
  // rate (see updateLoadingProgress). The final fill to 100% keeps the original
  // short, eased glide by taking the defaults.
  async animateProgressBar(
    progressBarTarget: number,
    options?: { durationMs?: number; easing?: string },
  ) {
    if (this.progressBarFill) {
      const currentProgressBarValue = this.getCurrentProgressBarValue();

      if (currentProgressBarValue < progressBarTarget) {
        // The superseded animation is deliberately left running rather than
        // cancelled. Cancelling a `fill: 'forwards'` animation snaps the element
        // back to its base width for the rest of the frame, which shows as a
        // flicker; leaving it means the newest animation simply wins the
        // composite order, and `getCurrentProgressBarValue` above has already
        // read the position it needs. A step can now stay active for up to
        // PROGRESS_STEP_MAX_MS, so a few overlap during a sync — bounded by the
        // number of replication batches in one boot, and released with the
        // element.
        this.progressAnimationStart = currentProgressBarValue;
        this.progressAnimationEnd = progressBarTarget;
        const progressBarFillElement = this.progressBarFill.nativeElement as HTMLElement;
        progressBarFillElement.style.setProperty('opacity', '1');
        this.progressAnimation = progressBarFillElement.animate(
          [
            { "width": `${Math.round(this.progressAnimationStart * 100)}%` },
            { "width": `${Math.round(this.progressAnimationEnd * 100)}%` },
          ],
          {
            duration: options?.durationMs ?? PROGRESS_STEP_DEFAULT_MS, // duration in milliseconds
            fill: 'forwards', // ensures the animation stays at the end state
            easing: options?.easing ?? 'ease-out', // easing function
          }
        );

        return this.progressAnimation.finished;
      }
    }

    return Promise.resolve();
  }

  // things to do before initiating login
  async preLoginAsync() { }

  async applyScheduleFixes() {
    const schedule = await this.userDb.loadScheduleAsync();

    const ruleStartDate = this.debug.getNowLocal().toISODate();
    let modified = false;
    if (schedule.days && schedule.days.length > 0) {
      schedule.rules = convertScheduleDaysToRules(schedule.days, ruleStartDate);
      delete schedule.days;
      modified = true;
    } else if (schedule.rules) {
      for (const rule of schedule.rules) {
        if (rule.dateISO === undefined) {
          rule.dateISO = ruleStartDate;
          modified = true;
        }
      }

      // deduplicate active workout rules (repairs corruption from sync conflicts or prior bugs)
      if (deduplicateActiveWorkoutRules(schedule.rules, ruleStartDate)) {
        modified = true;
      }

      // validate schedule structural completeness
      const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
      if (declaredState.trainingDays) {
        const hasDayStart = schedule.rules.some(r => r.itemType === Schedule_ItemType.DayStart);
        const hasDayEnd = schedule.rules.some(r => r.itemType === Schedule_ItemType.DayEnd);
        const hasMeals = schedule.rules.some(r => r.itemType === Schedule_ItemType.Meal);

        if (!hasDayStart || !hasDayEnd || !hasMeals) {
          // schedule is missing structural rules — regenerate from defaults
          const missing = [!hasDayStart && 'DayStart', !hasDayEnd && 'DayEnd', !hasMeals && 'Meal'].filter(Boolean);
          const ruleTypes = [...new Set(schedule.rules.map(r => r.itemType))];
          this.logging.trackSilentProblem('schedule_missing_structural_rules', { missing, ruleCount: schedule.rules.length, ruleTypes });
          const days = generateScheduleWithDefaults(declaredState);
          schedule.rules = convertScheduleDaysToRules(days, ruleStartDate);
          modified = true;
        } else if (
          // count only the meal slots still in force: lowering the meals per day ends the
          // extra slot's rules rather than deleting them, and counting those would re-save
          // an already correct schedule on every app start (df-4157f03af462)
          getLiveMealReferences(schedule.rules, ruleStartDate).length !== declaredState.mealFrequency ||
          !doActiveWorkoutDaysMatchDeclared(schedule.rules, declaredState.trainingDays, ruleStartDate)) {
          // schedule is structurally complete but meal/workout counts don't match declared state
          adjustScheduleToDeclaredState(schedule, declaredState, ruleStartDate);
          modified = true;
        }
      }
      
      if (schedule.rules) {
        const weeklyCheckupRule = schedule.rules.find(e => e.itemType === Schedule_ItemType.WeeklyCheckup);
        if (!weeklyCheckupRule) {
          const dayEndRule = schedule.rules.find(e => e.itemType === Schedule_ItemType.DayEnd && e.weekday === 7 && !e.endDateISO) ||
            schedule.rules.find(e => e.itemType === Schedule_ItemType.DayEnd && e.repeat === ScheduleRuleRepeat.Daily && !e.endDateISO);
          schedule.rules.push({
            itemType: Schedule_ItemType.WeeklyCheckup,
            dateISO: ruleStartDate,
            weekday: 7,
            // a snapshot, not the source of truth — see Schedule_ItemWeeklyCheckup
            time_hr: dayEndRule ? getWeeklyCheckupTimeHr(dayEndRule.time_hr) : 8,
            repeat: ScheduleRuleRepeat.Weekly,
          });
          modified = true;
        } 
      }
    } else {
      // empty days and no rules — initialize rules so downstream code never sees undefined
      schedule.rules = [];
      modified = true;
    }

    if (modified) {
      await this.userDb.saveScheduleAsync(DatabaseWrapper.getTransactionId());
      await this.userDb.clearBacklogCacheAsync();
    }
  }


  // ─── #4981 phases 3 and 4: the Android local-database engine lifecycle ──────
  //
  // The lifecycle itself — migrate, roll back, count the grace period, cancel — lives in
  // `app/sqlite-lifecycle.ts`, where it can be driven by a unit test. What stays here is
  // the part that genuinely belongs to a component: WHEN each half runs, which is boot
  // ordering and a documented contract (design-docs/boot-sequence.md), and the wiring
  // below, which is nothing but browser globals and injected services.
  //
  // Everything the lifecycle DECIDES is in `services/sqlite-migration-decisions.ts` and
  // `services/local-database-engine-decisions.ts`; everything it DOES is in
  // `app/sqlite-migration.ts` and `app/sqlite-migration-reverse.ts`. All of those are
  // Angular-free and node-tested, because this component cannot be driven from a unit
  // test.
  //
  // See design-docs/android-local-database-engine.md.

  /**
   * This page load's engine lifecycle.
   *
   * Its state is per page load, which is this component's own lifetime — `MyApp` is the
   * root shell, created once and never destroyed. That is why it is built here rather
   * than provided as a singleton service: the two lifetimes have to be the same one.
   *
   * Every entry in the deps object below is a function, so nothing is read while this
   * field is being initialised — `reporter` forwards rather than capturing `this.logging`
   * for the same reason. That keeps the construction order of the injected services out
   * of the question entirely.
   */
  private readonly sqliteLifecycle = new SqliteLifecycle({
    reporter: {
      trackSilentInfo: (name, context) => this.logging.trackSilentInfo(name, context),
      trackSilentProblem: (name, context) => this.logging.trackSilentProblem(name, context),
    },
    localStorageOrNull: () => browserLocalStorageOrNull(),
    isAndroidNative: () => isCapacitorNative() && !isPlatformIOS(),
    deviceId: () => getDeviceId(),
    latchedEngine: () => getLocalDatabaseEngine(),
    engineHasBeenRead: () => hasLocalDatabaseEngineBeenRead(),
    waitForConfigLoadedAsync: () => this.configDb.loaded,
    getConfigValue: (key) => this.configDb.getConfigValue(key),
    describeBootStage: () => this.bootStage,
    enterMigrationBootStage: () => { this.bootStage = BootStage.SqliteMigration; },
    enterRollbackBootStage: () => { this.bootStage = BootStage.SqliteRollback; },
    animateProgressBarAsync: (fraction) => this.animateProgressBar(fraction),
    // a raw setTimeout, like the splash-watchdog chain and for the same reason: MyApp is
    // the never-destroyed root shell, so there is no destruction for a guarded timer to
    // protect against.
    scheduleTimeout: (callback, ms) => setTimeout(callback, ms),
    buildMigrationDeps: (uid, storage, phase) => this.buildSqliteMigrationDeps(uid, storage, phase),
    buildRollbackDeps: (uid, storage, phase) => this.buildSqliteRollbackDeps(uid, storage, phase),
    // #5270 step 6b. The browser globals live here, as they do for the copy itself; the
    // lifecycle owns the gate and the once-per-app-run promise around this call.
    cleanUpSupersededIndexedDbAsync: (storage) => cleanUpSupersededIndexedDbAsync({
      indexedDb: indexedDB,
      storage,
      reporter: this.logging,
    }),
  });

  /**
   * Everything the migration needs from the outside world, for either half. (#5449)
   *
   * One builder rather than two, because almost all of it is identical and the pieces
   * that differ are worth seeing side by side. What the PHASE changes is stated in
   * `MigrationPhase` and enforced inside the migration, not here.
   *
   * The three collaborators the BACKGROUND half cannot run without are wired only into
   * the background shape, because `SqliteMigrationDeps` requires them there and does not
   * accept them at all on the switch shape. That is the whole point of the split: the
   * switch half never calls them, so handing it three functions it will not use said
   * nothing and hid the fact that the background half must have them.
   *
   * It stays on the component, like `buildReloadTeardownDeps`, because every line of it
   * is a browser global or an injected service. Nothing here decides anything.
   */
  private buildSqliteMigrationDeps(
    uid: string,
    storage: Storage,
    phase: MigrationPhase,
  ): SqliteMigrationDeps {
    const migrationTimer = runningTimeTimer();
    const common: SqliteMigrationDepsCore = {
      indexedDb: indexedDB as unknown as { databases(): Promise<{ name?: string }[]> },
      // named adapters, never `getLocalAdapterOpts()` — that helper asks the engine
      // question, and R3 depends on nothing having asked it yet
      openDatabase: (name, adapter) => new PouchDB(name, { adapter }) as any,
      deleteSqliteDatabaseAsync: async (name) => {
        const { CsSQLite } = await import('services/cs-sqlite-plugin');
        return CsSQLite.deleteDatabase({ name });
      },
      storage,
      uid,
      // through the service accessor, NOT a raw localStorage read: the key holds
      // `JSON.stringify(client)`, so the raw string matches no uid and the coach's
      // own client database would be judged to belong to another account and
      // dropped from the enumeration
      managedClientUid: this.userDb.getCoachManagingClient()?.clientUid ?? undefined,
      deviceId: getDeviceId(),
      decideFromControls: (allowListValue, percentValue) => shouldMigrateThisDeviceToSqlite(
        allowListValue, percentValue, uid, getDeviceId(), bucketForIdentifier),
      currentLocalStoreName: (baseName) => currentLocalStoreName(localStorage, baseName),
      reporter: this.logging,
      // #6093: both limits count only time the page was running. The copy runs in the
      // background while the app is in use, so a phone locked mid-copy is ordinary; on a
      // plain setTimeout it would have cancelled a healthy copy the moment the phone woke.
      withTimeout: (operation, operationName, timeoutMs) => withLocalStoreTimeout(
        operation, operationName, timeoutMs, migrationTimer.schedule, migrationTimer.clear),
      // #5448: the copy is bounded by how long it goes QUIET, not by how long it
      // takes. A real account takes about five minutes, so the total-time bound
      // cancelled every copy on every device at 60 seconds.
      withIdleTimeout: (operation, operationName, idleTimeoutMs, subscribeToProgress) =>
        withLocalStoreIdleTimeout(operation, operationName, idleTimeoutMs,
          subscribeToProgress, migrationTimer.schedule, migrationTimer.clear),
      // #5448: asked AGAIN after the copy. Reading this does not take the latch —
      // it reports whether the latch is already taken, which is exactly the
      // question, because anything that opened a database during the copy took it.
      // #5449 asks it in the SWITCH half only; in the background half the app is
      // running, so the answer is always yes and the question is meaningless.
      hasEngineBeenRead: () => hasLocalDatabaseEngineBeenRead(),
      // recorded with each target, so a storage-format change between releases makes
      // the copy start again rather than be trusted
      appVersion: AppVersion,
    };

    if (phase === MigrationPhase.Switch) return { ...common, phase };

    return {
      ...common,
      phase,
      pauseBetweenSlicesAsync: () => this.waitForAGoodMomentToCopyAsync(),
      // #5449: the two halves of stopping an in-flight copy when the app tears down —
      // the slice running now, and the ones not yet started. Both are owned by the
      // lifecycle, because both have to outlive any one copy and none of them is a
      // browser fact. See `cancelSqliteMigrationWork` there.
      registerCancellable: (cancel) => this.sqliteLifecycle.registerCancellable(cancel),
      isCancelled: () => this.sqliteLifecycle.isCancelled(),
    };
  }

  /**
   * Everything the way back needs from the outside world, for either half. (#5270)
   *
   * Built on `buildSqliteMigrationDeps` rather than beside it: the copying is the same
   * copying, and anything restated here would eventually be stated differently. What it
   * overrides is exactly the set of things that are not the same — which direction the
   * data moves, which two config documents the kill switch reads, which pure helper
   * answers them, and the four things only a rollback does.
   */
  private buildSqliteRollbackDeps(
    uid: string,
    storage: Storage,
    phase: MigrationPhase,
  ): SqliteRollbackDeps {
    const deps: SqliteRollbackDeps = {
      ...this.buildSqliteMigrationDeps(uid, storage, phase),
      decideFromControls: (allowListValue, percentValue) => shouldRollBackThisDeviceToIndexedDb(
        allowListValue, percentValue, uid, getDeviceId(), bucketForIdentifier),
      controlDocIds: {
        allowList: ROLLBACK_ALLOW_LIST_DOC_ID,
        percent: ROLLBACK_PERCENT_DOC_ID,
      },
      readLocalStoreGeneration: (baseName) => readLocalStoreGeneration(localStorage, baseName),
      localStoreNameFor: (baseName, generation) => localStoreNameFor(baseName, generation),
      bumpLocalStoreGeneration: (baseName) => bumpLocalStoreGeneration(localStorage, baseName),
      setEngineMarker: (value) => storage.setItem(LOCAL_DATABASE_ENGINE_KEY, value),
      deleteIndexedDbDatabaseAsync: (logicalName) =>
        this.deleteOneIndexedDbDatabaseAsync(logicalName),
      // carried from the session that decided, in the request marker's value. `undefined`
      // when it cannot be read, which stops the rollback committing rather than guessing.
      reArmGeneration: readRequestedReArmGeneration(
        storage.getItem(SQLITE_ROLLBACK_REQUESTED_KEY)),
    };
    // assigned after the object exists because the direction reads the generation helpers
    // above, and a self-reference cannot be written inside the literal
    deps.direction = reverseCopyDirection(deps);
    return deps;
  }

  /**
   * Delete ONE IndexedDB database, and refuse to call anything but a confirmed deletion a
   * success.
   *
   * Used to throw away a reverse-copy target that can no longer vouch for itself. Through
   * the shared bounded deleter, because `indexedDB.deleteDatabase` deletes nothing by the
   * time it returns: the answer arrives later as `success`, `error` or `blocked`, and after
   * a `blocked` nothing further may ever fire. A caller that ignored those would copy into
   * a database it believed it had emptied.
   *
   * Throws on anything other than a confirmed deletion, so the failure reaches the copy's
   * own catch and is reported there with its real reason.
   */
  private async deleteOneIndexedDbDatabaseAsync(logicalName: string): Promise<void> {
    const storageName = `_pouch_${logicalName}`;
    const [result] = await deleteIdbDatabasesAsync(indexedDB, [storageName], {
      timeoutMs: MIGRATION_BATCH_TIMEOUT_MS,
      logPrefix: 'SQLite rollback (phase 4): ',
    });
    if (result?.outcome !== IdbDeletionOutcome.Deleted) {
      throw new Error(
        `could not delete IndexedDB database ${storageName}: ${result?.outcome ?? 'no result'}`);
    }
  }

  /**
   * Wait until it is a good moment to copy the next slice. (#5449)
   *
   * Two rules, and the second is the one the owner asked for:
   *
   *  - **A short pause between slices**, so the copy takes its turn rather than holding
   *    the thread the interface draws on for the minutes it needs.
   *  - **Nothing at all while the app is hidden.** Somebody who has put their phone
   *    down is not waiting for this, and Android suspends a backgrounded WebView's
   *    timers anyway — so a copy that carried on there would not actually run, it would
   *    just sit half-started until the 60-second silence bound cancelled it and spent an
   *    attempt for nothing.
   *
   * Deliberately resolves on `visibilitychange` rather than polling: a hidden page gets
   * no timers, so a poll would not fire either.
   */
  private waitForAGoodMomentToCopyAsync(): Promise<void> {
    return new Promise<void>(resolve => {
      const resumeWhenVisible = () => {
        if (document.visibilityState === 'hidden') return;
        document.removeEventListener('visibilitychange', resumeWhenVisible);
        resolve();
      };
      // a raw setTimeout, like the splash-watchdog chain above and for the same
      // reason: MyApp is the never-destroyed root shell, so there is no destruction
      // for a guarded timer to protect against.
      setTimeout(() => {
        if (document.visibilityState !== 'hidden') {
          resolve();
          return;
        }
        document.addEventListener('visibilitychange', resumeWhenVisible);
      }, MIGRATION_SLICE_PAUSE_MS);
    });
  }

  async postLoginAsync(user: UserAuth) {
    // df-53c89a39b0e6: when a login retry re-emitted the user, this boot holds the retry's claim on
    // the post-login move until its navigation start-up has sent the person through the page
    // filter. the warm path's start-up runs in the background and takes the claim over itself
    const retryRoute: RetryRouteHandoff = { claim: this.auth.takeRetryRouteClaim(), takenByNavInit: false };
    try {
      await this.postLoginBootAsync(user, retryRoute);
    } finally {
      if (!retryRoute.takenByNavInit) retryRoute.claim?.release();
    }
  }

  private async postLoginBootAsync(user: UserAuth, retryRoute: RetryRouteHandoff) {
    if (await this.sso.redirect()) {
      return;
    }

    // if the splash is visible but the watchdog isn't running, arm it — the
    // persisted-session cold boot shows the splash via the field initializer
    // and never goes through markAppLoadingAsync(true) (#3698)
    this.armSplashWatchdogIfNeeded();

    // #4627: a friend who arrived from an invite link stored it before creating
    // their account. This is the first moment they are signed in, so it is where
    // the referral is attached.
    //
    // Deliberately not awaited, and it never throws: attaching a referral must
    // not delay the boot or be able to break it, and phase 1 grants nothing, so
    // there is nothing the user loses by it failing quietly here. The service
    // reports its own failures, and a friend whose claim never landed can still
    // type the code by hand on the referral hub. For everybody else — which is
    // almost everybody — this is one read of session storage and nothing more.
    void this.referral.claimPendingIfAnyAsync();

    // #4981 phase 3 — the MIGRATING boot. ONE call site, immediately above the
    // warm/cold branch so BOTH paths reach it, and deliberately not inside either
    // branch's method. It must run after `auth.initAsync()` (there is a signed-in
    // user id by here) and before any DatabaseWrapper opens (nothing has asked which
    // engine this device uses yet, and that answer is latched on its first read).
    // The method itself explains why both halves of that window are required.
    // #4981 phase 4 (#5270) — finish any rollback whose commit point has already been
    // passed, THEN run the rollback's own switch half. Both sit here, above the migration
    // call and above the warm/cold branch, for the same window reason the migration gives.
    // The tail goes first because it is what makes the engine answer below current.
    this.sqliteLifecycle.resolveSqliteRollbackTailIfAny(user?.uid);
    await this.sqliteLifecycle.runSqliteRollbackIfRequestedAsync(user?.uid);

    await this.sqliteLifecycle.runSqliteMigrationIfRequestedAsync(user?.uid);

    // #4981 phase 4 (#5270): tag every Sentry event from here on with which engine this
    // device's local data is in, so a crash or a problem can be grouped by it during the
    // ramp.
    //
    // **Here, and deliberately NOT beside the other `Sentry.setTag` at the top of
    // `app-sentry.ts`.** Placement still matters even though the read below cannot take the
    // latch: by this line both hooks have run, so the marker reflects any migration or
    // rollback THIS page load performed, and the tag describes the engine the device is
    // going forward on rather than the one it arrived with. Tagging at import time would
    // report the pre-migration answer for the whole session.
    Sentry.setTag('local_database_engine', peekLocalDatabaseEngineWithoutLatching());

    const isWarmResume = AuthService.getWarmResumeUid() === user.uid;
    this.isWarmResume = isWarmResume;

    if (isWarmResume) {
      await this.postLoginWarmResumeAsync(user, retryRoute);
    } else {
      await this.postLoginColdStartAsync(user);
    }

    // #4981 phase 3 — the DECIDING boot, which writes the marker the NEXT page load
    // acts on. It sits after the branch because it needs ConfigDbService to have
    // loaded, which each branch starts. Not awaited: nothing in the boot depends on
    // it, and it never throws.
    void this.sqliteLifecycle.decideSqliteMigrationAsync(user?.uid);

    // #4981 phase 4 (#5270) — the same, for the way back. The two are mutually exclusive
    // in practice: one requires the device to be on IndexedDB, the other on SQLite.
    void this.sqliteLifecycle.decideSqliteRollbackAsync(user?.uid);

    // A coach may have paid for a coaching plan whose confirmation never reached
    // our server, in which case the phone wrote the purchase down and this
    // re-sends it (`df-2745c2d64a48`). It sits AFTER the branch above on purpose:
    // sign-in has settled and the boot's own couch login has finished, so it
    // neither races that login nor reports an auth failure that is not one.
    // Not awaited, and it never throws — a coach's recovery must not delay the
    // boot or be able to break it, and the service reports its own failures. For
    // everybody else this is one read of local storage and nothing more.
    void this.coachTierService.retryUnconfirmedPurchaseIfAnyAsync();

    // F24 (#3991): the boot was held on the offline first-load splash. Do not stack an install
    // sheet on top of a splash the user cannot get past — they have not even seen the app yet.
    if (this.offlineFirstLoadBlocked || this.accountLoadBlocked) return;

    // fires on every cold relaunch regardless of warm/cold path so existing
    // PWA users (who almost always take the warm-resume path) still see the
    // migration nudge. Background→foreground does NOT re-run postLoginAsync,
    // so this won't pester users who keep the tab open.
    this.maybeShowAppInstallSheet();
  }

  private maybeShowAppInstallSheet(): void {
    if (!this.appInstallSvc.shouldShowStartupSheet()) return;
    void this.appInstallSvc.resolveInstallTarget().then(target => {
      if (target.kind === InstallTargetKind.NativeStore || target.kind === InstallTargetKind.NativeOpen) {
        void CsAppInstallSheet.present(this.popoverCtrl, target, 'startup');
      }
    }).catch(err => {
      // resolveInstallTarget shouldn't reject in practice, but surface any
      // future regression instead of letting it become an unhandled rejection
      this.logging.trackAbnormalException(err);
    });
  }

  // S6 (#3986 Step 18): warm the backlog cache with the exact read home's
  // first render performs (cs-day-items.loadScheduleAsync: today ±1 day of
  // log entries), so that read overlaps ColdFinishing/nav init instead of
  // running serially after them. BacklogCache serializes concurrent access
  // through its internal mutex, so overlapping with home's own read is safe
  // (the second caller reuses the cached days). If home was deep-linked to a
  // different date, this warms today for nothing — harmless, the cache keeps
  // it for later navigation.
  private async prewarmHomeScheduleAsync(): Promise<void> {
    // one-off read through the throwaway-watcher helper. Called inside the try
    // so even a synchronous throw is caught below — the call site is
    // fire-and-forget, so nothing else would observe a rejection.
    try {
      const currentDay = this.debug.getNowLocal().startOf('day');
      await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.getLogEntriesInRangeAsync(
        currentDay.minus({ days: 1 }).startOf('day'),
        currentDay.plus({ days: 1 }).endOf('day'),
        watcher,
        { inLogEntriesTimeZone: true },
      ));
    } catch (e: any) {
      // the pre-warm is an optimization only — home retries the identical
      // read on first render and that path surfaces its own failure to the
      // user. A transient network drop during pass-through is expected noise
      // here; anything else is surfaced so a broken pre-warm (which would
      // silently give back the old serial-boot latency) stays visible.
      if (isTransientFetchError(e)) {
        console.warn('App: home schedule pre-warm skipped (transient network error):', e?.message ?? e);
      } else {
        this.logging.trackSilentProblem('HomePrewarmFailed', errorReportFields(e));
      }
    }
  }

  // ===========================================================================
  // Shared post-login boot steps.
  //
  // Both boot paths do the work below, but they do it at different moments. A
  // cold start does all of it while the splash screen is up. A warm resume
  // splits it: a local-only Phase A gets a page on screen fast, then Phase B
  // finishes in the background once the user can already see the app (see
  // design-docs/warm-resume.md).
  //
  // That difference is deliberate, so none of these methods decides WHEN it
  // runs. Each is a body of work and nothing more; the call site keeps every
  // sequencing decision — the boot stage, the timing brackets, whether the call
  // is awaited or fired and forgotten.
  //
  // They are gathered here because each one used to be written out twice, about
  // 400 lines apart, and nothing failed when only one copy was changed. Two of
  // them had already drifted by accident before this was fixed.
  // ===========================================================================

  // Record one sync-progress report from one database. Both boot paths keep the
  // same list of per-database states, and both treat any sync activity at all as
  // progress — that is what stops the splash-stuck watchdog from firing during a
  // sync that is legitimately slow rather than wedged (#3493 Phase B-2).
  private recordDatabaseSyncProgress(payload: { database: DatabaseWrapper, state: string }): void {
    this.splashWatchdogTimes.recordProgress();
    const database = this.allDatabasesSyncProgressState.find(d => d.database === payload.database);
    if (!database) {
      this.allDatabasesSyncProgressState.push({ database: payload.database, state: payload.state });
    } else {
      database.state = payload.state;
    }
  }

  // Copy the settings that came down from the user database over the in-memory
  // settings service. While a coach is managing a client, only the program
  // settings come from the client's database — everything else belongs to the
  // coach's own device and must not be overwritten by the client's values.
  //
  // The copying itself lives in services/settings-sync-copy.ts, which knows which
  // fields merge rather than being copied and can be driven by a test. What stays
  // here is the decision this method actually makes: WHICH keys to copy.
  private mergeSettingsFromUserDb(isManagingClient: boolean): void {
    const keysToMerge = isManagingClient
      ? programSettingKeys
      : Object.keys(this.settings._defaults);
    copySettingsFromDatabase(this.settings, this.userDb.settings, keysToMerge);
  }

  // Start the fire-and-forget background services that need the databases initialised.
  // Both the cold-boot and the warm-resume path call this, so a new service added here
  // starts on both; adding it to only one path would leave it silently off after the other.
  private startBackgroundServicesAfterDbInit(): void {
    // Phase 2 of #3198: filtered replication into local global_exercises staging DB +
    // apply pipeline. Fire-and-forget.
    void this.coachExerciseSync.startAsync();

    // #3989: upload any photos queued offline in a previous session. Fire-and-
    // forget; the drain no-ops when the outbox is empty and re-runs on every
    // Offline→Online transition (subscription inside the service).
    // Preview preload runs first so queued photos display from local bytes
    // while (or in case) the upload still has to happen.
    void this.imageUploadOutbox.preloadPreviewsAsync()
      .then(() => this.imageUploadOutbox.drainAsync());

    // #5692: the native apps' offline image cache. loads its index, then fetches the
    // thumbnails of the user's own foods once the first sync settles. does nothing on the web
    void this.nativeImageCache.startAsync(this.userDb);

    // df-efc66c4a2f6e: ask the server again to remove any shared food copies a delete in
    // a previous session could not get removed. Fire-and-forget; the drain no-ops when the
    // outbox is empty and re-runs on every Offline→Online transition (subscription inside
    // the service).
    void this.foodUnpublishOutbox.drainAsync();
  }

  // Save settings back into the user database whenever they change. Not
  // installed while a coach is managing a client, because those writes would
  // land in the client's database instead of the coach's own.
  private installSettingsSyncCallback(isManagingClient: boolean): void {
    if (isManagingClient) return;
    this.settings.setSyncCallback(async (settings) => {
      copySettingsToDatabase(
        settings, this.userDb.settings, Object.keys(this.settings._defaults));
      await this.userDb.saveSettingsAsync(DatabaseWrapper.getTransactionId());
    });
  }

  /**
   * Push a referral marker that was written before the database could be reached
   * (#4963).
   *
   * `SettingsService.saveAsync()` writes this device's stored settings first and
   * only then calls the sync callback that copies the value into the user's
   * `settings` document. That callback is installed here, late in boot — but the
   * invite claim that writes the marker starts much earlier, at the top of
   * `postLoginAsync`, and on the invite page. So a claim that finishes first
   * leaves the marker on this device and NOT in the document that syncs.
   *
   * That is not a small loss. The marker is what tells the app it was invited by
   * somebody, and the ordinary invite journey crosses devices: a friend claims in
   * a browser and then trains in the app on their phone. A marker that never
   * reaches the document never reaches the phone, the friend's training is never
   * reported, and their inviter simply never sees it — with nothing saying so.
   * Nothing repairs it later either, because the claim happens once and the marker
   * is already set in memory, so nothing writes it again.
   *
   * Called immediately after the callback is installed, which is what closes the
   * window completely: a claim that finished before this point is picked up here,
   * and one that finishes after it reaches the document by the ordinary path. It
   * also repairs a device left in that state by an earlier session.
   *
   * It saves only when a marker really is behind, so an ordinary boot writes
   * nothing.
   */
  private pushStickyReferralMarkersIfBehind(isManagingClient: boolean): void {
    // no callback is installed while a coach manages a client, and the markers in
    // memory are the coach's own — there is nothing to push and nowhere to push it
    if (isManagingClient) return;

    const behind = STICKY_REFERRAL_SETTING_KEYS.some(
      key => this.settings[key] === true && this.userDb.settings[key] !== true);
    if (!behind) return;

    void this.settings.saveAsync().catch(err => {
      // a local database write, so being offline is not a cause and this is a real
      // problem rather than an ordinary failure. Reported rather than swallowed:
      // the consequence is invisible to the user and permanent for that referral.
      this.logging.trackSilentProblem('referral-marker-push-failed', { detail: describeError(err) });
    });
  }

  // Create the DocWatcher that stays alive for the whole session. Any document
  // arriving from sync re-renders the view, and a changed coach-branding
  // document re-themes the app — either because this device's own save synced
  // back, or because a coach's fan-out reached this client (#3707).
  private createBootDocWatcher(): DocWatcher {
    return this.userDb.database.createDocWatcher((_changes) => {
      this.cd.markForCheck();
      this.coachBranding.reapplyActiveBrandingIfChanged();
    });
  }

  // Handlers that only apply while a coach is managing a client: leave the
  // client's account when the client revokes access, and show the offline
  // overlay while the client's database cannot be reached.
  private setupCoachManagedModeHandlers(isManagingClient: boolean): void {
    if (!isManagingClient) return;
    this.userDb.database.onNonRetryableAuthError = () => {
      void this.stopManagingClient();
    };
    this.pubSub.subscribe(DebouncedCloudStateChangeEvent, (data: { state: DatabaseState }) => {
      this.showCoachOfflineOverlay = data.state === DatabaseState.Offline;
      this.cd.markForCheck();
    });
  }

  // Decide whether home should offer the "what's new" card, by comparing the
  // published changelog document's revision against the revision this user has
  // already seen. Never throws: both call sites fire it and forget it.
  private async checkChangelogNotificationAsync(commonDbReadiness: CommonDbReadiness): Promise<void> {
    try {
      if (commonDbReadiness === CommonDbReadiness.MustWaitForSync) {
        // the changelog text is not in the local database until the shared common
        // database has synced at least once, so wait for a sync that SUCCEEDED.
        // whatever the wait answers we fall through to the read: if the text never
        // arrived, the read finds nothing, no card is offered, and the next cold
        // start tries again. why this is not an await on the wrapper's promise:
        // see waitForInitialSyncAsync and design-docs/sync-integrity.md.
        await waitForInitialSyncAsync(
          listener => this.commonDb.database.onInitialSyncSettled(listener),
          InitialSyncWaitFor.Success);
      }
      // pin English: this read is only used for its `_rev`, which is the identity
      // the "what's new" gate compares against the synced
      // `profile.lastAppChangelog`. Per-locale documents have unrelated
      // revision lineages, so a localized rev would re-fire the card on every
      // language switch and never agree across devices.
      const changelog = await this.textDb.getTextByHelpIdAsync(
        'app-version-changelog', undefined, ENGLISH_TEXT_SEGMENT);
      if (changelog) {
        this.changelogRev = changelog._rev;
        const profile = await this.userDb.loadProfileStateAsync(this.docWatcher);
        this.showChangelogPageNotification = profile.lastAppChangelog !== undefined && profile.lastAppChangelog !== this.changelogRev;
      }
    } catch (e: any) {
      // changelog notification is non-blocking by design and self-heals on the next cold
      // start, and the dashboard card's own watcher re-reads the document when it syncs
      // in. if the failure is one of the expected kinds the dashboard card also stays
      // quiet about (the document not synced in yet, a network drop, the shared database
      // recovering or shutting down: see isExpectedChangelogReadFailure), only log it;
      // otherwise surface it so we can still spot regressions in the cold-start text-db
      // read path (#3493 Phase C; Sentry CYBERNETIC-FITNESS-APP-444, df-9977117a7491).
      if (isExpectedChangelogReadFailure(e)) {
        console.warn('ChangelogLoad skipped (expected read failure):', e?.message ?? e);
      } else {
        this.logging.trackSilentProblem('ChangelogLoadFailed', {
          name: e?.name,
          message: e?.message,
        });
      }
    }
  }

  /**
   * The legacy-IndexedDB cleanup currently running, so cold start and warm resume cannot
   * both start one on the same app run. See the method below for why that matters.
   */
  private legacyIdbCleanupInFlight?: Promise<void>;

  // One-time cleanup after iOS moved from IndexedDB to SQLite: delete the
  // IndexedDB databases left behind by the old storage engine.
  //
  // The work itself lives in legacy-idb-cleanup.ts, free of Angular, because this
  // component cannot be driven from a unit test — the same split signout-teardown.ts
  // already uses. That module waits to hear whether each deletion actually
  // succeeded, records the cleanup as finished only when they all did, reports the
  // ones that did not, and stops retrying after a few boots (#4771).
  //
  // Never throws, so both call sites can fire it and forget it.
  private async cleanUpLegacyIndexedDbAfterSqliteMigrationAsync(): Promise<void> {
    // TWO conditions, both required, and they are different facts (#4981 phase 2). A
    // THIRD fact is checked separately below, and is kept out of this `if` on purpose —
    // `legacy-idb-cleanup.test.ts` pins the shape of this one as source text.
    // This deletes EVERY `_pouch_*` IndexedDB database on the origin. On a device
    // that has not moved to SQLite, that is the live data — hence the engine check.
    // And "this device is on SQLite" still does not mean "the old copy is safe to
    // destroy": phase 3 migrates but deliberately KEEPS the IndexedDB copy, and
    // phase 4 deletes it only after a grace period. legacyIdbCleanupPermitted() is
    // the second fact, and phase 4 is what widens it.
    // #5270 widened the second fact for Android: it is now the grace period plus an
    // operator's switch. The config value is read HERE and passed in, because
    // `local-database-engine.ts` must stay loadable with no Angular; an unloaded config
    // resolves to undefined, which reads as off — the direction that delays a deletion.
    if (!usesSqliteEngine()
      || !legacyIdbCleanupPermitted(
        this.configDb.getConfigValue(ConfigKey.AndroidSqliteCleanupEnabled))) {
      return;
    }
    // A THIRD fact (df-33d6deb02cb1): a phase 4 rollback is copying back. That copy writes
    // a FRESH #4401 IndexedDB generation while the device is still on SQLite, so both
    // conditions above hold, and the sweep below would delete the copy in progress.
    //
    // Two things about the shape are load-bearing and neither is obvious: returning HERE
    // rather than inside `legacy-idb-cleanup.ts` is what keeps the deferral from spending
    // that module's bounded retry budget, and this is a SEPARATE check rather than a third
    // clause above because `legacy-idb-cleanup.test.ts` pins that `if` as source text.
    // The full reasoning, and why the deferral cannot become permanent, is in
    // `design-docs/android-local-database-engine.md`, "Phase 4 added a THIRD fact".
    //
    // The storage is resolved and read inside a `try` (df-d4d2e7ef4f36). On a browser with
    // site data blocked, evaluating `localStorage` throws from the GETTER itself, and on iOS
    // both gates above pass without touching storage, so this is where the throw would land.
    // An unreadable store declines the cleanup, the same direction `legacyIdbCleanupPermitted`
    // fails in: waiting costs disk space, deleting early costs the only copy.
    let storage: Storage;
    try {
      storage = localStorage;
      if (storage.getItem(SQLITE_ROLLBACK_REQUESTED_KEY)) return;
    } catch {
      // already reported: the engine accessor reports an unreadable store on this page load
      return;
    }
    const platform = isPlatformIOS() ? 'ios' : 'android';
    // Reuse the run already in flight rather than starting a second one. The cleanup
    // spends one of its bounded retry attempts per run, and a single app run can reach
    // this from two places (cold start and warm resume) now that neither waits for it.
    //
    // The `.catch` is what keeps this method's "never throws" true (df-d4d2e7ef4f36): the
    // module writes its attempts counter outside its own `try`, so a full device rejects
    // the run. It is attached to the shared promise, not to each `await`, so a failure is
    // reported once however many callers are waiting; and the slot is cleared so a later
    // resume can start a fresh run instead of re-awaiting a failed one.
    this.legacyIdbCleanupInFlight ??= cleanUpLegacyIndexedDbAsync({
      indexedDb: indexedDB,
      storage,
      reporter: this.logging,
      // #5270 step 6b: the problem names this cleanup files carry no platform of their
      // own, so an Android deletion failure could not be told from an iOS one while a
      // ramp was being watched. Passed IN rather than read inside the module, which must
      // stay free of `environment-helpers` — that import breaks two node suites.
      platform,
    }).catch((e: unknown) => {
      this.legacyIdbCleanupInFlight = undefined;
      this.logging.trackSilentProblem('LegacyIdbCleanupRunRejected', { ...errorReportFields(e), platform });
    });
    await this.legacyIdbCleanupInFlight;
  }

  // Reschedule the local notifications and refresh the push token on every app
  // open. Native apps only, and only once onboarding has finished — before that
  // there is no schedule to notify anyone about.
  private rescheduleNotificationsAndRegisterForPush(): void {
    if (!isCapacitorNative() || this.plan?.welcomePhase !== WelcomePhase.Done) return;
    void this.notificationService.rescheduleAllAsync(
      this.userDb,
      this.settings,
      this.debug.getNowLocal(),
      this.cloudFunctions,
    );

    // register FCM token for server-side push notifications (fire-and-forget)
    void this.notificationService.registerFcmTokenAsync(this.cloudFunctions);
  }

  // Background work that only a paid subscriber has anything to do: warm the
  // auto-plan food cache, publish foods an earlier session could not publish,
  // and retry coach notifications that failed to send. A remembered notification
  // link is not opened here: CoachManageDeepLinkService opens it once the app is
  // ready, which on a warm resume is before this runs and also when it never does (#6573).
  private startPaidUserBackgroundTasks(): void {
    if (this.auth.getAuthRole() < AuthRole.Paid) return;
    void this.foodDb.cacheAutoPlanFoodsAsync();
    void this.foodDb.tryPublishUnpublishedFoodsAsync();
    void this.coachNotify.retryPendingNotificationsAsync();
  }

  // L13 (#3986, #665): the one-shot report for a consent gate still open after
  // CONSENT_GATE_HEARTBEAT_MS. The fields separate its two causes — a person who is
  // reading or has left the app, and a dialog that never appeared. How to read them:
  // design-docs/boot-sequence.md, "Reading ConsentGateSlow".
  private reportConsentGateSlow(gateOpenedAtMs: number, gateVisibility: BootWindowVisibility): void {
    const firedAtMs = performance.now();
    // "drawn" means the licence dialog's element exists and takes up space. A dialog
    // drawn but covered by something else still counts as drawn.
    const licenseDialog = document.querySelector('consent-dialog .license-dialog-wrapper');
    const licenseDialogBox = licenseDialog?.getBoundingClientRect();
    this.logging.trackSilentProblem('ConsentGateSlow', {
      boot_stage: this.bootStage,
      license_dialog_rendered: !!licenseDialogBox && licenseDialogBox.width > 0 && licenseDialogBox.height > 0,
      hidden_at_fire: document.hidden,
      hidden_ms: Math.round(gateVisibility.hiddenMsSoFar(firedAtMs)),
      gate_elapsed_ms: Math.round(firedAtMs - gateOpenedAtMs),
    });
  }

  private async postLoginColdStartAsync(user: UserAuth) {
    // df-4ebecd016a94: claim the boot screen for THIS cold start. A second cold start really can
    // start while this one is still running — the sign-in retry re-emits and postLoginAsync runs
    // again (see supersedePreviousDatabase in services/user-db.service.ts) — and the newest one
    // owns the splash. The catch below compares this number against the field to find out
    // whether it is still the newest, which decides whether it may end the boot itself.
    this.coldStartsBegun++;
    const thisColdStart = this.coldStartsBegun;
    // the session this cold start runs for, taken from the parameter so a sign-out that landed
    // before it began is spotted too. see design-docs/warm-resume.md → Sign-out race convention
    // (df-75b868acaa0d)
    const uidAtStart = user.uid;

    // detect whether the CouchDB load balancer connection uses HTTP/2
    // (placed here because the AuthSession cookie was just set by attemptCouchLoginAsync)
    this.bootDiagnostics.probeHttp2Protocol();

    const pubSubReg = this.pubSub.subscribe(DatabaseSyncProgressEvent, async (payload: { database: DatabaseWrapper, state: string, retryMessage?: string, largeFirstSync?: boolean }) => {
      // #3493 Phase B-2: any sync activity (replication change, stage transition,
      // retry message) resets the no-progress timer so the splash Retry button
      // doesn't false-fire during legitimate large syncs.
      this.recordDatabaseSyncProgress(payload);
      // if the sync is retrying, show the retry message on the boot screen
      if ('retryMessage' in payload) {
        this.syncRetryMessage = payload.retryMessage;
        this.cd.markForCheck();
      }
      // if a large first-time sync is starting, show a specific message
      if (payload.largeFirstSync) {
        this.firstSyncMessage = LL.app.downloadingExistingData();
        this.cd.markForCheck();
      }

      void this.updateLoadingProgress();
    });

    try {
      console.log('App: loading databases...');

      this.loadingProgressType = 'bar';
      this.cd.markForCheck();

      // load databases
      // O1 (#3986): the cold-path phase brackets in this file deliberately do
      // NOT use try/finally (unlike the couchLogin / singletons brackets in
      // auth.service.ts and user-db.service.ts): a throw on any of these
      // awaits routes to the fatalError / interceptDatabaseError abort paths
      // below, where no booting_time is ever emitted, so a missing end() is
      // unobservable — and the next boot's reset() clears the open mark. If
      // you ever add a caught-and-continue path inside one of these brackets,
      // move its end() into a finally.
      this.bootStage = BootStage.ColdUserDbInit;
      bootTimings.start(BootPhase.UserDbInit);
      // #6788: the purchase-first checkout waits on this before reloading. Settled either
      // way, because a failure is handled below and the waiter only needs "no longer mid-open".
      let settleUserDbInitWait: () => void = () => {};
      this.userDbInitInFlight = new Promise<void>(resolve => { settleUserDbInitWait = resolve; });
      try {
        await this.userDb.initializeAsync(user);
      } finally {
        settleUserDbInitWait();
      }
      bootTimings.end(BootPhase.UserDbInit);
      // #6248: stop here, before anything reads or SAVES the user's documents, when start-up
      // could only produce empty defaults for some of them. See the method for the full story.
      // Same exit as the F24 gate further down: the boot-scoped subscriptions are torn down by
      // the `finally` below, and the read-priority window is closed because this boot is over.
      if (this.gateUnreadAccountDocumentsBoot()) {
        setBootReadPriorityActive(false);
        return;
      }
      // start building PouchDB view indexes immediately (in background, during loading screen)
      // so they're ready when the user opens a workout
      void this.userDb.warmUpViewsAsync();
      this.userDb.database.telemetry = this.telemetry;
      this.firstSyncMessage = null;

      // start monitoring online/offline state changes
      this.connectivityResume.subscribeToConnectionAndFocusEvents();

      this.coachManagingClient = this.userDb.getCoachManagingClient();
      const isManagingClient = !!this.coachManagingClient;

      // if managing a client, set up callback for revoked access
      this.setupCoachManagedModeHandlers(isManagingClient);

      // Migrate settings from localStorage to database if needed
      if (!isManagingClient && !this.userDb.settings.migrated) {
        // Copy all settings from SettingsService (localStorage) to UserDb
        for (const k in this.settings._defaults) {
          if (k in this.settings) {
            this.userDb.settings[k] = this.settings[k];
          }
        }
        this.userDb.settings.migrated = true;
        await this.userDb.saveSettingsAsync(DatabaseWrapper.getTransactionId());
        console.log('Settings migrated from localStorage to database');
      }

      // Reload settings from database.
      // During management: only load program settings from client's CouchDB.
      // Normal mode: load all settings from own CouchDB.
      this.mergeSettingsFromUserDb(isManagingClient);

      this.settings._isManagingClient = isManagingClient;

      // Set up sync callback to save settings to database when they change
      this.installSettingsSyncCallback(isManagingClient);
      this.pushStickyReferralMarkersIfBehind(isManagingClient);

      // #4147: the signed-in user's UI-language preference (settings.uiLanguage)
      // has just synced down. Reconcile the device-scoped boot cache to it — this
      // is what makes language follow the user onto a fresh device AND stops a
      // shared device from stranding a second user in the first user's language.
      // Not while managing a client (their prefs are not this coach's UI language).
      // df-4aea9048b683: this must run AFTER installSettingsSyncCallback. When the
      // reconcile heals an empty account preference it saves settings, and a save
      // made before the callback exists reaches only this device, never the
      // account's settings document (the same trap as #4963's referral markers).
      if (!isManagingClient) {
        this.uiLanguageReconcile.reconcileAfterSettingsSync(
          user.uid,
          // fire-and-forget: reloadAppAsync never returns.
          () => void this.reloadAppAsync({ resetToRoot: false }),
        );
      }

      this.bootStage = BootStage.ColdSharedDbs;
      bootTimings.start(BootPhase.SharedDbs);
      await Promise.all([
        this.commonDb.initializeAsync(),
        this.textDb.initializeAsync(),
        this.exerciseDb.initializeAsync(),
        this.foodDb.initializeAsync(),
        this.testCasesDb.initializeAsync(),
      ]);
      bootTimings.end(BootPhase.SharedDbs);
      // configDb is not critical, so we don't await it
      void this.configDb.initializeAsync();
      this.telemetry.initialize(this.userDb, this.auth, this.configDb);

      this.startBackgroundServicesAfterDbInit();

      // this has to be in a separate pass, because it has interdependencies to services above
      this.bootStage = BootStage.ColdDebugInit;
      await Promise.all([this.debug.initializeAsync(this.userDb)]);
      this.docWatcher = this.createBootDocWatcher();
      this.bootStage = BootStage.ColdLoadPlan;
      bootTimings.start(BootPhase.Plan);
      this.plan = await this.userDb.loadPlanAsync(this.docWatcher);
      bootTimings.end(BootPhase.Plan);

      // F24 (#3991): stop here rather than route a signed-in user into empty onboarding just
      // because their brand-new device could not sync yet. See the method for the full story.
      //
      // The boot-scoped `pubSubReg` is torn down by the `finally` below, which is load-bearing here
      // and not mere hygiene: the initial sync goes on retrying behind the held splash, and its
      // DatabaseSyncProgressEvent handler writes `firstSyncMessage` — the very field the gate just
      // set to the honest offline message. Left subscribed, a `largeFirstSync` payload would replace
      // "You're offline…" with "Downloading existing data…", which is exactly the lie F24 exists to
      // remove. The read-priority window is closed for the same reason: this boot is over.
      if (this.gateNeverSyncedOfflineBoot()) {
        setBootReadPriorityActive(false);
        return;
      }
      // #4617: the gate declined to hold. If this is the OTHER never-synced dead end — network up,
      // backend not answering — report it so the situation can be sized. Measurement only; the boot
      // carries on exactly as before.
      this.reportNeverSyncedServerUnreachableBoot();
      // #3808 — one-time heal of spurious self-forks (fire-and-forget; self-guarded once per
      // session, never throws). Collapses duplicates created by editing the user's own legacy
      // exercises so the false "shared exercise no longer available" banner stops showing.
      void this.userDb.healSpuriousSelfForksAsync(user?.uid);
      this.telemetry.setUserInfo(this.userDb, user, this.settings);

      // start heartbeat + presence watcher appropriate for the current mode
      this.coachPresenceState.setupForUser(user, this.coachManagingClient);

      // coach white-labeling (#3707): theme the app from the active client's
      // coach branding (no-op / MH default when there is none).
      this.coachBranding.applyActiveBranding();

      this.bootStage = BootStage.ColdScheduleFixes;
      bootTimings.start(BootPhase.ScheduleFixes);
      await this.userDb.convertAutomaticTrainingGoalIfNeeded();
      await this.applyScheduleFixes();
      bootTimings.end(BootPhase.ScheduleFixes);

      // S6 (#3986 Step 18): pre-warm the backlog cache for the home first
      // render, fire-and-forget. Home's first render serially awaits the
      // day-items log-entry read; starting the same read NOW lets it run
      // during the finishing phase + nav init, so home's own read hits the
      // cache. Placed after applyScheduleFixes on purpose: that is the last
      // DETERMINISTIC boot step that may clear the backlog cache (it only
      // clears when it actually modified the schedule). A background sync
      // event (a schedule doc arriving from another device) can still clear
      // the cache afterwards — then home simply re-reads on the cold miss,
      // exactly as before this optimization.
      void this.prewarmHomeScheduleAsync();

      // reschedule local notifications and sync to server on every app open (only after onboarding is complete)
      this.rescheduleNotificationsAndRegisterForPush();

      console.log('App: databases loaded!');

      // on iOS native with SQLite, clean up old IDB databases (one-time migration).
      // NOT awaited: it now waits to hear whether each deletion actually worked, and no
      // boot step depends on the answer, so awaiting it would only add that wait to the
      // cold start on exactly the devices where a deletion is wedged.
      void this.cleanUpLegacyIndexedDbAfterSqliteMigrationAsync();
      // #5270 step 6b — and the other cleanup, for a device that has ROLLED BACK. A
      // no-op on every device that has not, which is all of them until an operator
      // opens the rollback ramp. Not awaited, for the same reason as the call above.
      void this.sqliteLifecycle.cleanUpSupersededIndexedDbAsync();

      // #5449 — the BACKGROUND half of the Android SQLite migration. Deliberately not
      // awaited and deliberately down here, after the databases are up and the app is
      // usable: this is the slow part of the copy, about five minutes on a real
      // account, and moving it off the boot screen is the whole point of the ticket.
      void this.sqliteLifecycle.runSqliteBackgroundCopyAsync(user?.uid);
      // #4981 phase 4 (#5270) — and the way back, which is a no-op on any device that is
      // not on SQLite with a rollback outstanding
      void this.sqliteLifecycle.runSqliteRollbackBackgroundCopyAsync(user?.uid);

      // the shared databases were already awaited above, so the changelog document
      // is local by now IF the shared common database's initial sync succeeded. its
      // initializeAsync lets the boot carry on while a failed initial sync is still
      // retrying, so the read can still find no document; that failure is expected
      // and not reported (see the catch in checkChangelogNotificationAsync)
      void this.checkChangelogNotificationAsync(CommonDbReadiness.AlreadyInitialized);

      // Hold the boot screen until the task calendar's own window has synced, so
      // those days fill in behind the PROGRESS BAR rather than behind a spinner on
      // an already-visible page. This waits only for the FIRST initial pass — the
      // rest of the priority window and all older history keep loading in the
      // background after boot, exactly as before.
      //
      // Bounded, and bounded twice over. The promise is resolved by every terminal
      // sync outcome (see whenCalendarWindowSyncCompleted), so it cannot outlive the
      // sync; and this wait additionally times out, so a pathological sync delays
      // boot by at most CALENDAR_SYNC_BOOT_WAIT_MS instead of wedging it. On timeout
      // we simply proceed — the calendar then fills in after boot, which is the old
      // behaviour, not a failure.
      //
      // POSITION IS LOAD-BEARING: this must stay INSIDE the try, because the
      // `finally` below unsubscribes the DatabaseSyncProgressEvent handler that
      // drives the progress bar. Waiting after that teardown holds the boot screen
      // with nothing listening for progress, so the bar sits frozen for the whole
      // wait and then jumps to full when boot forces it — which is exactly the bug
      // this wait was added to fix (measured: a 14.5 s hold with zero updates).
      if (this.userDb.database?.whenCalendarWindowSyncCompleted) {
        this.bootStage = BootStage.ColdCalendarWindowSync;
        await new Promise<void>((resolve) => {
          // raw setTimeout is this file's own convention — MyApp is the
          // never-destroyed root (see the splashStuckCheck timer)
          const timer = setTimeout(() => {
            console.log('App: calendar-window sync did not finish in time — continuing boot');
            resolve();
          }, MyApp.CALENDAR_SYNC_BOOT_WAIT_MS);
          void this.userDb.database.whenCalendarWindowSyncCompleted
            // a rejection here must not leave the splash up forever
            .catch(() => undefined)
            .then(() => {
              clearTimeout(timer);
              resolve();
            });
        });
      }

    } catch (error) {
      // S3 (#3986 Step 19): however this error is handled, this boot does not go on to load
      // anything else — so close the READ-priority window now instead of leaving background sync
      // throttled until the 180 s failsafe. That holds for every route out of this catch, the
      // ones that reload the page and the ones that leave the boot stopped where it is.
      setBootReadPriorityActive(false);
      // if the person was signed out (or switched) while this cold start ran, the failure is the
      // sign-out teardown pulling the databases away, typically a TypeError from a shared database
      // opening for a null `auth.user` (df-75b868acaa0d). the sign-out path already owns the
      // teardown and the splash, or the new user's own cold start does, so stop here: no report,
      // no recovery reload, no boot-failure screen. keyed on the app's own user only, like Phase B
      // (#6088): a still-signed-in user whose Firebase has not settled must still be handled
      if (!this.auth.user || this.auth.user.uid !== uidAtStart) {
        console.log('App: Cold start stopped — the session ended or changed while it ran:', describeErrorForLog(error));
        return;
      }
      if (this.databaseRecovery.interceptDatabaseError(error)) {
        // #5128, df-4ebecd016a94: the recovery took this failure, and what the cold start owes
        // the user next depends on which of the three outcomes happened. Each enum member's own
        // docblock carries the full reasoning; in short: stop and leave the splash up when
        // something else finishes this boot (a reload is coming, or a newer cold start has taken
        // over), restart the app when the database was repaired in place but not the part this
        // boot needed, and end the boot honestly when nothing repaired anything and nothing is
        // going to reload — because then this boot is the only thing that can take the splash
        // down, and it used to simply return and leave it up.
        const action = await decideColdStartRecoveryActionAsync(
          () => this.databaseRecovery.whenDatabaseRecoverySettledAsync(),
          this.coldStartsBegun !== thisColdStart);
        switch (action) {
          case ColdStartRecoveryAction.ReloadToRestartTheColdStart:
            // report it under its own name FIRST. recoverFromDatabaseError emits
            // database_recovery_start, and a reader seeing that straight after a
            // database_graceful_recovery_success would reasonably read it as the repair having
            // failed — which is the opposite of what happened.
            this.logging.trackSilentProblem('ColdStartAbandonedAfterGracefulRepair', {
              boot_stage: this.bootStage,
              ...errorReportFields(error),
            });
            this.databaseRecovery.recoverFromDatabaseError(error);
            break;
          case ColdStartRecoveryAction.ReportAndShowTheBootFailure:
            // df-4ebecd016a94: its own name, separate from the FatalError report fatalError()
            // writes, because this is the one route into that screen where the app broke its own
            // boot rather than the device failing. boot_stage says where, which is what tells
            // whoever eventually chases the caller that keeps a superseded wrapper
            // (cybsol/mh-physique#5306) where to look.
            this.logging.trackSilentProblem('ColdStartStoppedOnClosedDatabaseHandle', {
              boot_stage: this.bootStage,
              ...errorReportFields(error),
            });
            console.error('App: the cold start called a database handle we had already closed, '
              + 'and nothing is repairing or reloading — showing the boot-failure screen:',
              describeErrorForLog(error));
            // fatalError calls markAppLoadingAsync(false) itself, which is what takes the splash
            // down and resolves auth.whenAppReady
            await this.logging.fatalError({ type: 'booting_error', error: error });
            break;
          case ColdStartRecoveryAction.StopWithoutEndingTheBootScreen:
            // deliberately nothing: something else is going to finish this boot — the reload that
            // is already coming, or the newer cold start that superseded this one. Taking the
            // splash down here would flash a half-built app on the way out, or tear down a screen
            // a healthy boot still owns. An explicit case rather than a fall-through, so that
            // "do nothing" reads as the decision it is.
            break;
        }
        return;
      }

      console.error('App: Database initialization error:', describeErrorForLog(error));
      await this.markAppLoadingAsync(false);
      await this.logging.fatalError({ type: 'booting_error', error: error });
      return; // this means the user will be stuck here and have to refresh manually. That's intended.
    } finally {
      // #3991: in a `finally`, not after the try/catch, because several paths return early from
      // INSIDE the try — the F24 offline-boot gate, and the two fatal-error returns in the catch.
      // A missed unsubscribe is not harmless: this handler writes `firstSyncMessage`, so a still-
      // retrying sync would overwrite the gate's honest "You're offline…" splash message.
      //
      // #5128: the database-recovery branch above now AWAITS the recovery outcome, so this
      // unsubscribe is delayed by however long the repair takes. That is deliberate and it is
      // only cosmetic on every route out of that branch: while the wait lasts the splash is
      // legitimately still up, and by the time the wait ends the branch has either reloaded the
      // page, left a reload already in flight to finish, left the splash to a newer cold start
      // that owns it, or (df-4ebecd016a94) taken the splash down itself and made the
      // boot-failure page the root — so a late `firstSyncMessage` write has no splash message
      // left to overwrite.
      pubSubReg.unsubscribe();
    }

    this.bootStage = BootStage.ColdFinishing;
    // O1 (#3986): `finishing` measures only this system-paced segment (the bounded
    // progress-bar wait). The consent gate below is a phase of its own,
    // `phase_consentGate_ms` (#4615) — it is user-paced, so keep the two apart and
    // subtract the gate before quoting a boot speed. A boot stuck AT the gate is
    // also visible via boot_stage and the ConsentGateSlow heartbeat.
    bootTimings.start(BootPhase.Finishing);

    // L1 (#3986): start the progress-bar fill, but do NOT await Animation.finished
    // directly. A paused animation (hidden tab / minimized PWA) never settles and
    // would wedge boot here; a cancelled one rejects and would leave the splash up
    // forever as an unhandled rejection. Wait at most PROGRESS_BAR_MAX_WAIT_MS,
    // swallowing any rejection. MyApp is the never-destroyed root, so raw setTimeout
    // is the file's own convention (see the splashStuckCheck timer).
    const progressAnimationFinished = this.animateProgressBar(1);
    await waitForAnimationOrTimeout(
      progressAnimationFinished,
      PROGRESS_BAR_MAX_WAIT_MS,
      (callback, ms) => setTimeout(callback, ms),
    );
    bootTimings.end(BootPhase.Finishing);

    // if there is a license dialog still pending
    if (this.auth.licenseDialogSubject.getValue()) {
      // hide the prorgess bar so it does not linger in the background
      this.loadingProgressType = 'none';
      this.cd.markForCheck();
      // wait until the eventual license dialog is done
      this.bootStage = BootStage.ColdLicenseDialog;
      // L13 (#3986): this await is intentionally UNBOUNDED — we are waiting on a human to accept
      // the consent / ToS dialog, so a timeout would be wrong (that is why the splash watchdog
      // also suppresses stall detection while awaitingUserConsent, #3803). But a dialog that fails
      // to render or flip would hang boot here silently. Emit ONE ConsentGateSlow heartbeat after
      // 60 s (visibility only, not a timeout) so a regressed dialog is observable in Sentry.
      // #665: count how long the app is backgrounded while the gate is open, so the heartbeat
      // can tell a person who left the app from one looking at a dialog that never appeared.
      // Removed in the finally below — unlike the boot-window listener, this one is per gate.
      const consentGateOpenedAtMs = performance.now();
      const consentGateVisibility = new BootWindowVisibility(consentGateOpenedAtMs, document.hidden);
      const onConsentGateVisibilityChanged = () => {
        consentGateVisibility.onVisibilityChanged(document.hidden, performance.now());
      };
      document.addEventListener('visibilitychange', onConsentGateVisibilityChanged);
      const consentHeartbeat = setTimeout(() => {
        this.reportConsentGateSlow(consentGateOpenedAtMs, consentGateVisibility);
      }, MyApp.CONSENT_GATE_HEARTBEAT_MS);
      // #4615: measure the wait. It stays user-paced and unbounded — nothing
      // about the wait itself changes here — but it sits INSIDE booting_time's
      // total, and with no bracket around it that human time could not be told
      // apart from boot work. Closed in the finally below, so a throw out of the
      // dialog still records how long it ran (same pattern as couchLogin).
      bootTimings.start(BootPhase.ConsentGate);
      try {
        await firstValueFrom(this.auth.licenseDialog.pipe(first(visible => !visible)));
      } finally {
        bootTimings.end(BootPhase.ConsentGate);
        clearTimeout(consentHeartbeat);
        document.removeEventListener('visibilitychange', onConsentGateVisibilityChanged);
        // O8 (#3986): time parked at the user-paced consent gate must not count toward the
        // splash watchdog's stuck / kick / reload-escalation windows. Treat leaving the gate as
        // fresh progress — otherwise a post-consent stall would inherit a pre-consent
        // first auto-kick time and could escalate to the one-shot reload almost immediately,
        // instead of giving the sync-kick lever its intended recovery window first.
        this.splashWatchdogTimes.resetAfterConsent();
      }
    }

    // set warm resume flag so next boot can use the fast Phase A/B path
    // not for users on the subscribe page (role=Unknown): they haven't synced, so warm resume
    // would load empty PouchDB. global_common may still be downloading here (#3580), so the next
    // boot checks that it finished before trusting the flag (df-e30dd9684cf6,
    // AuthService.dropWarmResumeIfCatalogNotDownloaded)
    if (this.auth.getAuthRole() >= AuthRole.Paid) {
      AuthService.setWarmResumeFlag(user);
    }

    // remove the loading screen and that will show the current page
    this.bootStage = BootStage.ColdNavInit;
    await this.markAppLoadingAsync(false);
    this.bootStage = BootStage.Ready;
    // S3 (#3986 Step 19): boot done — close the SQLite READ-priority window
    // so background sync writes run un-throttled again
    setBootReadPriorityActive(false);
    this.notifySignedInAppReady();

    this.loadingProgressType = 'none';
    this.cd.markForCheck();

    // claim this tab as the last focused tab to prevent spurious cross-tab reload on first focus
    localStorage.setItem('lastFocusedTabId', getSessionId());

    // now kick off background tasks... but only if the user is a paid subscriber
    this.startPaidUserBackgroundTasks();

    // the browser check is for paid users too, but unlike the tasks above it is
    // skipped when we were only called to do SSO — that lets users log in to the
    // community even from a browser we otherwise do not support
    if (this.auth.getAuthRole() >= AuthRole.Paid && !this.ssoSvc.isSsoRequested()) {
      this.browserCheck();
    }

    // if all is complete, means the database was recovered (if it was in recovery)
    this.databaseRecovery.completeDatabaseRecovery();
  }

  // warm resume Phase A: open local PouchDB, load singletons, show last page
  private async postLoginWarmResumeAsync(user: UserAuth, retryRoute: RetryRouteHandoff) {
    console.log('App: Warm resume Phase A — local init');
    // the session Phase A runs for, taken from the parameter before the first await. see
    // design-docs/warm-resume.md → Sign-out race convention (df-75b868acaa0d)
    const uidAtStart = user.uid;

    const pubSubReg = this.pubSub.subscribe(DatabaseSyncProgressEvent, async (payload: { database: DatabaseWrapper, state: string, retryMessage?: string, largeFirstSync?: boolean }) => {
      // #3493 Phase B-2: reset the no-progress timer on every sync activity —
      // warm resume is the dominant cold-relaunch path and large warm-resume
      // syncs would otherwise spuriously trip the splash-stuck threshold.
      this.recordDatabaseSyncProgress(payload);
    });

    try {
      // open local PouchDB and load singletons (no network)
      this.bootStage = BootStage.WarmLocalInit;
      // O1 (#3986): the phase brackets on THIS path go through measureAsync, so
      // the bracket closes even when the call throws — unlike the cold-path
      // brackets, which are deliberately bare (see the comment in
      // postLoginColdStartAsync). The difference is the catch below: it does not
      // abort the boot, it logs and falls back to a cold start. A bracket left
      // open by the failed warm attempt would survive into that cold attempt —
      // nothing calls bootTimings.reset() again — and `currentPhase()` would
      // then report that dead phase as `boot_step` on a later stuck-boot
      // payload, naming a step this boot never reached.
      await bootTimings.measureAsync(BootPhase.UserDbInit, () => this.userDb.initializeLocalOnlyAsync(user));
      // #6248: a warm start reads only this device's store, so an unread singleton here means
      // the store itself could not answer even after a repair. Do not carry on with its empty
      // default: fall back to a cold start (the catch below), which reads again and holds the
      // loading screen if it still cannot.
      if (this.userDb.unreadBootSingletonIds.length > 0) {
        throw new Error(`warm start could not read singleton documents: ${this.userDb.unreadBootSingletonIds.join(', ')}`);
      }
      this.userDb.database.telemetry = this.telemetry;

      // coach managing client
      this.coachManagingClient = this.userDb.getCoachManagingClient();
      const isManagingClient = !!this.coachManagingClient;

      // merge settings from local PouchDB
      this.mergeSettingsFromUserDb(isManagingClient);

      this.settings._isManagingClient = isManagingClient;

      // set up settings sync callback
      this.installSettingsSyncCallback(isManagingClient);
      this.pushStickyReferralMarkersIfBehind(isManagingClient);

      // #4147: reconcile the device UI-language cache to the (possibly updated)
      // per-user preference just merged from local PouchDB — same rule as the
      // cold-boot path. A no-op unless the effective language actually changed
      // (e.g. the user switched language on another device and it synced here).
      // Runs after installSettingsSyncCallback for the same reason as on the cold
      // path (df-4aea9048b683): a heal saved before it would never reach the account.
      if (!isManagingClient) {
        this.uiLanguageReconcile.reconcileAfterSettingsSync(
          user.uid,
          // fire-and-forget: reloadAppAsync never returns.
          () => void this.reloadAppAsync({ resetToRoot: false }),
        );
      }

      // open commonDb locally
      await bootTimings.measureAsync(BootPhase.SharedDbs, () => this.commonDb.initializeLocalOnlyAsync());
      // mark the text database ready now that commonDb is open locally. without this its
      // reads wait for Phase B, which runs only after the server login: Home's "What's new"
      // check reads a text, so a slow login stalled Home's entry past its 10 s limit
      // (PageWillEnterTimeout, step day.whatsNew). the Phase B call is then a no-op
      await this.textDb.initializeAsync();
      // start the food database too. it is remote-only, so before the login this opens nothing,
      // but the Foods page's guard waits for it: started in Phase B, after the login, a slow
      // login held "Add Foods" until it finished (#6790). Phase B connects it after the login
      await this.foodDb.startAsync();

      // set up docWatcher and load plan from local PouchDB
      this.docWatcher = this.createBootDocWatcher();
      this.plan = await bootTimings.measureAsync(BootPhase.Plan, () => this.userDb.loadPlanAsync(this.docWatcher));
      // #3808 — one-time heal of spurious self-forks (fire-and-forget; self-guarded once per
      // session, never throws). Warm resume keeps the full local DB, so the user's customs are
      // all present here. Collapses duplicates created by editing the user's own legacy
      // exercises so the false "shared exercise no longer available" banner stops showing.
      void this.userDb.healSpuriousSelfForksAsync(user?.uid);

      // load debug state in Phase A so fakeDate is available before page renders
      await this.debug.initializeAsync(this.userDb);

      // subscribe to connection/focus events
      this.connectivityResume.subscribeToConnectionAndFocusEvents();

      // coach managing client setup
      this.setupCoachManagedModeHandlers(isManagingClient);

      // start heartbeat + presence watcher appropriate for the current mode
      this.coachPresenceState.setupForUser(user, this.coachManagingClient);

      // coach white-labeling (#3707): theme the app from the active client's
      // coach branding (no-op / MH default when there is none).
      this.coachBranding.applyActiveBranding();

      console.log('App: Warm resume Phase A complete — initializing navigation');

      // 1b: it is fine to trust the cache when it says "subscribed", but a
      // cached "expired" must be rechecked with the server before we lock the
      // user out — otherwise someone who has just paid (their renewal already
      // processed server-side) gets shown the lockout from a stale cache. Only
      // the already-onboarded, not-paid cohort pays this small extra latency;
      // paid-cache users keep the instant warm resume untouched. If the server
      // can't be reached we fall through and the cached state governs (correct
      // offline behaviour). Phase B logs in again below; that redundant call is
      // cheap and only happens for this small cohort.
      if (shouldRecheckSubscriptionBeforeLockout({
        welcomePhaseDone: this.isWelcomePhaseDone(),
        cachedRole: this.auth.getAuthRole(),
      })) {
        console.log('App: Warm resume — cached role is locked out; rechecking server before showing lockout');
        // O1 (#3986): this recheck is the only couch-login work on the warm
        // CRITICAL path (Phase B logs in after the splash is gone), so it is
        // the warm path's `couchLogin` phase — recorded only for the small
        // lapsed-subscription cohort that actually runs it.
        bootTimings.start(BootPhase.CouchLogin);
        try {
          await this.auth.firebaseReadyAsync('warm-resume-recheck');
          await this.auth.attemptCouchLoginAsync(CouchLoginReason.WarmResumeLockoutRecheck);
        } catch (e: any) {
          // if the failure is a transient/offline network error, that is the
          // expected case — we simply fall back to the cached state below.
          // anything else is unexpected and must surface as telemetry rather
          // than be silently dropped (same convention as the changelog/cold
          // -start fetch catches elsewhere in this file).
          if (isTransientFetchError(e)) {
            console.warn('App: Warm resume pre-lockout server recheck skipped (transient network error), using cached state:', e?.message ?? e);
          } else {
            this.logging.trackSilentProblem('WarmResumePreLockoutRecheckFailed', { name: e?.name, message: e?.message });
          }
        } finally {
          bootTimings.end(BootPhase.CouchLogin);
        }
      }

      // Phase A local init done — hide loading screen and show nav restoration overlay
      this.bootStage = BootStage.Ready;
      // S3 (#3986 Step 19): warm Phase A done — close the SQLite READ-priority
      // window (the background nav restore + Phase B run with normal sync
      // priorities; keeping the window open would throttle Phase B's sync)
      setBootReadPriorityActive(false);
      this.appLoading = false;
      this.navRestoring = true;
      this.armNavRestoreWatchdog(); // L8 (#3986): cover the warm-resume nav-restore window too
      this.loadingProgressType = 'spinner';
      this.cd.markForCheck();

      // fire nav init in background — it awaits ionViewWillEnter which does heavy
      // SQLite queries. The spinner overlay hides all intermediate page transitions.
      // When nav init completes, it clears the overlay.
      retryRoute.takenByNavInit = true;
      void (async () => {
        try {
          await this.waitNavReadyAsync();
          const navEl = this.nav?.getElementRef()?.nativeElement as HTMLElement;
          navEl?.classList.add('restoring');
          try {
            await this.navCtrl.initializeNavigationSystem(this.overlayStack);
          } finally {
            navEl?.classList.remove('restoring');
            this.navRestoring = false;
            this.clearNavRestoreWatchdog(); // L8 (#3986): restore completed
            this.loadingProgressType = 'none';
            this.cd.markForCheck();
            console.log('App: Warm resume navigation restoration complete');
          }
        } catch (e) {
          // the person may be left on the wrong page, or an empty one (#5191)
          this.logging.trackSilentProblem('WarmResumeNavRestoreFailed', errorReportFields(e));
          this.navRestoring = false;
          this.clearNavRestoreWatchdog(); // L8 (#3986)
          this.loadingProgressType = 'none';
          this.cd.markForCheck();
        }
        retryRoute.claim?.release();
        // only now: the restore overlay sits above every dialog, so asking earlier would hide
        // the question behind it (#5895). If Phase A fell back to a cold start meanwhile, the
        // cold boot asks at its own Ready instead, so this one stays quiet
        if (this.isWarmResume) this.notifySignedInAppReady();
        // #6202: the warm boot is finished too, restored or not (the cold path calls this after its
        // restore). a cold fallback calls it at its own Ready instead
        if (this.isWarmResume) this.reportPreviousFreezeOnce();
      })();

      const bootingTime = bootTimings.elapsedSinceWindowStartMs();
      // #4615: see the cold emit site — same property, same reason.
      const bootKind = bootTimings.nextEmissionKind({ signedIn: true, warmResume: true });
      console.log(`App: Warm resume ready in ${(bootingTime / 1000).toFixed(2)} s (${bootKind})`);
      this.telemetry.trackEvent('booting_time', buildBootingTimeProperties({
        bootingTimeMs: bootingTime,
        bootKind,
        warmResume: true,
        // on the warm path the user is emitted from cache, so `user.uid` is the
        // right identity to classify the boot-start sample against
        uid: user.uid,
        bootStartSessionHint: this.bootStartSessionHint,
        settingsForTelemetry: this.settings.getSettingsForTelemetry(),
        pageUrl: window.location.href,
      }));
      bootTimings.noteEmission(bootKind);
      this.auth.whenAppReady.resolve(true);

      // claim this tab as the last focused tab to prevent spurious cross-tab reload
      localStorage.setItem('lastFocusedTabId', getSessionId());

      void this.userDb.warmUpViewsAsync();

      pubSubReg.unsubscribe();

      // fire Phase B in background
      void this.warmResumePhaseB(user);

    } catch (error) {
      pubSubReg.unsubscribe();
      // if the person was signed out (or switched) during Phase A, the failure is the sign-out
      // teardown pulling the databases away, typically a TypeError from commonDb opening for a null
      // `auth.user` (df-75b868acaa0d). falling back to a cold start would re-run the whole boot for
      // somebody who is gone and end on a boot-failure report. the sign-out path owns the teardown
      // and the splash (or the new user's own boot does), so drop what Phase A half-built and stop.
      // keyed on the app's own user only, like Phase B (#6088)
      if (!this.auth.user || this.auth.user.uid !== uidAtStart) {
        console.log('App: Warm resume Phase A stopped — the session ended or changed while it ran:', describeErrorForLog(error));
        AuthService.clearWarmResumeFlag();
        this.isWarmResume = false;
        if (this.docWatcher) {
          this.docWatcher.destroy();
          this.docWatcher = null;
        }
        return;
      }
      // trackSilentProblem, NOT trackAbnormalException: the latter routes through the database-error
      // interceptor, which could start a recovery reload in the middle of this fallback (#5191)
      this.logging.trackSilentProblem('WarmResumePhaseAFailed', errorReportFields(error));
      AuthService.clearWarmResumeFlag();
      this.isWarmResume = false;

      // clean up partially initialized resources before cold start creates new ones
      if (this.docWatcher) {
        this.docWatcher.destroy();
        this.docWatcher = null;
      }
      await this.coachExerciseSync.stopAsync();
      await this.userDb.shutdownAsync();
      if (this.commonDb.database) {
        await this.commonDb.shutdownAsync();
      }
      // the food database Phase A started, so the cold start builds a fresh one (#6790)
      await this.foodDb.shutdownAsync();

      // show loading screen and run full cold start
      this.appLoading = true;
      this.loadingProgressType = 'spinner';
      this.cd.markForCheck();
      // the splash was opened by direct assignment (not markAppLoadingAsync),
      // so arm the watchdog for this window explicitly (#3698)
      this.armSplashWatchdogIfNeeded();
      await this.postLoginColdStartAsync(user);
    }
  }

  // warm resume Phase B's own login unlocked the app: route the person in, and release Phase B's
  // claim on the post-login move once the route-in has run (df-53c89a39b0e6)
  private async routeInAfterPhaseBLoginAsync(routeClaim: PostLoginRouteClaim) {
    console.log('App: Warm resume — server login unlocked the app; routing to home');
    // L10 (#3986): defer this re-route if the nav restore is still rebuilding the stack — a
    // bare setRootAsync here can clobber the half-restored stack (blank/wrong page for the
    // paid-after-expiry cohort). rerouteRespectingRestore runs it now if no restore is in
    // flight, otherwise queues it to fire when the restore finishes; the claim is released
    // only once the route-in has run, deferred or not
    await this.navCtrl.rerouteRespectingRestore(async () => {
      try {
        await this.navCtrl.setRootAsync(IHomePage, {});
      } finally {
        routeClaim.release();
      }
    });
  }

  // warm resume Phase B: CouchDB login, remote sync, remaining services (page already visible)
  private async warmResumePhaseB(user: UserAuth) {
    // the session Phase B runs for, taken from the parameter so a sign-out that landed before
    // Phase B began is spotted too. see design-docs/warm-resume.md → Sign-out race convention (#6088)
    const uidAtStart = user.uid;
    try {
      console.log('App: Warm resume Phase B — background init');

      // Remember whether the app was locked before the server login. Phase A
      // renders the page from the cached role (this.couch is still null), so a
      // user whose cache says "expired" is shown the welcome/locked page even
      // if they have since paid.
      const wasLockedBeforeLogin = !this.isFullAppUnlocked();

      // claim the post-login move from before the login until Phase B has decided whether to
      // route the person in, and until that route-in is over: a page that hears the record this
      // login loads (the subscribe and unsubscribe pages) then waits instead of navigating
      // alongside it (df-53c89a39b0e6)
      const routeClaim = this.navCtrl.claimPostLoginRoute('warm resume Phase B');
      let routeClaimHandedToReroute = false;
      try {
        // CouchDB login
        await this.auth.attemptCouchLoginAsync(CouchLoginReason.WarmResumePhaseB);

        // if the person was signed out (or switched) during the login, the sign-out path already
        // owns the teardown and nothing below makes sense without this user (#6088). this keys on
        // the app's own user only, not isLikelySessionChangeRace: that helper is also true when just
        // Firebase's current user is missing while the app still holds its cached user, and stopping
        // then would leave the remaining databases unopened for someone still signed in
        if (!this.auth.user || this.auth.user.uid !== uidAtStart) {
          console.log('App: Warm resume Phase B — session ended during login, stopping');
          return;
        }

        // 1a: the background login just refreshed the role from the server, but
        // nothing re-evaluates the lockout guard afterwards (unlike the
        // focus/online retry path, which re-emits on mainAuthSubject — see
        // auth.service.ts retryCouchLoginAsync). So a user who paid while the
        // cache still said "expired" would stay stranded on the locked welcome
        // page until they manually reopened or navigated. If the fresh role just
        // unlocked the app, route them in — same move as the post-checkout
        // re-route in external-flow.service.ts.
        const routeIn = shouldRouteInAfterServerLogin({
          wasLockedBeforeLogin,
          unlockedAfterLogin: this.isFullAppUnlocked(),
        });
        if (routeIn) {
          routeClaimHandedToReroute = true;
          await this.routeInAfterPhaseBLoginAsync(routeClaim);
        }
      } finally {
        if (!routeClaimHandedToReroute) routeClaim.release();
      }

      // connect remote and start sync
      await this.userDb.connectRemoteAsync();
      await this.commonDb.connectRemoteAsync();

      // initialize remaining databases. the food database was started in Phase A and only
      // needs its server connection, which the login above made possible (#6790)
      await Promise.all([
        this.textDb.initializeAsync(),
        this.exerciseDb.initializeAsync(),
        this.foodDb.connectRemoteAsync(),
        this.testCasesDb.initializeAsync(),
      ]);
      void this.configDb.initializeAsync();

      this.startBackgroundServicesAfterDbInit();

      // telemetry and debug
      this.telemetry.initialize(this.userDb, this.auth, this.configDb);
      await this.debug.initializeAsync(this.userDb);
      this.telemetry.setUserInfo(this.userDb, user, this.settings);

      await this.userDb.convertAutomaticTrainingGoalIfNeeded();
      await this.applyScheduleFixes();

      // reschedule notifications
      this.rescheduleNotificationsAndRegisterForPush();

      // detect HTTP/2
      this.bootDiagnostics.probeHttp2Protocol();

      // changelog check — the shared databases only started syncing a moment
      // ago, so this one has to wait for that sync before it can read
      void this.checkChangelogNotificationAsync(CommonDbReadiness.MustWaitForSync);

      // iOS SQLite migration cleanup — not awaited, for the same reason as the cold-start
      // call site above
      void this.cleanUpLegacyIndexedDbAfterSqliteMigrationAsync();
      // #5270 step 6b — and the other cleanup, for a device that has ROLLED BACK. A
      // no-op on every device that has not, which is all of them until an operator
      // opens the rollback ramp. Not awaited, for the same reason as the call above.
      void this.sqliteLifecycle.cleanUpSupersededIndexedDbAsync();

      // #5449 — the background copy, from the warm path too. Warm resume is a full
      // page-load boot and is the dominant relaunch path, so a cold-only call would
      // start the copy roughly once per account, ever.
      void this.sqliteLifecycle.runSqliteBackgroundCopyAsync(user?.uid);
      // #4981 phase 4 (#5270) — and the way back, which is a no-op on any device that is
      // not on SQLite with a rollback outstanding
      void this.sqliteLifecycle.runSqliteRollbackBackgroundCopyAsync(user?.uid);

      // background tasks for paid users
      this.startPaidUserBackgroundTasks();

      this.databaseRecovery.completeDatabaseRecovery();
      console.log('App: Warm resume Phase B complete');

    } catch (error) {
      // Phase B failure: log as silent problem, do NOT show loading screen.
      // if the session ended or switched during a later await, the failure is the expected
      // teardown, not a problem (#6088). same app-user check as the early return above, for the
      // same reason: a still-signed-in user whose Firebase has not settled must still be reported
      console.log('App: Warm resume Phase B failed (non-fatal):', error);
      if (this.auth.user && this.auth.user.uid === uidAtStart) {
        this.logging.trackSilentProblem('WarmResumePhaseB', { error: error?.message || String(error) });
      }
    }
  }

  clearRecentlySyncedMarks() {
    DatabaseWrapper.clearRecentlySyncedMarks();
  }

  browserCheck() {
    // Capacitor native apps use a system WebView — skip browser compat check
    if (isCapacitorNative()) return;

    // if not running one of the supported browsers
    const bowser = Bowser.getParser(window.navigator.userAgent);
    if (!bowser.satisfies({
      chrome: '>=112',
      safari: '>=10', // support ancient versions because Safari iOS cannot be updated without upgrading the whole OS
      edge: '>=112',
      samsung_internet: '>=22',
      firefox: '>=115',
      opera: '>=100',
    })) {
      this.telemetry.trackEvent(`warning_browser`, { browser: bowser.getBrowserName(), version: bowser.getBrowserVersion(), os: bowser.getOSName(), osVersion: bowser.getOSVersion(), user_agent: navigator.userAgent });

      // tracked via warning_browser telemetry event above (Mixpanel); no Sentry event needed
      console.log('UnsupportedBrowser:', bowser.getBrowserName(), bowser.getBrowserVersion(), bowser.getOSName());

      // show a toast to the user with "Don't show again" option
      this.toastCtrl.showToast({
        uniqueId: 'unsupported-browser-toast',
        message: LL.errors.userFacing.unsupportedBrowser(),
        type: ToastType.WarningLight,
        duration: 8000,
        position: 'top',
        showCloseButton: true,
        showDontShowAgainButton: true
      });
    }
  }

  // L8 (#3986): arm the navRestoring watchdog. Called as the nav restore starts. The
  // navRestoring spinner window runs AFTER appLoading=false, so the splash watchdog (which only
  // runs while appLoading) does not cover it — a hung page-load inside restoreNavigationStack is
  // otherwise a forever spinner with zero telemetry.
  private armNavRestoreWatchdog() {
    this.clearNavRestoreWatchdog();
    this.navRestoreWatchdogTargetUrl = location.pathname;
    // df-d80a99934799: a new restore window — a rescue from here on belongs to whichever restore
    // starts next, never to one an earlier window started
    this.navRestoreRecovery = null;
    this.isNavRestoreWatchdogWindowOpen = true;
    this.hookNavRestoreVisibilityOnce();
    this.startNavRestoreWatchdogWindow(NAV_RESTORE_WATCHDOG_MS);
  }

  // #4232: (re)start the countdown for the given remaining FOREGROUND budget. If the document is
  // hidden right now, no timer starts at all — the restore is paused by the OS, and the
  // visibilitychange handler starts the countdown when the app becomes visible again.
  private startNavRestoreWatchdogWindow(remainingMs: number) {
    this.navRestoreWatchdogRemainingMs = remainingMs;
    if (document.hidden) {
      return;
    }
    this.navRestoreWatchdogVisibleSinceMs = performance.now();
    this.navRestoreWatchdogHandle = setTimeout(() => {
      this.navRestoreWatchdogHandle = null;
      this.onNavRestoreWatchdogDeadline();
    }, remainingMs);
  }

  // the watchdog's foreground budget ran out — decide whether the restore is actually wedged.
  // Runs from the timer, and also directly from the visibility handler when the app returns to
  // the foreground with no budget left.
  private onNavRestoreWatchdogDeadline() {
    const targetUrl = this.navRestoreWatchdogTargetUrl;
    // key on restore completion, NOT bootStage (bootStage is Ready before navRestoring flips).
    // a pending reload (sign-out re-roots to LoginPage, which calls reloadAppAsync) means the
    // restore was superseded rather than wedged — firing there is a false NavRestoreStuck.
    // a hidden document means the restore is paused by the OS, also not wedged (#4232).
    const restoreStillActive = this.navRestoring;
    const documentHidden = document.hidden;
    if (shouldFireNavRestoreWatchdog({
      navRestoringStillActive: restoreStillActive,
      appReloadPending: this.navCtrl.reloadPending,
      documentHidden,
    })) {
      this.recoverStuckNavRestore(targetUrl);
      return;
    }
    // a deadline that runs while hidden means the pause missed (belt-and-braces): keep the budget
    // at 0 so the next visible evaluates immediately instead of granting a fresh window.
    if (restoreStillActive && documentHidden) {
      this.navRestoreWatchdogRemainingMs = 0;
      console.log('App: navRestoring watchdog deadline while hidden — treating as paused', { targetUrl });
      return;
    }
    // if a still-active restore did NOT fire, the helper suppressed it — leave a plain log
    // breadcrumb. Keyed only on restoreStillActive, so it cannot drift from the helper's rule if
    // that ever gains another suppressor. NOT trackSilentProblem: that console.errors, which is
    // exactly the canary-reddening noise this suppression removes. reloadAppAsync always reaches
    // location.reload(), so the state is benign — this line just keeps it traceable.
    if (restoreStillActive) {
      console.log('App: navRestoring watchdog suppressed — reload pending', { targetUrl });
    }
  }

  // #4232: pause the countdown while the app is backgrounded, resume with the remaining
  // foreground budget when it comes back. Registered once, lazily, on the first arm — MyApp is
  // the never-destroyed root, so the listener never needs removing.
  private hookNavRestoreVisibilityOnce() {
    if (this.isNavRestoreVisibilityHooked) {
      return;
    }
    this.isNavRestoreVisibilityHooked = true;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseNavRestoreWatchdog();
      } else {
        this.resumeNavRestoreWatchdog();
      }
    });
  }

  // #4232: the app went to the background — stop the countdown and bank the remaining
  // foreground budget, so hidden time never counts toward the 20 s window.
  private pauseNavRestoreWatchdog() {
    // nothing armed (restore finished, or already paused) — nothing to pause
    if (this.navRestoreWatchdogHandle === null) {
      return;
    }
    clearTimeout(this.navRestoreWatchdogHandle);
    this.navRestoreWatchdogHandle = null;
    this.navRestoreWatchdogRemainingMs = remainingNavRestoreWatchdogMs({
      remainingMs: this.navRestoreWatchdogRemainingMs,
      elapsedVisibleMs: performance.now() - this.navRestoreWatchdogVisibleSinceMs,
    });
  }

  // #4232: the app came back to the foreground — restart the countdown with the banked
  // budget, or evaluate immediately when the budget was already used up.
  private resumeNavRestoreWatchdog() {
    const action = navRestoreWatchdogResumeAction({
      windowOpen: this.isNavRestoreWatchdogWindowOpen,
      navRestoring: this.navRestoring,
      timerRunning: this.navRestoreWatchdogHandle !== null,
      remainingMs: this.navRestoreWatchdogRemainingMs,
    });
    // if budget is left, count it down again
    if (action === NavRestoreWatchdogResume.Restart) {
      this.startNavRestoreWatchdogWindow(this.navRestoreWatchdogRemainingMs);
    } else if (action === NavRestoreWatchdogResume.EvaluateNow) {
      // the foreground budget was already fully consumed — the restore really is wedged; evaluate now
      this.onNavRestoreWatchdogDeadline();
    }
  }

  // cancel the navRestoring watchdog timer if one is armed (restore finished or is re-arming)
  private clearNavRestoreWatchdog() {
    if (this.navRestoreWatchdogHandle !== null) {
      clearTimeout(this.navRestoreWatchdogHandle);
      this.navRestoreWatchdogHandle = null;
    }
    // a restore that finished while paused (hidden) left no timer but a stored budget — drop it
    // so a later visible event cannot resurrect a countdown for a restore that is already done.
    // (resumeNavRestoreWatchdog also checks navRestoring; this keeps the state honest besides.)
    this.navRestoreWatchdogRemainingMs = 0;
    this.isNavRestoreWatchdogWindowOpen = false;
  }

  // L8 (#3986): the navRestoring watchdog fired — the restore is wedged. Report it, then recover.
  // The recovery must NOT wait on the defer-guard: a restore hung inside an in-flight nav.push
  // never reaches the finally that clears isRestoringNavStack, so a deferred re-route would wait
  // forever. forceClearNavRestore declares the restore dead (force-clears the guard) so the
  // recovery setRootAsync runs; the defer-guard is only for healthy concurrency (Step 10).
  private recoverStuckNavRestore(targetUrl: string) {
    console.warn('App: navRestoring watchdog fired — forcing recovery to home', { targetUrl });
    this.logging.trackSilentProblem('NavRestoreStuck', {
      target_url: targetUrl,
      boot_stage: this.bootStage,
    });
    this.navCtrl.forceClearNavRestore();
    this.navRestoring = false;
    // #6180: hold push taps until the re-root below has finished. The app is marked ready before
    // then (just below on a warm resume, at the cold boot's Ready once the line after this
    // unblocks it), and a tap let through then would have its page replaced by the re-root.
    // registered first, so it is in place before either of those can mark ready
    const reRootDone = DeferredPromise.defer<void>();
    this.notificationService.holdPushTapsUntil(reRootDone);
    // df-d80a99934799: end the cold boot's wait on the wedged restore, so the rest of that boot runs
    this.navRestoreRecovery?.resolve();
    // drop the transition-suppression class the restore added — otherwise page-transition
    // animations stay suppressed after this recovery re-root in the wedged-forever case.
    const navEl = this.nav?.getElementRef()?.nativeElement as HTMLElement;
    navEl?.classList.remove('restoring');
    this.cd.markForCheck();
    // if this is a warm resume, its restore will never finish, so the account-hint check that
    // waits on it never runs: ask here instead (#5895). The hint is handed out once, so if the
    // restore does finish later, that check finds nothing left to ask about
    if (this.isWarmResume) this.notifySignedInAppReady();
    // #6202: for the same reason, tell the native watchdog the warm boot finished here. the cold
    // path reaches its own call once this rescue ends its wait
    if (this.isWarmResume) this.reportPreviousFreezeOnce();
    // L8 (#3986): this recovery navigation is the LAST lever after the restore was declared
    // wedged — if it rejects silently the user stays stuck with zero signal, so report it.
    void this.navCtrl.setRootAsync(IHomePage, {}).catch(err => {
      this.logging.trackSilentProblem('NavRestoreRecoveryFailed', errorReportFields(err));
    }).finally(() => reRootDone.resolve());
  }

  async waitNavReadyAsync(): Promise<CsNavComponent> {
    for (let i = 0; i < 100; i++) {
      if (this.nav) {
        return this.nav;
      }
      await sleep(100);
    }
    // L9 (#3986): nav never became ready within the 10 s budget — boot proceeds with an
    // undefined nav (broken navigation). This used to be a bare console.error, invisible in
    // prod; report it so a future debugger can see how often it happens and at what stage.
    console.error('Nav was never initialized!');
    if (shouldReportNavNeverReady(this.nav != null)) {
      this.logging.trackSilentProblem('NavNeverReady', { boot_stage: this.bootStage });
    }
    return undefined;
  }

  private getActivePageName() {
    return this.navCtrl.getActivePageName();
  }

  onFabTestMenuClick() {
    this.fab.close();
    void this.navCtrl.pushAsync(ITestingRootPage, {});
  }

  async fakeNextDayAsync() {
    const newDate = this.debug.getNowLocal().plus({ days: 1 });

    await this.debug.setNowLocalDayAsync(newDate);

    return this.nav.setPages([
      {
        page: getAllDeepLinks().find(p => p.name === 'HomePage').component,
        params: { day: newDate.toISODate() },
      },
    ]);
  }

  // part of IMyApp: the intake form and the subscribe page ask it. The rule lives with the menus.
  shouldShowMenu() {
    return this.navMenu.shouldShowMenu();
  }


  async openCommunityPage() {
    // F7 (#3991): the community lives on a separate website, so it is useless offline. Without
    // this guard we navigate away from the app and hand the user the browser's own error page —
    // and on the way back they pay a full app boot. Say so instead, and stay put.
    //
    // Checked before the first-time intro alert on purpose: there is no point explaining what
    // the community is when we are about to refuse to open it.
    // re-check (not the cheap read) before refusing — the cached state can sit stale for minutes
    // (the foreground tick re-checks an Offline belief, but only every six minutes), and refusing a
    // user who is actually online would be worse than the old behavior
    if (!await this.onlineStateService.isLikelyOnlineOrRecheckAsync()) {
      this.toastCtrl.showToast({
        message: LL.app.communityOffline(),
        type: ToastType.WarningLight,
        duration: 5000,
      });
      return;
    }

    if (!this.settings.usedCommunityBefore) {
      await this.alertCtrl.showAlertAsync({
        title: LL.app.communityTitle(),
        message: LL.app.communityFirstTime(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }]
      })
    }

    // single canonical Discourse for all envs. dev/canary bounce through www on
    // the SSO return leg; the cookie set below tells SsoService.parseUrl which
    // host to bounce back to (#3300, mirrors PPG's ppg_return_host pattern).
    //
    // On a phone the cookie is a no-op and that is fine: the app runs on its own
    // local address, so a cookie scoped to our root domain cannot be stored, and
    // there is nothing to bounce because prod is already the canonical host. The
    // navigation below also does not move the web view there — it is refused and
    // handed to the operating system, which opens the person's own browser. What
    // happens when the forum sends them back is #4688; see
    // design-docs/discourse-sso.md, "The return leg on a phone".
    const communityUrl = `https://community.${getRootDomain()}`;
    setReturnHostCookie(location.host, getRootDomain());
    // we cannot use a loader, because we can't know when/whether the community page is actually loaded
    // so just show a toast that will autohide, hopefully before the user can return from the community page
    this.toastCtrl.showToast({
      message: LL.app.openingCommunity(),
      duration: 2000,
    });
    // always open in the same tab - some (mobile) browsers can get confused with redirection across tabs
    location.assign(communityUrl);
  }

  // the "report a bug" camera button lives in BugReportFabService, and app.html
  // binds to it directly. these two are on IMyApp, so the feedback and coach
  // message pages still reach them through getMyApp()
  showScreenshotButton(feedbackType: SendFeedbackType) {
    this.bugReportFab.showScreenshotButton(feedbackType);
  }

  hideScreenshotButton() {
    this.bugReportFab.hideScreenshotButton();
  }

  /** The "noticed something unusual?" toast, from LoggingService: report it as an issue. */
  reportIssueFromToastAsync(sentryId: string | undefined): Promise<void> {
    return this.bugReportFab.reportIssueFromToastAsync(sentryId);
  }

  shouldDisplayDebugFabButton() {
    return this.settings.testMode && this.getActivePageName() !== 'LoginPage';
  }


  getCurrentPageName(): string | null {
    return this.navCtrl?.getCurrentPage() || null;
  }

  // we intercept all fetch requests and all XMLHttpRequests - so we can handle HTTP errors
  installHttpErrorHandler() {
    // shared function that is called if error happens - both for fetch and XHR
    const handleHttpError = (url: string, status: number, context: any) => {
      // status 0 = network-level failure (offline, CORS, net::ERR_FAILED) — not a server error
      if (status === 0) {
        console.log(`HTTP error: ${status} for ${url}`);
        this.logging.trackAdBlocker(url, "from HttpErrorHandler", { status, url, context });
        return;
      }
      console.error(`HTTP error: ${status} for ${url}`);
    }

    // bound to `window`: `fetch` is not callable with an arbitrary `this`, and
    // some engines throw "Illegal invocation" for a detached reference.
    const originalFetch = window.fetch.bind(window);
    this.onlineStateService.setFetch(originalFetch);
    const fetchRejectionHandlers: FetchRejectionHandlers = {
      isOfflineAsync: async () =>
        (await this.onlineStateService.verifyOnlineStatus(ConnectionState.Offline, 'suspected')) === ConnectionState.Offline,
      act: (action, url, error) => {
        switch (action) {
          case FetchFailureAction.PassThrough:
            break;
          case FetchFailureAction.LogOffline:
            console.log(`Fetch error because offline (url: ${url}):`, error);
            break;
          case FetchFailureAction.LogCallerOwned:
            console.log(`Fetch error left to its caller to retry and report (url: ${url}):`, error);
            break;
          case FetchFailureAction.LogThirdParty:
            console.log(`Fetch error on third-party/self-healing URL (${url}):`, error);
            break;
          case FetchFailureAction.Report:
            this.logging.trackSilentProblem(`Fetch error (url: ${url})`, error);
            break;
        }
      },
      // the classification of a Firebase failure runs after the error went back to Firebase (#6174),
      // so a throw in it would reach nobody; the query string is dropped, it carries the API key
      reportBackgroundFailure: (url, err) => this.logging.trackSilentProblem('FetchFailureBackgroundClassifyFailed', {
        url: url.split('?')[0],
        error: describeError(err),
      }),
      noteFirebaseAuthFailure: (url, error) => firebaseFetchFailures.note(url, error, performance.now()),
    };
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        if (!response.ok) {
          // Handle HTTP error
          handleHttpError(args[0].toString(), response.status, { type: "fetch", headers: response.headers });
        }
        return response;
        // if fetch fails with exception - that signifies a network error: https://stackoverflow.com/a/39297738
      } catch (error) {
        // the decision and the rethrow live in fetch-failure-classifier.ts, where they are unit-tested
        return handleFetchRejectionAsync(error, args[0].toString(), args[1], fetchRejectionHandlers);
      }
    };

    // eslint-disable-next-line @typescript-eslint/unbound-method -- native browser API being monkey-patched
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (...args: any[]) {
      // the original url is sometimes not available at the time `send()` is called (notably happens in offline mode), so we store it here
      this['originalUrl'] = args[1];
      return originalOpen.apply(this, args);
    };

    // eslint-disable-next-line @typescript-eslint/unbound-method -- native browser API being monkey-patched
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (...args) {
      this.addEventListener('readystatechange', function () {
        if (this.readyState === 4 && (this.status >= 400 || this.status === 0)) {
          // Handle HTTP error
          handleHttpError(this['originalUrl'], this.status, { type: "xmlhttp", headers: this.getAllResponseHeaders() });
        }
      }, false);
      originalSend.apply(this, args);
    };

  }

  checkDarkMode() {
    if (navigator.userAgent.match(/Samsung/i) && window.matchMedia && !window.matchMedia('(prefers-color-scheme:dark)').matches) {
      const img = new Image();
      img.onload = () => {
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.drawImage(img, 0, 0);
        const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
        if ((r & b & g) < 255) {
          void this.reportDarkModeConflict();
        }

      };
      img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IndoaXRlIi8+PC9zdmc+';
    }
  }

  async reportDarkModeConflict() {
    const retry = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.app.darkModeConflictTitle(),
      message: LL.app.darkModeConflictMessage(),
      buttons: [{ text: LL.app.darkModeConflictButton(), role: AlertButtonRole.Primary, result: true },
      { text: LL.common.buttons.ignore(), role: AlertButtonRole.Cancel, result: false }]
    });
    this.telemetry.trackEvent('dark_mode_conflict', { user_agent: navigator.userAgent, retry: retry });
    if (retry) {
      this.checkDarkMode();
    }
  }

  handleFakeNextDayFabButtonClick() {
    this.fab.close();
    void this.fakeNextDayAsync();
  }

  private setDevFavicon() {
    // only needed for local dev server (deployed dev/prod icons are swapped at build time)
    if (AppEnvironment.isAngularProduction) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = 'assets/icons/favicon-local.ico';
  }

}
